/**
 * @fileoverview Generate Precedents — Post-creation wizard
 *
 * Purpose: After creating a job, guides the user through selecting
 * day-one templates, reviewing auto-populated fields, and generating documents.
 * URL: /dashboard/jobs/:jobId/generate-precedents
 */

import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useConvex } from "convex/react";
import { useUploadFiles } from "@xixixao/uploadstuff/react";
import { api } from "~/convex/_generated/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useActiveOrg } from "@/utils/org-context";
import {
  Input,
  Badge,
  Spinner,
  Checkbox,
  Button,
  Label,
} from "@/ui/primitives";
import { LetterheadOverridePicker } from "@/components/branding/LetterheadOverridePicker";
import { VariantPicker } from "@/components/precedents/VariantPicker";
import { extractVariantGroupSlugs } from "@/components/precedents/VariantPicker.helpers";
import {
  resolveBoundVariantSelections,
  type VariantFieldBinding,
} from "~/convex/jobs/precedents/variantBinding";
import {
  resolveSuppressedFieldKeys,
  type FieldConditionMap,
} from "~/convex/jobs/precedents/fieldConditions";
import {
  applyGroupDefaultsToValues,
  resolveCheckboxGroupDefaults,
  type CheckboxGroupPlacement,
} from "~/convex/jobs/precedents/checkboxGroupDefaults";
import {
  Fragment,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Loader2,
  GitBranch,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Sparkles,
  SkipForward,
  Star,
  ChevronDown,
  ChevronsUpDown,
  Check,
  FileText,
  FileSpreadsheet,
  Upload,
  Paperclip,
  LayoutGrid,
  List,
  Search,
  Tag,
  X as XIcon,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import {
  buildFieldsLabelMap,
  collectPlaceholderKeysFromStoredTemplate,
  extractVariantBlockInstances,
  stripInlineHighlights,
  prepareHtmlForEditor,
  decorateFieldChipModifiers,
} from "@/ui/composed/editor/html-utils";
import {
  collectManualBodyFieldKeys,
  partitionWizardFieldKeys,
  resolvePerDocumentFieldMeta,
} from "@/utils/precedents/wizardFieldDiscovery";
import {
  applyStackLayout,
  formatInlineNameList,
  NAME_LIST_FIELDS,
  parseModifiers,
  reflowCompanyStatus,
  toIsoDate,
} from "@/ui/composed/editor/field-modifiers";
import { slotLabelForRefs } from "@/ui/composed/editor/variant-slots";
import {
  annotateFieldSources,
  classifyFieldSource,
} from "@/utils/precedents/classifyFieldSource";
import type { FunctionReturnType } from "convex/server";
import type { Id } from "~/convex/_generated/dataModel";
import { toast } from "sonner";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  CONTACT_PERSON_MODE_LABELS,
  memberDisplayName,
  resolveContactPersonFields,
} from "~/convex/jobs/precedents/contactPerson";
import type { DayOneContactDefault } from "@cvx/organizations/schema";
import {
  rehydrateWizardState,
  countOverridden,
  withoutKeys,
} from "@/utils/precedents/savedOverrides";
import {
  firmStatesSelectOnTheirOwn,
  hasAnyFanOutTag,
} from "@/utils/precedents/fanOutTagPolicy";
import { resolveScopedTitles } from "~/convex/lib/titleScopes";
import {
  APPOINTMENT_TYPE_TAGS,
  GALLAGHER_INSURANCE_FIELDS,
  getJobMatchReasons,
  reasonsCoverTypeAndStatus,
  JOB_STATUS_TAG_PREFIX,
  type MatchReason,
  type PrecedentField,
} from "@/lib/precedent-templates";
import {
  ROLE_META,
  FIRM_CATEGORY_META,
  FIRM_CATEGORY_ORDER,
  parseContactRoleTag,
  parseFirmContactTag,
  parseFirmContactCustomCategoryTag,
  parseFirmContactStateTag,
  parseJobContactCustomRoleTag,
  isJobContactExternalAdminTag,
  isFirmContactJobStateTag,
  jobAddressState,
  ROLE_TO_SOURCE_KINDS,
  type ContactRole,
  type FirmContactCategory,
  type BadgeColor,
} from "@/lib/contact-roles";
import { roleIconComponent } from "@/components/settings/tasks/role-icons";
import { RoleBadge } from "@/components/settings/tasks/RoleBadge";
import {
  CONTACT_PERSON_MODES,
  CONTACT_PERSON_MODE_ICONS,
} from "@/components/settings/precedents/contact-person-modes";
import type { CustomJobRole } from "@/components/jobs/phases/contacts/types";
import { JOB_STATUS_LABELS, JOB_STATUS_ORDER } from "@/lib/job-statuses";
import { AU_STATE_VALUES } from "@/utils/format/auStates";
import { getPresetPractitionerNames } from "@/lib/practitioner-presets";
import {
  FIELD_CATEGORIES,
  CANONICAL_FIELDS_MAP,
  DEFAULT_CATEGORIES,
  getValidKeysForCategory,
  resolveCategoryLabel,
  resolveFieldLabel,
  type CanonicalField,
} from "~/convex/settings/precedents/canonicalFields";
import {
  CONTACT_DERIVABLE_FIELD_KEYS,
  mapContactToCanonicalFields,
  mapJobToCanonicalFields,
  mergeFieldValues,
  resolveContactFields,
  resolveContactSourceFields,
  resolveContactCustomFields,
  resolveFirmReferenceFields,
  type ContactDataForFields,
  type ContactCustomFieldDef,
} from "~/convex/jobs/precedents/fieldMapping";
import {
  resolveTemplateDateCalcs,
  type DateCalc,
  type DateCalcContext,
} from "~/convex/settings/precedents/dateCalc";
import {
  CONTACT_SOURCE_FIELDS,
  CONTACT_SOURCE_KIND_LABELS,
  buildContactSourceFieldKey,
  firmRefKeyCategory,
  isContactFieldKey,
  parseContactFieldKey,
  parseContactSourceFieldKey,
  getContactFieldPartMeta,
  jobContactPartLabel,
  EXTERNAL_ADMIN_ROLE_LABEL,
  type ContactFieldDomain,
  type ContactFieldPart,
  type ContactSourceFieldMeta,
  type ContactSourceKind,
} from "~/convex/jobs/precedents/contactFields";
import {
  collectCollectionKeys,
  hasCollectionMarkup,
} from "~/convex/jobs/precedents/collectionsShared";
import {
  getContactPartValue,
  missingContactFields,
} from "./-contactCompleteness";
import {
  resolveGallagherFieldValues,
  type GallagherFieldConfig,
} from "~/convex/jobs/precedents/gallagherFieldConfig";
import type { JobPrecedentAttachment } from "@cvx/jobs/precedents/jobPrecedents/schema";
import type { DocumentCategory } from "@cvx/jobs/gatherInformation/documents/schema";
import type { JobStatus } from "@cvx/jobs/schema";
import { buildSignatureImageToken } from "~/convex/lib/signatureToken";
import type {
  AttachmentRequirement,
  PrecedentAttachmentKey,
  PrecedentAttachmentSourceKind,
} from "@/lib/precedent-attachments";
import {
  buildAttachmentSlotKey,
  buildAttachmentReferenceKey,
  normaliseAttachmentRequirement,
} from "@/lib/precedent-attachments";
import { SharedReferencePicker } from "@/components/jobs/phases/day-one/SharedReferencePicker";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";

type SearchParams = {
  reselect?: boolean;
};

// ---------------------------------------------------------------------------
// Row types for the Convex list queries this wizard reads, derived from the
// queries' own return types so they never need manual sync with the backend.
// (Rows whose queries are consumed purely through inference need no alias.)
// ---------------------------------------------------------------------------

type PrecedentTemplateRow = FunctionReturnType<
  typeof api.settings.precedents.queries.listByCategory
>[number];

type FieldPresetRow = FunctionReturnType<
  typeof api.settings.precedents.presets.listByOrg
>[number];

// Reverse lookup: Morgan canonical field key → the Gallagher form's own
// label/description for that field. The Gallagher per-document card pulls its
// inputs from generic canonical fields (e.g. "Type of Administrator",
// "Registered Office — Street"), but the preview, the portal-fields config,
// and the live AJG portal all use the numbered Gallagher names ("5. Type of
// Appointment", "9. Principal Risk Address"). We relabel the card's fields to
// the Gallagher names so the same field reads identically everywhere — see
// the Gallagher branch in `perDocumentCards`.
const GALLAGHER_LABELS_BY_CANONICAL_KEY: Record<
  string,
  { label: string; description?: string }
> = Object.fromEntries(
  GALLAGHER_INSURANCE_FIELDS.filter((f) => f.canonicalKey).map((f) => [
    f.canonicalKey as string,
    { label: f.label, description: f.description },
  ]),
);

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/generate-precedents",
)({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    reselect: search.reselect === true || search.reselect === "true",
  }),
  component: GeneratePrecedentsPage,
});

type Step = "select" | "review" | "attachments" | "confirm";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

// Resizable Name column (table view of the Select Templates step). Long
// template names would otherwise be clipped; width (px) is persisted so the
// user's preferred width survives reloads. Mirrors the precedents settings
// list-view behaviour.
const NAME_COL_MIN = 160;
const NAME_COL_MAX = 560;
const NAME_COL_DEFAULT = 260;
const NAME_COL_STORAGE_KEY = "generate-precedents-name-col-width";

/** Provenance of a resolved review field, used for source badges + counts. */
type FieldReviewSource =
  | "job"
  | "default"
  | "edited"
  | "empty"
  | "template"
  // MOR-1003 — answer supplied by a checkbox-group default configured on
  // the template's PDF placements ("Selected by default").
  | "template_default";

/** Sentinel collapse-key for the shared "Common Fields" review card. */
const COMMON_CARD_KEY = "__common__";

/**
 * When the total number of review fields across all selected documents
 * exceeds this, the wizard auto-collapses cards that have no outstanding
 * "Needs Input" fields so the practitioner sees the work that remains.
 */
const AUTO_COLLAPSE_FIELD_THRESHOLD = 24;

/**
 * Fields that differ per document and should always appear under each
 * template, even when multiple templates reference them. Module-level
 * (not component state) since the list is a fixed set of field keys, not
 * derived from props or server data.
 */
const ALWAYS_PER_DOCUMENT = new Set([
  "recipient_name",
  "recipient_address",
  "recipient_email",
  "account_holder_name",
  "account_holder_email",
  "business_owner_name",
  "business_owner_email",
  "xero_organisation",
  "insolvency_type",
  "request_type",
  "myob_serial_number",
  "myob_account_code",
  "accounting_practice",
]);

/**
 * Categories that have presets but should only show inside their
 * per-document card, not in the shared common section.
 */
const TEMPLATE_SPECIFIC_CATEGORIES = new Set(["gallagher"]);

type AttachmentCandidate = {
  documentId: Id<"documents">;
  storageId: Id<"_storage">;
  fileName: string;
  fileType: string;
  fileSize: number;
  category: DocumentCategory;
  documentType?: string;
  uploadedAt: number;
  downloadUrl: string | null;
  reason: string;
};

type AttachmentSelection = {
  name: string;
  description?: string;
  required: boolean;
  attachmentKey: PrecedentAttachmentKey;
  sourceKind: PrecedentAttachmentSourceKind;
  status: "resolved" | "missing" | "skipped";
  documentCategory?: DocumentCategory;
  documentId?: Id<"documents">;
  generatedPrecedentId?: Id<"jobPrecedents">;
  generatedTemplateKey?: string;
  storageId?: Id<"_storage">;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  resolvedAt?: number;
};

type TemplateAttachmentFromDb = AttachmentRequirement & {
  storageId?: Id<"_storage">;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  generatedTemplateKey?: string;
};

function documentCategoryForAttachment(
  requirement: AttachmentRequirement,
): DocumentCategory {
  if (requirement.documentCategory) {
    return requirement.documentCategory as DocumentCategory;
  }
  switch (requirement.attachmentKey) {
    case "notice_of_appointment":
      return "notice_of_appointment";
    case "court_order":
      return "legal_disputes";
    case "rocap":
      return "director_disclosures";
    case "custom":
      return "other";
  }
}

/**
 * Suggested next phase to advance a job to once the current phase's documents
 * have been generated. Only phases with a sensible "onward" step are listed;
 * absent keys mean "no advance prompt". Currently just Day One -> Active.
 */
const NEXT_PHASE: Partial<Record<JobStatus, JobStatus>> = {
  day_one: "active",
};

// ---------------------------------------------------------------------------
// MatchReasonBadge — renders a single "why was this template suggested?" chip
//
// Each chip surfaces a specific dimension that matched (appointment type,
// job status, or a custom tag group selection) so the user understands the
// auto-selection decision rather than seeing a generic "Suggested" pill.
// ---------------------------------------------------------------------------

type TagGroupOption = { value: string; label: string };
type TagGroupLite = {
  slug: string;
  name: string;
  options?: TagGroupOption[];
};

function resolveAppointmentLabel(value: string): string {
  return APPOINTMENT_TYPE_TAGS.find((t) => t.value === value)?.label ?? value;
}

function resolveCustomTagLabels(
  groupSlug: string,
  optionValue: string,
  tagGroups: TagGroupLite[] | undefined,
): { groupLabel: string; optionLabel: string } {
  const group = tagGroups?.find((g) => g.slug === groupSlug);
  const option = group?.options?.find((o) => o.value === optionValue);
  return {
    groupLabel: group?.name ?? groupSlug,
    optionLabel: option?.label ?? optionValue,
  };
}

function MatchReasonBadge({
  reason,
  tagGroups,
}: {
  reason: MatchReason;
  tagGroups: TagGroupLite[] | undefined;
}) {
  const baseClass = "text-[8px] px-1 py-0 leading-tight";
  switch (reason.kind) {
    case "appointment_all":
      return (
        <Badge
          variant="secondary"
          className={baseClass}
          title="Available for any job"
        >
          Any job
        </Badge>
      );
    case "appointment":
      return (
        <Badge
          variant="secondary"
          className={baseClass}
          title="Matches appointment type"
        >
          {resolveAppointmentLabel(reason.value)}
        </Badge>
      );
    case "status":
      return (
        <Badge
          variant="outline"
          className={cn(baseClass, "border-blue-500/40 text-blue-700/80")}
          title="Matches job status"
        >
          {JOB_STATUS_LABELS[reason.value] ?? reason.value}
        </Badge>
      );
    case "custom": {
      const { groupLabel, optionLabel } = resolveCustomTagLabels(
        reason.groupSlug,
        reason.value,
        tagGroups,
      );
      return (
        <Badge
          variant="outline"
          className={cn(baseClass, "border-purple-500/40 text-purple-700/80")}
          title={`Matches ${groupLabel}`}
        >
          {groupLabel}: {optionLabel}
        </Badge>
      );
    }
    case "contactRole":
      return (
        <Badge
          variant="outline"
          className={cn(baseClass, "border-amber-500/40 text-amber-700/80")}
          title="Generates one document per matching contact"
        >
          {reason.label}
        </Badge>
      );
    case "jobContactCustomRole":
      return (
        <Badge
          variant="outline"
          className={cn(baseClass, "border-amber-500/40 text-amber-700/80")}
          title="Generates one document per matching contact"
        >
          {reason.label}
        </Badge>
      );
  }
}

// ---------------------------------------------------------------------------
// ContactFanOutPicker — collapsed-by-default per-template contact list
//
// Templates tagged with `cr:*` (job contact role) and/or `fc:*` (firm
// contact category) tags fan out at generation time: one document per
// matching active contact, across BOTH the per-job `jobContacts` and the
// org-wide `firmContacts` directory. This picker renders a one-line
// summary with a chevron to expand the list of matching contacts as
// toggle rows. All contacts are ticked by default (lazily seeded by the
// parent the first time the picker is rendered) and the user can deselect
// any individual contact to skip them in the fan-out.
// ---------------------------------------------------------------------------

/**
 * A single fan-out recipient — normalised across the job-contact and
 * firm-contact domains so the picker and generation loop have one path.
 */
type FanOutContact = {
  /** Convex id of the source row (`jobContacts` or `firmContacts`). */
  _id: string;
  domain: ContactFieldDomain;
  /**
   * The matched role used to resolve `contact:*` fields:
   * `ContactRole` for `job`, `FirmContactCategory` for `firm`.
   */
  role: string;
  displayName: string;
  /** Human role labels shown beside the name in the picker. */
  roleLabels: string[];
  /** Contact data used to fill recipient / contact fields. */
  data: ContactDataForFields;
  /** Firm-contact category — only set for `firm` domain. */
  category?: FirmContactCategory;
  /** Raw firm-contact custom field values — only set for `firm` domain. */
  customFields?: Record<string, string>;
  /**
   * Set when the contact matched via an org-defined custom role (`jcr:*`)
   * rather than a built-in `cr:*` role. Drives the label/badge in the
   * fan-out picker so a custom-role match reads with its own name/colour/
   * icon instead of the generic "Contact" fallback. `role` is left empty
   * for these matches — custom roles have no built-in source-kind mapping.
   */
  customRole?: {
    label: string;
    color: BadgeColor;
    Icon: LucideIcon | null;
  };
};

/**
 * Derived address state for a fan-out contact (MOR-913) — first non-empty
 * `state` across its addresses, upper-cased. Drives the state filter chips
 * in `ContactFanOutPicker` so long creditor lists can be narrowed by state.
 */
function fanContactState(c: FanOutContact): string | null {
  for (const address of c.data.addresses ?? []) {
    const state = address.state?.trim();
    if (state) return state.toUpperCase();
  }
  return null;
}

/** Human label for a fan-out contact's matched role / category. */
function fanContactRoleLabel(c: FanOutContact): string {
  if (c.customRole) return c.customRole.label;
  return c.domain === "firm"
    ? (FIRM_CATEGORY_META[c.role as FirmContactCategory]?.label ??
        "Firm Contact")
    : (ROLE_META[c.role as ContactRole]?.label ?? "Contact");
}

/**
 * Badge metadata (label + colour + icon) for a fan-out contact's role,
 * so the preview header can show the same coloured role chip used in the
 * Contacts legend. Firm contacts share the `orange` colour (mirrors the
 * wizard's firm-contact chips); job roles use their own `badgeColor`.
 */
function fanContactRoleBadge(c: FanOutContact): {
  label: string;
  color: BadgeColor;
  Icon: LucideIcon | null;
} {
  if (c.customRole) {
    return {
      label: c.customRole.label,
      color: c.customRole.color,
      Icon: c.customRole.Icon,
    };
  }
  if (c.domain === "firm") {
    const meta = FIRM_CATEGORY_META[c.role as FirmContactCategory];
    return {
      label: meta?.shortLabel ?? "Firm Contact",
      color: "orange",
      Icon: meta?.icon ?? null,
    };
  }
  const meta = ROLE_META[c.role as ContactRole];
  return {
    label: meta?.label ?? "Contact",
    color: meta?.badgeColor ?? "gray",
    Icon: meta?.icon ?? null,
  };
}

function summariseFanOutRoles(contacts: ReadonlyArray<FanOutContact>): {
  singular: string;
  plural: string;
} {
  const counts = new Map<
    string,
    { count: number; role: string; domain: string }
  >();
  for (const c of contacts) {
    const label = fanContactRoleLabel(c);
    const prev = counts.get(label);
    if (!prev || c.domain === "job") {
      counts.set(label, {
        count: (prev?.count ?? 0) + 1,
        role: c.role,
        domain: c.domain,
      });
    } else {
      prev.count++;
    }
  }
  const { top } = [...counts].reduce<{
    top: { label: string; role: string; domain: string } | null;
    topCount: number;
  }>(
    (best, [label, entry]) =>
      entry.count > best.topCount
        ? {
            top: { label, role: entry.role, domain: entry.domain },
            topCount: entry.count,
          }
        : best,
    { top: null, topCount: 0 },
  );
  if (!top) return { singular: "Contact", plural: "Contacts" };

  if (top.domain === "job") {
    const meta = ROLE_META[top.role as ContactRole];
    if (meta) return { singular: meta.label, plural: meta.pluralLabel };
  }
  if (top.domain === "firm") {
    const meta = FIRM_CATEGORY_META[top.role as FirmContactCategory];
    if (meta) return { singular: meta.label, plural: meta.label + "s" };
  }
  return { singular: top.label, plural: top.label + "s" };
}

// ---------------------------------------------------------------------------
// FanOutContactPreview — collapsible summary of pre-filled contact fields
// ---------------------------------------------------------------------------

/** Parts that map to a single scalar field on the contact record. */
const SCALAR_PART_TO_PATCH_KEY: Partial<Record<ContactFieldPart, string>> = {
  name: "displayName",
  date_of_birth: "dateOfBirth",
  email: "email",
  phone: "phone",
  abn: "abn",
  acn: "acn",
  // External administration text overlay (job contacts). The appointee link
  // and derived firm/primary-contact parts are set from the contact panel.
  external_admin_status: "externalAdminStatus",
  external_admin_appointee_title: "externalAdminAppointeeTitle",
};

/** Address parts → the key on a single `addresses[]` entry. */
const ADDRESS_PART_TO_ADDR_KEY: Partial<Record<ContactFieldPart, string>> = {
  address_line1: "line1",
  address_line2: "line2",
  address_suburb: "suburb",
  address_state: "state",
  address_postcode: "postcode",
};

/**
 * Single editable contact-field card. Mirrors the visual contract of
 * `FieldReviewInput` / `SourceFieldReviewInput` (label, source badge,
 * input) so contact fields read consistently with the per-template fields
 * below. Populated fields show a green "From Contact" badge; empty ones
 * show "Needs Input" (amber) and an editable input. Edits write straight
 * back to the contact record (job → `updateJobContactFields`, firm →
 * `updateFirmContact`, admins only). `address_full` is composite and
 * firm contacts have no DOB, so those render read-only.
 */
function ContactFieldReviewInput({
  contact,
  part,
  label,
  canEditFirmContacts,
}: {
  contact: FanOutContact;
  part: ContactFieldPart;
  label: string;
  canEditFirmContacts: boolean;
}) {
  const current = getContactPartValue(part, contact.data);
  const [draft, setDraft] = useState(current);
  const [saving, setSaving] = useState(false);
  const updateJobContact = useConvexMutation(
    api.contacts.mutations.updateJobContactFields,
  );
  const updateFirmContact = useConvexMutation(
    api.contacts.mutations.updateFirmContact,
  );

  // Re-sync the draft when the underlying value changes (e.g. after a save
  // elsewhere or a different contact reusing this slot).
  useEffect(() => setDraft(current), [current]);

  const scalarKey = SCALAR_PART_TO_PATCH_KEY[part];
  const addrKey = ADDRESS_PART_TO_ADDR_KEY[part];
  const isFirm = contact.domain === "firm";
  // Firm contacts have no DOB; `address_full` is composite (read-only).
  const supported =
    (Boolean(scalarKey) && !(isFirm && part === "date_of_birth")) ||
    Boolean(addrKey);
  const editable = supported && !(isFirm && !canEditFirmContacts);

  const isEmpty = current.trim() === "";
  const sourceLabel = isEmpty ? "Needs Input" : "From Contact";
  const sourceColor = isEmpty
    ? "text-amber-600 bg-amber-50 dark:bg-amber-950/20"
    : "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20";

  const inputId = `contact-${contact._id}-${part}`;

  const save = async () => {
    const trimmed = draft.trim();
    if (saving || !editable) return;
    if (trimmed === current.trim()) return; // no change
    // Display name can't be cleared — revert an empty edit.
    if (!trimmed && part === "name") {
      setDraft(current);
      return;
    }
    setSaving(true);
    try {
      // Patch the first address, preserving its other parts; create one
      // with a manual-entry label when the contact has none yet.
      const patch: Record<string, unknown> = addrKey
        ? (() => {
            const existing = contact.data.addresses ?? [];
            const first = existing[0] ?? { sourceLabel: "Manual entry" };
            return {
              addresses: [
                { ...first, [addrKey]: trimmed },
                ...existing.slice(1),
              ],
            };
          })()
        : { [scalarKey as string]: trimmed };

      if (isFirm) {
        await updateFirmContact({
          firmContactId: contact._id as Id<"firmContacts">,
          patch,
          reason: "Edited from document generation preview",
        });
      } else {
        await updateJobContact({
          contactId: contact._id as Id<"jobContacts">,
          patch,
          reason: "Edited from document generation preview",
        });
      }
      toast.success("Contact updated");
    } catch {
      toast.error("Failed to update contact");
      setDraft(current);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="mb-1.5 flex min-h-[2.5rem] flex-col justify-end">
        <div className="flex items-baseline justify-between gap-2">
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-primary/70"
          >
            {label}
          </label>
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-medium",
              sourceColor,
            )}
          >
            {sourceLabel}
          </span>
        </div>
      </div>
      {editable ? (
        <Input
          id={inputId}
          type="text"
          placeholder="Add…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          disabled={saving}
          className={cn(
            "h-9 text-sm",
            isEmpty && "border-amber-200 dark:border-amber-900/30",
          )}
        />
      ) : (
        <div className="flex h-9 items-center rounded-md border border-input bg-muted/30 px-3 text-sm text-primary/70">
          {current || (
            <span className="italic text-primary/40">
              {isFirm ? "Add in firm directory" : "—"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function FanOutContactPreview({
  contacts,
  expectedParts,
  canEditFirmContacts,
  isExternalAdminTemplate,
  notice,
}: {
  contacts: FanOutContact[];
  expectedParts: ContactFieldPart[];
  canEditFirmContacts: boolean;
  /**
   * Template is narrowed to companies in external administration
   * (`jcf:external_admin`): label parts the way the editor chips do
   * ("EXAD: Primary Contact Email") so the preview reads like the template.
   */
  isExternalAdminTemplate?: boolean;
  /**
   * Optional explanation shown under the header when the matched list is
   * not what the template's tags would normally produce (e.g. a "match the
   * job's state" template on a job with no state recorded yet).
   */
  notice?: string;
}) {
  const missingCount = missingContactFields(contacts, expectedParts).length;
  // Auto-expand when something needs input. For external-administration
  // templates, stay expanded even when complete: these letters are addressed
  // to the appointee (who now fills the primary-contact parts), so the
  // practitioner expects to see and verify those values rather than a
  // collapsed "auto-fills" one-liner.
  const [open, setOpen] = useState(
    missingCount > 0 || Boolean(isExternalAdminTemplate),
  );

  const resolvedParts = expectedParts.map((part) => ({
    part,
    label: isExternalAdminTemplate
      ? `${EXTERNAL_ADMIN_ROLE_LABEL}: ${jobContactPartLabel(part, EXTERNAL_ADMIN_ROLE_LABEL)}`
      : getContactFieldPartMeta(part).shortLabel,
  }));

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-primary/10 bg-background">
      <button
        type="button"
        className="flex w-full items-center gap-2 border-b border-primary/10 bg-primary/[0.02] px-4 py-2.5 text-left text-xs"
        onClick={() => setOpen((v) => !v)}
      >
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-primary/40 transition",
            open && "rotate-180",
          )}
        />
        <span className="font-medium text-primary">
          {contacts.length} document{contacts.length === 1 ? "" : "s"} will be
          generated
        </span>
        {missingCount > 0 ? (
          <span className="ml-auto shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-600 dark:bg-amber-950/20 dark:text-amber-400">
            {missingCount} field{missingCount === 1 ? "" : "s"} need
            {missingCount === 1 ? "s" : ""} input
          </span>
        ) : (
          <span className="text-primary/50">
            — fields auto-fill from each contact
          </span>
        )}
      </button>

      {notice && (
        <p className="border-b border-primary/10 bg-amber-50/60 px-4 py-2 text-[11px] leading-snug text-amber-700 dark:bg-amber-950/20 dark:text-amber-400">
          {notice}
        </p>
      )}

      {open && (
        <div className="flex flex-col gap-3 p-3">
          {contacts.map((contact) => {
            const roleBadge = fanContactRoleBadge(contact);
            return (
              <div
                key={contact._id}
                className="overflow-hidden rounded-lg border border-primary/10 bg-background"
              >
                <div className="flex items-center justify-between gap-2 border-b border-primary/10 bg-primary/[0.02] px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium text-primary">
                      {contact.displayName}
                    </span>
                    <Badge
                      variant="soft"
                      color={roleBadge.color}
                      size="sm"
                      className="shrink-0 gap-1"
                    >
                      {roleBadge.Icon && <roleBadge.Icon className="size-3" />}
                      {roleBadge.label}
                    </Badge>
                  </div>
                </div>
                <div className="grid gap-x-6 gap-y-4 p-3 sm:grid-cols-2">
                  {resolvedParts.map(({ part, label }) => (
                    <ContactFieldReviewInput
                      key={part}
                      contact={contact}
                      part={part}
                      label={label}
                      canEditFirmContacts={canEditFirmContacts}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SourceFanOutPreview — second-tier preview shown when a template's body
// references `contact_job_<source>_*` fields. Each ticked contact then
// fans out to one row per matching `sourceLinks[]` entry (filtered by
// the template's role tags and the role's allowed source kinds), with
// the link's headline detail surfaced so the practitioner can see which
// security interest / liability / loan / etc. each generated document
// will draw from.
//
// Override semantics: every row is keyed by
// `${templateKey}::${contactId}::${rowIndex}` and presents a Select of
// the contact's other links of the same kind so the practitioner can
// last-minute swap "use SI #2 instead of SI #1" before generation
// without going back to the contact record. An unfilled override means
// the row uses its natural (default) link, which is the first matching
// source entry returned by `sourceFanOutByTemplate`.
// ---------------------------------------------------------------------------

type SourceFanOutEntry = {
  sourceKind: ContactSourceKind;
  link: {
    sourceId: string;
    roleLabel: string;
    detail?: string;
    amount?: number;
  } | null;
  /**
   * Pre-resolved `contact_job_<kind>_<part>` values for this row,
   * computed by the parent via `resolveContactSourceFields`. Empty for
   * "no matching source" rows (`link == null`).
   */
  resolvedFields: Record<string, string>;
};

type SourceFanOutPreviewContact = {
  contactId: string;
  displayName: string;
  roleLabel: string;
  /**
   * The contact's tagged role enum — used to look up the same Lucide
   * icon that the contact-role checkbox list in template settings
   * shows next to each role, so the preview reads consistently with
   * where the role was selected.
   */
  role: ContactRole;
  /** Natural (default) source rows for this (template × contact). */
  rows: ReadonlyArray<SourceFanOutEntry>;
  /**
   * Every available link on the contact keyed by source kind — feeds
   * the override Select so the practitioner can pick from links beyond
   * the natural defaults (e.g. swap to a different security interest).
   */
  allLinksByKind: ReadonlyMap<
    ContactSourceKind,
    ReadonlyArray<{
      sourceId: string;
      roleLabel: string;
      detail?: string;
      amount?: number;
      /** Pre-resolved fields for THIS link so swapping picks up
       * defaults for the swapped row immediately. */
      resolvedFields: Record<string, string>;
    }>
  >;
};

function formatSourceLinkSubdetail(
  link: { detail?: string; amount?: number } | null | undefined,
): string | null {
  if (!link) return null;
  const parts: string[] = [];
  if (link.detail) parts.push(link.detail);
  if (link.amount !== undefined && Number.isFinite(link.amount)) {
    const formatted = new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      maximumFractionDigits: 0,
    }).format(link.amount);
    parts.push(formatted);
  }
  return parts.length > 0 ? parts.join(" — ") : null;
}

/**
 * Single editable source-field card. Mirrors the visual contract of
 * `FieldReviewInput` (label, source badge, input) but uses a tighter
 * API — source fields don't need practitioner/company combo support and
 * always render as plain text inputs (resolved values are already
 * formatted strings, e.g. "21/04/2026", "$45,000").
 */
function SourceFieldReviewInput({
  field,
  value,
  source,
  onChange,
}: {
  field: { key: string; label: string; description?: string };
  value: string;
  source: "source" | "edited" | "empty";
  onChange: (value: string) => void;
}) {
  const sourceLabel = {
    source: "From Source",
    edited: "Edited",
    empty: "Needs Input",
  }[source];

  const sourceColor = {
    source: "text-rose-700 bg-rose-50 dark:bg-rose-950/20 dark:text-rose-300",
    edited: "text-violet-600 bg-violet-50 dark:bg-violet-950/20",
    empty: "text-amber-600 bg-amber-50 dark:bg-amber-950/20",
  }[source];

  return (
    <div className="flex flex-col">
      <div className="mb-1.5 flex min-h-[2.5rem] flex-col justify-end">
        <div className="flex items-baseline justify-between gap-2">
          <label
            htmlFor={`src-${field.key}`}
            className="text-sm font-medium text-primary/70"
          >
            {field.label}
          </label>
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-medium",
              sourceColor,
            )}
          >
            {sourceLabel}
          </span>
        </div>
      </div>
      <Input
        id={`src-${field.key}`}
        type="text"
        placeholder=""
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-9 text-sm",
          source === "empty" && "border-amber-200 dark:border-amber-900/30",
        )}
      />
    </div>
  );
}

function SourceFanOutPreview({
  templateKey,
  contacts,
  templateSourceFieldParts,
  linkOverrides,
  fieldOverrides,
  onLinkOverride,
  onFieldOverride,
  buildRowKey,
}: {
  templateKey: string;
  contacts: ReadonlyArray<SourceFanOutPreviewContact>;
  /**
   * The set of source-field parts the template actually references,
   * keyed by source kind. Used to scope the editable cards so we only
   * show fields the document will use (showing all 11 security-interest
   * parts when the template only uses 2 would clutter the panel).
   */
  templateSourceFieldParts: ReadonlyMap<ContactSourceKind, ReadonlySet<string>>;
  linkOverrides: ReadonlyMap<
    string,
    { sourceKind: ContactSourceKind; sourceId: string }
  >;
  fieldOverrides: ReadonlyMap<string, Record<string, string>>;
  onLinkOverride: (
    rowKey: string,
    sourceKind: ContactSourceKind,
    sourceId: string,
  ) => void;
  onFieldOverride: (rowKey: string, fieldKey: string, value: string) => void;
  buildRowKey: (
    templateKey: string,
    contactId: string,
    rowIndex: number,
  ) => string;
}) {
  const [open, setOpen] = useState(true);

  if (contacts.length === 0) return null;

  const totalRows = contacts.reduce((sum, c) => sum + c.rows.length, 0);

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-rose-200/70 bg-background dark:border-rose-900/40">
      <button
        type="button"
        className="flex w-full items-center gap-2 border-b border-rose-200/70 bg-rose-50/60 px-4 py-2.5 text-left text-xs dark:border-rose-900/40 dark:bg-rose-950/20"
        onClick={() => setOpen((v) => !v)}
      >
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-rose-700/70 transition dark:text-rose-300/70",
            open && "rotate-180",
          )}
        />
        <span className="font-medium text-rose-900 dark:text-rose-200">
          {totalRows} document{totalRows === 1 ? "" : "s"} from contact sources
        </span>
        <span className="text-rose-800/70 dark:text-rose-300/60">
          — one per matching link (security interest, liability, loan, etc.)
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-3 p-3">
          {contacts.map((contact) => {
            // Same Lucide icon that the contact-role checkbox list in
            // template settings shows next to each role — keeps the
            // preview visually anchored to where the role was picked.
            const RoleIcon = ROLE_META[contact.role]?.icon ?? null;
            return (
              <div
                key={contact.contactId}
                className="overflow-hidden rounded-lg border border-primary/10 bg-background"
              >
                <div className="flex items-center justify-between gap-2 border-b border-primary/10 bg-primary/[0.02] px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium text-primary">
                      {contact.displayName}
                    </span>
                    {RoleIcon && (
                      <RoleIcon className="size-3.5 shrink-0 text-primary/45" />
                    )}
                    <span className="truncate text-xs text-primary/50">
                      {contact.roleLabel}
                    </span>
                  </div>
                  <span className="shrink-0 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-rose-700 dark:bg-rose-950/20 dark:text-rose-300">
                    {contact.rows.length} doc
                    {contact.rows.length === 1 ? "" : "s"}
                  </span>
                </div>

                <div className="flex flex-col">
                  {contact.rows.map((entry, rowIndex) => {
                    const rowKey = buildRowKey(
                      templateKey,
                      contact.contactId,
                      rowIndex,
                    );
                    // No matching source link on the contact — surface
                    // the warning row so the practitioner sees the doc
                    // will generate with blank source fields rather than
                    // silently dropping the row.
                    if (!entry.link) {
                      return (
                        <div
                          key={rowKey}
                          className="flex flex-wrap items-baseline gap-2 border-t border-primary/10 bg-amber-50/40 px-3 py-2.5 text-xs first:border-t-0 dark:bg-amber-950/10"
                        >
                          <span className="font-medium text-amber-800 dark:text-amber-300">
                            {CONTACT_SOURCE_KIND_LABELS[entry.sourceKind]}:
                          </span>
                          <span className="italic text-amber-700/90 dark:text-amber-300/80">
                            No matching source — document will generate with
                            blank source fields.
                          </span>
                        </div>
                      );
                    }

                    const naturalLink = entry.link;
                    const override = linkOverrides.get(rowKey);
                    const activeKind = override?.sourceKind ?? entry.sourceKind;
                    const optionsForKind =
                      contact.allLinksByKind.get(activeKind) ?? [];
                    const effectiveOpt = override
                      ? (optionsForKind.find(
                          (o) => o.sourceId === override.sourceId,
                        ) ?? null)
                      : null;
                    const effectiveLink: {
                      sourceId: string;
                      roleLabel: string;
                      detail?: string;
                      amount?: number;
                    } = effectiveOpt
                      ? {
                          sourceId: effectiveOpt.sourceId,
                          roleLabel: effectiveOpt.roleLabel,
                          detail: effectiveOpt.detail,
                          amount: effectiveOpt.amount,
                        }
                      : naturalLink;
                    const effectiveResolved =
                      effectiveOpt?.resolvedFields ?? entry.resolvedFields;

                    const subdetail = formatSourceLinkSubdetail(effectiveLink);
                    // Only show the parts this template actually
                    // references. Falls back to every part of the source
                    // kind if (somehow) the template scoped set is empty
                    // — keeps the panel useful instead of rendering as
                    // an empty card.
                    const referencedParts =
                      templateSourceFieldParts.get(activeKind) ?? null;
                    const allMetaForKind: ReadonlyArray<ContactSourceFieldMeta> =
                      CONTACT_SOURCE_FIELDS[activeKind];
                    const visibleMeta = referencedParts
                      ? allMetaForKind.filter((m) =>
                          referencedParts.has(m.part),
                        )
                      : allMetaForKind;

                    const overridesForRow = fieldOverrides.get(rowKey) ?? {};

                    return (
                      <div
                        key={rowKey}
                        className="border-t border-primary/10 px-3 py-3 first:border-t-0"
                      >
                        {/* Source-link header bar — names which security
                          interest / liability / loan this doc draws
                          from, plus the override Select for swapping
                          to a sibling link of the same kind. The
                          sub-detail (e.g. "Director (appointed
                          2016-03-14, ceased 2024-06-30). Shareholder…")
                          lives on its own row so long explanatory text
                          wraps cleanly rather than being truncated. */}
                        <div className="mb-3 rounded-md bg-rose-50/50 px-2.5 py-2 text-xs dark:bg-rose-950/15">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="shrink-0 font-medium text-rose-800 dark:text-rose-300">
                              {CONTACT_SOURCE_KIND_LABELS[activeKind]}:
                            </span>
                            <span className="text-primary">
                              {effectiveLink.roleLabel}
                            </span>
                            {optionsForKind.length > 1 && (
                              <div className="ml-auto w-56">
                                <Select
                                  value={
                                    override?.sourceId ??
                                    naturalLink.sourceId ??
                                    optionsForKind[0]?.sourceId ??
                                    ""
                                  }
                                  onValueChange={(v) =>
                                    onLinkOverride(rowKey, activeKind, v)
                                  }
                                >
                                  <SelectTrigger className="h-7 text-xs">
                                    <SelectValue placeholder="Switch source…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {optionsForKind.map((opt) => {
                                      const optDetail =
                                        formatSourceLinkSubdetail(opt);
                                      const label = optDetail
                                        ? `${opt.roleLabel} — ${optDetail}`
                                        : opt.roleLabel;
                                      return (
                                        <SelectItem
                                          key={opt.sourceId}
                                          value={opt.sourceId}
                                        >
                                          {label}
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          {subdetail && (
                            <p className="mt-1.5 whitespace-pre-wrap break-words leading-relaxed text-primary/60">
                              {subdetail}
                            </p>
                          )}
                        </div>

                        {/* Editable source-field cards — mirror the
                          standard FieldReviewInput layout (label +
                          source badge + input) so source-data fields
                          feel native alongside the regular per-template
                          fields below. */}
                        {visibleMeta.length === 0 ? (
                          <p className="text-xs italic text-primary/40">
                            This template doesn't reference any{" "}
                            {CONTACT_SOURCE_KIND_LABELS[
                              activeKind
                            ].toLowerCase()}{" "}
                            fields directly.
                          </p>
                        ) : (
                          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                            {visibleMeta.map((meta) => {
                              const fieldKey = buildContactSourceFieldKey({
                                domain: "job",
                                sourceKind: activeKind,
                                part: meta.part,
                              });
                              const resolved =
                                effectiveResolved[fieldKey] ?? "";
                              const override = overridesForRow[fieldKey];
                              const value =
                                override !== undefined ? override : resolved;
                              const fieldSource: "source" | "edited" | "empty" =
                                override !== undefined
                                  ? "edited"
                                  : resolved
                                    ? "source"
                                    : "empty";
                              return (
                                <SourceFieldReviewInput
                                  key={fieldKey}
                                  field={{ key: fieldKey, label: meta.label }}
                                  value={value}
                                  source={fieldSource}
                                  onChange={(v) =>
                                    onFieldOverride(rowKey, fieldKey, v)
                                  }
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Replaces the bare "Not configured" amber line with an actionable hint
 * that names the actual blocker (no source file / extracting / failed)
 * and deep-links the user to the precedent library entry so they can
 * upload, retry, or inspect the failure without losing context.
 */
function NotConfiguredHint({
  hasSourceFile,
  extractionStatus,
  extractionError,
  templateKey: _templateKey,
}: {
  hasSourceFile?: boolean;
  extractionStatus?: "pending" | "extracting" | "completed" | "failed";
  extractionError?: string;
  templateKey: string;
}) {
  const {
    message,
    tone,
  }: {
    message: string;
    tone: "amber" | "rose" | "slate";
  } = (() => {
    if (!hasSourceFile) {
      return {
        message: "No source document — upload a file in the Precedent Library.",
        tone: "slate",
      };
    }
    if (extractionStatus === "extracting" || extractionStatus === "pending") {
      return {
        message: "Still extracting fields — try again in a moment.",
        tone: "amber",
      };
    }
    if (extractionStatus === "failed") {
      return {
        message: extractionError
          ? `Extraction failed: ${extractionError}`
          : "Extraction failed — re-upload or retry from the Precedent Library.",
        tone: "rose",
      };
    }
    // completed but somehow not in configuredKeys — defensive fallback
    return {
      message: "Not configured — finish setup in the Precedent Library.",
      tone: "amber",
    };
  })();

  const toneClass =
    tone === "rose"
      ? "text-rose-600 dark:text-rose-400"
      : tone === "slate"
        ? "text-primary/50"
        : "text-amber-600 dark:text-amber-400";

  return (
    <div
      className={cn("mt-1 flex flex-wrap items-center gap-1", toneClass)}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="text-[10px]">{message}</span>
      <Link
        to="/dashboard/settings/docs"
        className="text-[10px] underline underline-offset-2 hover:no-underline"
      >
        Open library
      </Link>
    </div>
  );
}

function ContactFanOutPicker({
  templateKey: _templateKey,
  matchingContacts,
  selectedContactIds,
  expanded,
  onToggleExpanded,
  onToggleContact,
  onSelectAll,
}: {
  templateKey: string;
  matchingContacts: ReadonlyArray<FanOutContact>;
  selectedContactIds: ReadonlySet<string>;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggleContact: (contactId: string) => void;
  onSelectAll: (selectAll: boolean) => void;
}) {
  const total = matchingContacts.length;
  const selected = selectedContactIds.size;
  const { singular, plural } = summariseFanOutRoles(matchingContacts);
  const roleNoun = selected === 1 ? singular : plural;
  const summary =
    selected === total
      ? `Generating ${total} doc${total === 1 ? "" : "s"} for selected ${roleNoun}`
      : `Generating ${selected} of ${total} doc${total === 1 ? "" : "s"} for selected ${roleNoun}`;

  const [contactSearch, setContactSearch] = useState("");
  /** Active address-state chips (MOR-913). `"__none__"` = contacts with no state. */
  const [activeStates, setActiveStates] = useState<Set<string>>(new Set());
  // Long contact lists (hundreds of creditors/members) are unwieldy, so
  // filter the visible rows by name and address state. Counts and Select
  // all / Clear still act on the full set — filters only narrow what's
  // shown; "Select shown" acts on the narrowed rows.
  const showSearch = total > 6;

  // Distinct address states across the matched contacts, AU order first,
  // then anything else alphabetically, then a no-state bucket. Chips are
  // only rendered when there is more than one bucket to choose between.
  const stateOptions = useMemo(() => {
    const present = new Set<string>();
    for (const c of matchingContacts) {
      const state = fanContactState(c);
      if (state) present.add(state);
    }
    const hasNone = matchingContacts.some((c) => !fanContactState(c));
    const known = AU_STATE_VALUES.filter((s) => present.has(s));
    const other = [...present]
      .filter((s) => !AU_STATE_VALUES.includes(s))
      .sort();
    const options = [...known, ...other].map((s) => ({ value: s, label: s }));
    if (hasNone && options.length > 0) {
      options.push({ value: "__none__", label: "No state" });
    }
    return options;
  }, [matchingContacts]);

  const toggleState = (value: string) =>
    setActiveStates((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });

  const visibleContacts = useMemo(() => {
    const needle = contactSearch.trim().toLowerCase();
    return matchingContacts.filter((c) => {
      if (needle && !(c.displayName || "").toLowerCase().includes(needle)) {
        return false;
      }
      if (activeStates.size > 0) {
        const state = fanContactState(c) ?? "__none__";
        if (!activeStates.has(state)) return false;
      }
      return true;
    });
  }, [matchingContacts, contactSearch, activeStates]);

  const isNarrowed = contactSearch.trim().length > 0 || activeStates.size > 0;

  return (
    <div className="ml-7 mt-2 rounded-md border border-amber-500/30 bg-amber-50/40">
      <button
        type="button"
        onClick={onToggleExpanded}
        className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left"
      >
        <span className="text-[11px] font-medium text-amber-800/90">
          {summary}
        </span>
        <ChevronDown
          className={cn(
            "size-3.5 text-amber-700/70 transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>
      {expanded && (
        <div className="border-t border-amber-500/20 px-3 py-2">
          <div className="mb-2 flex items-center justify-between text-[10px] text-amber-800/70">
            <span>
              {selected} of {total} selected
            </span>
            <div className="flex gap-2">
              {isNarrowed && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      // Select every currently-visible unselected contact;
                      // parent toggles use functional setState so the calls
                      // compose in a single React batch.
                      for (const c of visibleContacts) {
                        if (!selectedContactIds.has(c._id)) {
                          onToggleContact(c._id);
                        }
                      }
                    }}
                    className="font-medium hover:underline"
                  >
                    Select shown
                  </button>
                  <span className="text-amber-700/30">·</span>
                  <button
                    type="button"
                    onClick={() => {
                      for (const c of visibleContacts) {
                        if (selectedContactIds.has(c._id)) {
                          onToggleContact(c._id);
                        }
                      }
                    }}
                    className="font-medium hover:underline"
                  >
                    Clear shown
                  </button>
                  <span className="text-amber-700/30">·</span>
                </>
              )}
              <button
                type="button"
                onClick={() => onSelectAll(true)}
                className="font-medium hover:underline"
              >
                Select all
              </button>
              <span className="text-amber-700/30">·</span>
              <button
                type="button"
                onClick={() => onSelectAll(false)}
                className="font-medium hover:underline"
              >
                Clear
              </button>
            </div>
          </div>
          {stateOptions.length > 1 && (
            <div className="mb-2 flex flex-wrap items-center gap-1">
              {stateOptions.map((opt) => {
                const active = activeStates.has(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleState(opt.value)}
                    className={cn(
                      "rounded-full border px-2 py-0.5 text-[10px] font-medium transition",
                      active
                        ? "border-amber-600/60 bg-amber-200/70 text-amber-900"
                        : "border-amber-500/30 bg-background/60 text-amber-800/70 hover:bg-amber-100/60",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
              {activeStates.size > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveStates(new Set())}
                  className="ml-1 text-[10px] text-amber-800/60 hover:underline"
                >
                  Reset
                </button>
              )}
            </div>
          )}
          {showSearch && (
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 size-3 -translate-y-1/2 text-amber-700/50" />
              <input
                type="text"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder={`Search ${plural.toLowerCase()}…`}
                className="h-7 w-full rounded border border-amber-500/30 bg-background/60 pl-7 pr-6 text-xs text-primary/80 placeholder:text-amber-800/40 focus:border-amber-500/60 focus:outline-none"
              />
              {contactSearch && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setContactSearch("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-amber-700/50 hover:text-amber-800"
                >
                  <XIcon className="size-3" />
                </button>
              )}
            </div>
          )}
          <div className="space-y-1">
            {visibleContacts.length === 0 ? (
              <p className="px-1.5 py-2 text-[11px] italic text-amber-800/50">
                No {plural.toLowerCase()} match “{contactSearch}”
              </p>
            ) : (
              visibleContacts.map((contact) => {
                const checked = selectedContactIds.has(contact._id);
                return (
                  <label
                    key={contact._id}
                    className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-amber-100/60"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => onToggleContact(contact._id)}
                    />
                    <span className="truncate text-xs font-medium text-primary/80">
                      {contact.displayName || "Unnamed contact"}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// FirmRefPicker — per-template firm-contact REFERENCE selectors. Unlike
// the fan-out picker above (which multiplies the document), each row here
// binds a single firm contact to that category's `firmref_*` placeholders.
// One Select per referenced category; auto-resolves when a category has a
// single contact.
function FirmRefPicker({
  categories,
  contactsByCategory,
  selections,
  onSelect,
}: {
  categories: ReadonlyArray<FirmContactCategory>;
  contactsByCategory: Map<
    FirmContactCategory,
    ReadonlyArray<{ _id: Id<"firmContacts">; displayName: string }>
  >;
  selections: Map<FirmContactCategory, Id<"firmContacts">> | undefined;
  onSelect: (
    category: FirmContactCategory,
    contactId: Id<"firmContacts">,
  ) => void;
}) {
  return (
    <div className="ml-7 mt-2 space-y-1.5 rounded-md border border-teal-500/30 bg-teal-50/40 px-3 py-2">
      <div className="text-[11px] font-medium text-teal-800/90">
        Referenced firm contacts
      </div>
      {categories.map((category) => {
        const contacts = contactsByCategory.get(category) ?? [];
        const picked =
          selections?.get(category) ??
          (contacts.length === 1 ? contacts[0]._id : undefined);
        return (
          <div key={category} className="flex items-center gap-2">
            <span className="w-24 shrink-0 truncate text-[11px] text-teal-800/70">
              {FIRM_CATEGORY_META[category]?.shortLabel ?? category}
            </span>
            {contacts.length === 0 ? (
              <span className="text-[11px] italic text-teal-800/50">
                No contacts — add one under Contacts
              </span>
            ) : (
              <Select
                value={picked ?? ""}
                onValueChange={(v) =>
                  onSelect(category, v as Id<"firmContacts">)
                }
              >
                <SelectTrigger className="h-7 flex-1 text-xs">
                  <SelectValue placeholder="Select a contact…" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => (
                    <SelectItem key={c._id} value={c._id} className="text-xs">
                      {c.displayName || "Unnamed contact"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

function GeneratePrecedentsPage() {
  const { jobId } = Route.useParams();
  const { reselect } = Route.useSearch();
  const navigate = useNavigate();
  const { orgId, activeOrg } = useActiveOrg();
  const canEditFirmContacts =
    activeOrg?.role === "owner" || activeOrg?.role === "admin";

  const { data: job, isLoading: jobLoading } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );

  const { data: templates } = useQuery({
    ...convexQuery(api.settings.precedents.queries.listByCategory, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  const { data: categories } = useQuery({
    ...convexQuery(api.settings.precedents.categories.listCategories, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  // Appointee jurisdiction + public holidays, so `business_days` date calcs
  // resolve against the right calendar while the user reviews field values.
  const { data: dateCalcCalendar } = useQuery(
    convexQuery(api.jobs.precedents.queries.getJobDateCalcContext, {
      jobId: jobId as Id<"jobs">,
    }),
  );

  const dateCalcContext = useMemo<DateCalcContext>(
    () => ({
      jurisdiction: dateCalcCalendar?.jurisdiction as
        DateCalcContext["jurisdiction"] | undefined,
      proclaimed: dateCalcCalendar?.proclaimed as
        DateCalcContext["proclaimed"] | undefined,
    }),
    [dateCalcCalendar],
  );

  const { data: allPresets } = useQuery({
    ...convexQuery(api.settings.precedents.presets.listByOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  const { data: favouriteIds } = useQuery({
    ...convexQuery(api.settings.precedents.favourites.listByUser, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  const { data: customFieldDefs } = useQuery({
    ...convexQuery(api.settings.precedents.customFields.listByOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  // Tag groups drive label resolution for the per-template "Match" chips
  // (so a `tg:industry:construction` reason renders as "Industry:
  // Construction" instead of the raw slug).
  const { data: tagGroups } = useQuery({
    ...convexQuery(api.settings.precedents.tagGroups.listTagGroups, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );

  const { data: attachmentCandidateGroups } = useQuery({
    ...convexQuery(
      api.jobs.precedents.attachments.resolveAttachmentCandidates,
      { jobId: jobId as Id<"jobs"> },
    ),
    enabled: !!jobId,
  });

  // All uploaded job documents + current shared-reference resolutions, used to
  // resolve `custom` (named) attachments inline. Resolving writes the job-level
  // map up-front so `generatePrecedents` pre-fills every generated precedent.
  const { data: attachableJobDocuments } = useQuery({
    ...convexQuery(api.jobs.precedents.attachments.listAttachableJobDocuments, {
      jobId: jobId as Id<"jobs">,
    }),
    enabled: !!jobId,
  });
  const { data: resolvedReferences } = useQuery({
    ...convexQuery(
      api.jobs.precedents.attachmentReferences.listResolvedReferences,
      { jobId: jobId as Id<"jobs"> },
    ),
    enabled: !!jobId,
  });

  // Job contacts drive the contact-role fan-out: a template tagged
  // `cr:liability` (Creditors) generates one row per matching contact.
  // We load the full list so the wizard can render the per-template
  // contact picker AND derive `recipient_*` field values for each.
  const { data: jobContacts } = useQuery({
    ...convexQuery(api.contacts.queries.listJobContacts, {
      jobId: jobId as Id<"jobs">,
    }),
    enabled: !!jobId,
  });

  // Source rows for every active job contact, keyed by jobContactId.
  // Feeds the per-(contact × source link) fan-out preview and the
  // resolver call that builds each row's `contact_job_<kind>_*` field
  // values. Batched into a single query so toggling the contact-picker
  // checkboxes doesn't refetch — the query depends only on `jobId`.
  const { data: jobContactSourceRows } = useQuery({
    ...convexQuery(api.contacts.queries.getJobContactSourceRowsForJob, {
      jobId: jobId as Id<"jobs">,
    }),
    enabled: !!jobId,
  });

  // Distinct contact roles present on the job — fed to `getJobMatchReasons`
  // so `cr:*` tagged templates only surface as matches when at least one
  // contact has the relevant role.
  const jobContactRoles = useMemo<ReadonlySet<ContactRole>>(() => {
    const set = new Set<ContactRole>();
    for (const c of jobContacts ?? []) {
      if (c.status !== "active") continue;
      for (const role of c.roles ?? []) set.add(role as ContactRole);
    }
    return set;
  }, [jobContacts]);

  // Pre-filter and group active job contacts by role for fast per-template
  // lookup. Each template's `cr:*` tags resolve to a flattened list of
  // matching contacts (deduplicated across multiple matching roles).
  const contactsByRole = useMemo<
    Map<ContactRole, NonNullable<typeof jobContacts>>
  >(() => {
    const map = new Map<ContactRole, NonNullable<typeof jobContacts>>();
    for (const c of jobContacts ?? []) {
      if (c.status !== "active") continue;
      const seenRoles = new Set<ContactRole>();
      for (const role of (c.roles ?? []) as ContactRole[]) {
        if (seenRoles.has(role)) continue;
        seenRoles.add(role);
        const list = map.get(role) ?? [];
        list.push(c);
        map.set(role, list);
      }
    }
    return map;
  }, [jobContacts]);

  // Org-defined custom job-contact roles — resolve `jcr:<id>` tags to their
  // live name/colour/icon for match reasons and fan-out chips.
  const { data: customJobRoles } = useQuery({
    ...convexQuery(api.contacts.customRoles.listJobContactRoles, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const customJobRolesById = useMemo<Map<string, CustomJobRole>>(() => {
    const map = new Map<string, CustomJobRole>();
    for (const r of customJobRoles ?? []) map.set(String(r._id), r);
    return map;
  }, [customJobRoles]);

  // Custom-role id → live name, for `getJobMatchReasons` labels.
  const customJobRoleNames = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const r of customJobRoles ?? []) map.set(String(r._id), r.name);
    return map;
  }, [customJobRoles]);

  // Distinct custom-role ids present on active job contacts — fed to
  // `getJobMatchReasons` so `jcr:*` tagged templates only surface as
  // matches when at least one contact carries the relevant custom role.
  const jobContactCustomRoleIds = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const c of jobContacts ?? []) {
      if (c.status !== "active") continue;
      for (const id of c.customRoleIds ?? []) set.add(String(id));
    }
    return set;
  }, [jobContacts]);

  // Active job contacts grouped by custom-role id for per-template fan-out
  // lookup — the custom-role counterpart to `contactsByRole`.
  const contactsByCustomRoleId = useMemo<
    Map<string, NonNullable<typeof jobContacts>>
  >(() => {
    const map = new Map<string, NonNullable<typeof jobContacts>>();
    for (const c of jobContacts ?? []) {
      if (c.status !== "active") continue;
      const seen = new Set<string>();
      for (const id of c.customRoleIds ?? []) {
        const key = String(id);
        if (seen.has(key)) continue;
        seen.add(key);
        const list = map.get(key) ?? [];
        list.push(c);
        map.set(key, list);
      }
    }
    return map;
  }, [jobContacts]);

  // Firm contacts drive the `fc:*` (firm-contact category) fan-out — the
  // org-wide directory counterpart to the per-job `cr:*` fan-out.
  const { data: firmContacts } = useQuery({
    ...convexQuery(api.contacts.queries.listFirmContacts, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  // Org-defined custom categories (MOR-914) — resolve `fcc:<id>` tags to
  // their live names for fan-out role labels.
  const { data: firmCustomCategories } = useQuery({
    ...convexQuery(api.contacts.customCategories.listFirmContactCategories, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const firmCustomCategoryById = useMemo(() => {
    const map = new Map<string, { name: string }>();
    for (const c of firmCustomCategories ?? []) {
      map.set(String(c._id), { name: c.name });
    }
    return map;
  }, [firmCustomCategories]);

  // Active firm contacts grouped by category for per-template lookup.
  const firmContactsByCategory = useMemo<
    Map<FirmContactCategory, NonNullable<typeof firmContacts>>
  >(() => {
    const map = new Map<
      FirmContactCategory,
      NonNullable<typeof firmContacts>
    >();
    for (const c of firmContacts ?? []) {
      if (c.status !== "active") continue;
      const list = map.get(c.category) ?? [];
      list.push(c);
      map.set(c.category, list);
    }
    return map;
  }, [firmContacts]);

  // Firm-contact custom field definitions, grouped by category — used to
  // format raw `customFields` values into `contact_firm_cf_*` fields at
  // fan-out time.
  const { data: firmContactFieldDefs } = useQuery({
    ...convexQuery(
      api.contacts.fieldDefinitions.listFirmContactFieldDefinitions,
      { orgId: orgId as Id<"organizations"> },
    ),
    enabled: !!orgId,
  });
  const firmFieldDefsByCategory = useMemo(() => {
    const map = new Map<
      FirmContactCategory,
      Array<{ key: string; fieldType: ContactCustomFieldDef["fieldType"] }>
    >();
    for (const def of firmContactFieldDefs ?? []) {
      const list = map.get(def.category) ?? [];
      list.push({ key: def.key, fieldType: def.fieldType });
      map.set(def.category, list);
    }
    return map;
  }, [firmContactFieldDefs]);

  const { data: membersWithSignatures } = useQuery({
    ...convexQuery(api.organizations.queries.getMembersWithSignatures, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });

  // MOR-2342 — the firm's default for the day-one primary contact person and
  // the members an analyst can pick instead.
  const { data: firmContactDefault } = useQuery({
    ...convexQuery(
      api.settings.precedents.contactDefaults.getDayOneContactDefault,
      { orgId: orgId as Id<"organizations"> },
    ),
    enabled: !!orgId,
  });
  const { data: contactPersonCandidates } = useQuery({
    ...convexQuery(
      api.settings.precedents.contactDefaults.listContactPersonCandidates,
      { orgId: orgId as Id<"organizations"> },
    ),
    enabled: !!orgId,
  });
  // Workflow roles (Partner / Manager / Analyst) and who holds each on this
  // job, for the "person allocated to a job role" contact option.
  const { data: workflowRoles } = useQuery({
    ...convexQuery(api.tasks.workflowRoles.listByOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const { data: jobRoleAssignments } = useQuery({
    ...convexQuery(api.jobs.roleAssignments.listByJob, {
      jobId: jobId as Id<"jobs">,
    }),
    enabled: !!jobId,
  });

  const favouriteSet = useMemo(
    () => new Set(favouriteIds ?? []),
    [favouriteIds],
  );

  const presetsByCategory = useMemo(() => {
    const map = new Map<string, FieldPresetRow[]>();
    if (!allPresets) return map;
    for (const p of allPresets) {
      const list = map.get(p.categoryId) ?? [];
      list.push(p);
      map.set(p.categoryId, list);
    }
    return map;
  }, [allPresets]);

  type ContactChip = {
    key: string;
    label: string;
    color: BadgeColor;
    icon: LucideIcon;
    domain: "job" | "firm";
  };

  type WizardCategory = {
    key: string;
    label: string;
    subtitle: string;
    types: Array<{
      key: string;
      name: string;
      shortName: string;
      description: string;
      category: string;
      icon: React.ComponentType<{ className?: string }>;
      thirdPartyForm?: boolean;
      hardcodedFields?: PrecedentField[];
      /** Extraction lifecycle so the "Not configured" hint can show
       *  the user *why* and link them to the right place to fix it. */
      extractionStatus?: "pending" | "extracting" | "completed" | "failed";
      extractionError?: string;
      hasSourceFile?: boolean;
      variantGroupSlugs?: string[];
      /** Contact chips for the Contacts column — includes both job
       *  contacts (`cr:` tags) and firm contacts (`fc:` tags), each
       *  carrying an icon so the two domains read as distinct groups. */
      contacts?: ContactChip[];
      /** Raw match tags (appointment type, `js:` status, `tg:`, `cr:`) —
       *  used by the job-type / status filter to bulk select/unselect. */
      tags?: string[];
    }>;
  };

  /** A principal document with the in-category attachments it pulls in. */
  type CategoryUnit = {
    principal: WizardCategory["types"][number];
    attachments: WizardCategory["types"][number][];
  };
  /** One table-view row: a template plus its nesting depth (0 or 1). */
  type CategoryRow = {
    type: WizardCategory["types"][number];
    depth: number;
  };

  const wizardCategories = useMemo((): WizardCategory[] => {
    if (!categories || !templates) return [];
    const templatesByCatId = new Map<string, PrecedentTemplateRow[]>();
    for (const t of templates) {
      const cid = t.categoryId ?? "__uncategorized";
      const list = templatesByCatId.get(cid) ?? [];
      list.push(t);
      templatesByCatId.set(cid, list);
    }

    const extractVariantSlugs = (html?: string | null): string[] => {
      if (!html) return [];
      const slugs = new Set<string>();
      const re = /data-group-slug="([^"]+)"/g;
      for (const m of html.matchAll(re)) slugs.add(m[1]);
      return [...slugs];
    };

    // Build the Contacts column chips from both job-contact (`cr:`) and
    // firm-contact (`fc:`) tags. Firm contacts share one colour so they
    // read as a distinct group from the per-role job-contact palette,
    // and each chip carries an icon to distinguish the two domains —
    // matching the Precedent Library's Contacts column.
    const extractContacts = (tags?: string[]): ContactChip[] => {
      if (!tags) return [];
      const contacts: ContactChip[] = [];
      const seen = new Set<string>();
      for (const tag of tags) {
        const role = parseContactRoleTag(tag);
        if (role) {
          const key = `cr:${role}`;
          if (seen.has(key)) continue;
          seen.add(key);
          contacts.push({
            key,
            label: ROLE_META[role].label,
            color: ROLE_META[role].badgeColor,
            icon: ROLE_META[role].icon,
            domain: "job",
          });
          continue;
        }
        const customRoleId = parseJobContactCustomRoleTag(tag);
        if (customRoleId) {
          const key = `jcr:${customRoleId}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const role = customJobRolesById.get(customRoleId);
          // Archived / unknown custom roles degrade to nothing rather than
          // rendering a dangling chip.
          if (!role) continue;
          contacts.push({
            key,
            label: role.name,
            color: role.color,
            icon: roleIconComponent(role.icon) ?? Tag,
            domain: "job",
          });
          continue;
        }
        const firmCategory = parseFirmContactTag(tag);
        if (firmCategory) {
          const key = `fc:${firmCategory}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const meta = FIRM_CATEGORY_META[firmCategory];
          contacts.push({
            key,
            // Singular form so a chip reads "Regulator", "Firm Member".
            label: meta.shortLabel,
            color: "orange",
            icon: meta.icon,
            domain: "firm",
          });
        }
      }
      return contacts;
    };

    const mapTemplate = (
      t: PrecedentTemplateRow,
    ): WizardCategory["types"][number] => ({
      key: t.templateKey,
      name: t.displayName ?? t.name ?? t.templateKey,
      shortName: t.displayName ?? t.name ?? t.templateKey,
      description: t.description ?? "",
      category: t.category,
      icon: t.isThirdPartyForm ? FileSpreadsheet : FileText,
      thirdPartyForm: t.isThirdPartyForm,
      hardcodedFields:
        t.templateKey === "form_gallagher"
          ? GALLAGHER_INSURANCE_FIELDS
          : undefined,
      extractionStatus: t.extractionStatus,
      extractionError: t.extractionError,
      hasSourceFile: Boolean(t.sourceDocumentStorageId),
      variantGroupSlugs: extractVariantSlugs(t.templateHtml),
      contacts: extractContacts(t.tags as string[] | undefined),
      tags: (t.tags ?? []) as string[],
    });

    const groups: WizardCategory[] = [];
    for (const cat of categories) {
      const tpls = templatesByCatId.get(cat._id) ?? [];
      if (tpls.length === 0) continue;
      groups.push({
        key: cat._id,
        label: cat.name,
        subtitle: cat.isSystem ? "System category" : "",
        types: tpls.map(mapTemplate),
      });
    }

    const uncategorized = templatesByCatId.get("__uncategorized");
    if (uncategorized?.length) {
      groups.push({
        key: "__uncategorized",
        label: "Uncategorised",
        subtitle: "Templates without a category",
        types: uncategorized.map(mapTemplate),
      });
    }

    return groups;
  }, [categories, templates, customJobRolesById]);

  const allTemplateTypes = useMemo(
    () => wizardCategories.flatMap((g) => g.types),
    [wizardCategories],
  );
  const totalTemplateCount = allTemplateTypes.length;

  const convex = useConvex();
  const { mutateAsync: generatePrecedents, isPending: generating } =
    useMutation({
      mutationFn: useConvexMutation(
        api.jobs.precedents.mutations.generatePrecedents,
      ),
    });

  const { mutateAsync: deleteAllForJob } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.deleteAllForJob,
    ),
  });

  const { mutateAsync: updateJob, isPending: advancingPhase } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateJob),
  });
  // Once dismissed (or acted on), the advance-phase prompt stays hidden for
  // the rest of the wizard session.
  const [advancePromptDismissed, setAdvancePromptDismissed] = useState(false);

  const generateDocumentUploadUrl = useConvexMutation(
    api.jobs.gatherInformation.documents.mutations.generateUploadUrl,
  );
  const { mutateAsync: saveDocument } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.gatherInformation.documents.mutations.saveDocument,
    ),
  });
  const pendingAttachmentUploadRef = useRef<{
    templateKey: string;
    slotKey: string;
    requirement: AttachmentRequirement;
  } | null>(null);
  const [uploadingAttachmentKey, setUploadingAttachmentKey] = useState<
    string | null
  >(null);

  // Existing generated documents for this job. Used to pre-populate template
  // selection when re-selecting fields (guarded by `reselect` below) AND to
  // detect when a phase's documents have already been generated so the wizard
  // can prompt the user to advance the job's phase.
  const { data: existingPrecedents } = useQuery(
    convexQuery(api.jobs.precedents.queries.listByJob, {
      jobId: jobId as Id<"jobs">,
    }),
  );

  const [step, setStep] = useState<Step>(reselect ? "review" : "select");
  // Templates the user has explicitly picked. The *effective* selection
  // (`selectedKeys`, derived below) layers on any generated-precedent
  // attachment dependencies so those documents are also generated.
  const [userSelectedKeys, setUserSelectedKeys] = useState<Set<string>>(
    new Set(),
  );
  // Dependency templates the user explicitly turned OFF even though a selected
  // document attaches them. Kept separate from `manuallyUnselectedKeys` so a
  // bulk "Unselect all" never permanently suppresses a dependency — only a
  // deliberate per-document unselect does (MOR-678).
  const [dismissedDependencyKeys, setDismissedDependencyKeys] = useState<
    Set<string>
  >(new Set());
  // ---------------------------------------------------------------------------
  // Select Templates step — view toggle, search, category filter
  // ---------------------------------------------------------------------------
  const [selectViewMode, setSelectViewMode] = useState<"grid" | "table">(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("day-one-select-view-mode");
      if (stored === "grid" || stored === "table") return stored;
    }
    return "grid";
  });
  const toggleSelectViewMode = useCallback((mode: "grid" | "table") => {
    setSelectViewMode(mode);
    if (typeof window !== "undefined") {
      localStorage.setItem("day-one-select-view-mode", mode);
    }
  }, []);
  // Resizable Name column in the table view. Width is clamped + persisted.
  const [nameColWidth, setNameColWidth] = useState<number>(() => {
    if (typeof window === "undefined") return NAME_COL_DEFAULT;
    const raw = window.localStorage.getItem(NAME_COL_STORAGE_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    if (Number.isFinite(parsed)) {
      return Math.min(NAME_COL_MAX, Math.max(NAME_COL_MIN, parsed));
    }
    return NAME_COL_DEFAULT;
  });
  const handleNameColResize = useCallback((width: number) => {
    const clamped = Math.min(NAME_COL_MAX, Math.max(NAME_COL_MIN, width));
    setNameColWidth(clamped);
    try {
      window.localStorage.setItem(NAME_COL_STORAGE_KEY, String(clamped));
    } catch {
      /* ignore persistence failures (e.g. private mode) */
    }
  }, []);
  // Aborts the in-flight drag's `window` listeners. `mouseup` ends a normal
  // drag, but an unmount mid-drag (navigating away while the mouse is down)
  // would otherwise leave them attached and let a later `mousemove` write
  // state and localStorage after teardown.
  const nameColDragAbortRef = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      nameColDragAbortRef.current?.abort();
      nameColDragAbortRef.current = null;
    },
    [],
  );
  // Track the drag from the header's right edge and report the new width.
  const handleNameColResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = nameColWidth;
      nameColDragAbortRef.current?.abort();
      const controller = new AbortController();
      nameColDragAbortRef.current = controller;
      window.addEventListener(
        "mousemove",
        (moveEvent: MouseEvent) => {
          handleNameColResize(startWidth + (moveEvent.clientX - startX));
        },
        { signal: controller.signal },
      );
      window.addEventListener(
        "mouseup",
        () => {
          controller.abort();
          if (nameColDragAbortRef.current === controller) {
            nameColDragAbortRef.current = null;
          }
        },
        { signal: controller.signal },
      );
    },
    [nameColWidth, handleNameColResize],
  );
  const [selectSearchText, setSelectSearchText] = useState("");
  const [selectCategoryTab, setSelectCategoryTab] = useState<string>("all");
  // Phase (job status) the wizard suggests templates for. Defaults to the
  // job's current phase (so the first run surfaces Day One) but can be
  // switched so a later-phase job can generate that phase's documents.
  const [selectedPhase, setSelectedPhase] = useState<JobStatus | null>(null);
  // Firms can have hundreds of templates, so the Select Templates step
  // defaults to a condensed view listing only the selected (suggested +
  // user-picked) templates. Flipping to "All" brings back the full list so
  // extra templates can be ticked.
  const [showAllTemplates, setShowAllTemplates] = useState(false);

  // Search + category filter applied to the Select Templates step. Returns
  // wizard categories with their `types` filtered to those matching the
  // current search/category tab, and skips groups with no matches.
  const filteredWizardCategories = useMemo((): WizardCategory[] => {
    const needle = selectSearchText.trim().toLowerCase();
    const out: WizardCategory[] = [];
    for (const group of wizardCategories) {
      if (selectCategoryTab !== "all" && group.key !== selectCategoryTab) {
        continue;
      }
      const types = needle
        ? group.types.filter((type) => {
            const hay = [type.name, type.shortName, type.description]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return hay.includes(needle);
          })
        : group.types;
      if (types.length === 0) continue;
      out.push({ ...group, types });
    }
    return out;
  }, [wizardCategories, selectSearchText, selectCategoryTab]);

  // Tracks which contacts are ticked for each contact-tagged template.
  // Lazily seeded "all selected" the first time we render the picker for
  // a template. Keyed by the contact's Convex `_id` (job or firm — ids
  // are globally unique so a bare string key is collision-free).
  const [contactSelectionsByTemplate, setContactSelectionsByTemplate] =
    useState<Map<string, Set<string>>>(() => new Map());
  const [expandedContactPickers, setExpandedContactPickers] = useState<
    Set<string>
  >(() => new Set());
  // Principal template keys whose nested attachment list is expanded in the
  // grid view. Collapsed by default so every card stays the same height.
  const [expandedAttachmentGroups, setExpandedAttachmentGroups] = useState<
    Set<string>
  >(() => new Set());
  // Firm-contact REFERENCE picks. templateKey → category → chosen
  // firmContacts id. A missing entry falls back to the sole contact in the
  // category (auto-single) at build time, so the map only stores explicit
  // user choices.
  const [firmRefSelectionsByTemplate, setFirmRefSelectionsByTemplate] =
    useState<Map<string, Map<FirmContactCategory, Id<"firmContacts">>>>(
      () => new Map(),
    );
  const setFirmRefSelection = useCallback(
    (
      templateKey: string,
      category: FirmContactCategory,
      contactId: Id<"firmContacts">,
    ) => {
      setFirmRefSelectionsByTemplate((prev) => {
        const next = new Map(prev);
        const inner = new Map(next.get(templateKey) ?? []);
        inner.set(category, contactId);
        next.set(templateKey, inner);
        return next;
      });
    },
    [],
  );
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, string>>(
    {},
  );
  const [presetSelections, setPresetSelections] = useState<
    Record<string, Id<"fieldPresets">>
  >({});
  const [attachmentSelections, setAttachmentSelections] = useState<
    Record<string, Record<string, AttachmentSelection>>
  >({});
  const [reviewTab, setReviewTab] = useState<string>("all");
  // Step 2 review controls. `reviewSearch` filters field rows by label,
  // `showOnlyNeedsInput` hides already-populated fields, and `collapsedCards`
  // tracks which section cards are collapsed (keyed by template key, plus
  // the sentinel `"__common__"` for the Common Fields card).
  const [reviewSearch, setReviewSearch] = useState<string>("");
  // Whether the user has dismissed the "no overview generated" flag for this
  // review session. Non-blocking — hides the banner without changing anything.
  const [backgroundFlagDismissed, setBackgroundFlagDismissed] =
    useState<boolean>(false);
  const [showOnlyNeedsInput, setShowOnlyNeedsInput] = useState<boolean>(false);
  const [collapsedCards, setCollapsedCards] = useState<Set<string>>(
    () => new Set(),
  );
  // Tracks whether the auto-collapse heuristic has run for the current set of
  // selected templates, so we don't fight the user's manual expand/collapse.
  const autoCollapseSignatureRef = useRef<string>("");
  // Refs to each review section card so the section tabs can jump-scroll to
  // them while in the "all" view. Keyed by template key / COMMON_CARD_KEY.
  const reviewCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollToReviewCard = useCallback((key: string) => {
    const el = reviewCardRefs.current[key];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  const [selectedSignatoryId, setSelectedSignatoryId] = useState<string | null>(
    null,
  );
  /**
   * Per-generation pick of the primary contact person (MOR-2342). `null`
   * follows the firm default; the analyst may switch to themselves, the
   * appointee or a colleague for this run only.
   */
  const [contactPersonChoice, setContactPersonChoice] =
    useState<DayOneContactDefault | null>(null);
  /**
   * Run-time pick for repeat-signatory blocks (the `signatories` template
   * collection): which members' sign-off blocks appear on the generated
   * documents. `null` = everyone with a saved signature.
   */
  const [pickedSignatoryIds, setPickedSignatoryIds] =
    useState<Set<string> | null>(null);

  // Per-template letterhead override picked in the wizard. `undefined`
  // means "follow the resolution chain at render time" (template default
  // → firm default → single-bundle → none). Stamped onto the precedent
  // row when the user confirms generation.
  type WizardLetterheadOverride =
    | { kind: "none" }
    | { kind: "letterhead"; bundleName: string }
    | { kind: "variant"; variantId: Id<"letterheadVariants"> }
    | null;
  const [letterheadOverrides, setLetterheadOverrides] = useState<
    Record<string, WizardLetterheadOverride>
  >({});

  // Per-template variant-option overrides chosen in the wizard. Indexed by
  // templateKey → { groupSlug → optionValue }. Values here are stamped onto
  // jobPrecedents.variantSelections at generation time and merge over the
  // job-wide defaults at render time.
  const [variantOverrides, setVariantOverrides] = useState<
    Record<string, Record<string, string>>
  >({});

  const setVariantOverride = useCallback(
    (templateKey: string, groupSlug: string, optionValue: string) => {
      setVariantOverrides((prev) => ({
        ...prev,
        [templateKey]: {
          ...(prev[templateKey] ?? {}),
          [groupSlug]: optionValue,
        },
      }));
    },
    [],
  );

  const clearVariantOverride = useCallback(
    (templateKey: string, groupSlug: string) => {
      setVariantOverrides((prev) => {
        const tpl = { ...(prev[templateKey] ?? {}) };
        delete tpl[groupSlug];
        return { ...prev, [templateKey]: tpl };
      });
    },
    [],
  );

  // Letterhead picker data — branding profile (firm default + favourites),
  // bundles (asset library, grouped), and variants. Same three queries
  // the day-one picker uses.
  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const { data: letterheadAssetsRaw } = useQuery({
    ...convexQuery(api.settings.branding.assetQueries.listAssetsForOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const { data: letterheadVariantsRaw } = useQuery({
    ...convexQuery(api.settings.branding.variantQueries.listVariantsForOrg, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const { data: variantGroupsRaw } = useQuery({
    ...convexQuery(api.settings.precedents.variantGroups.listVariantGroups, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const variantGroupsForPicker = useMemo(
    () =>
      (variantGroupsRaw ?? []).map((g) => ({
        slug: g.slug,
        name: g.name,
        options: g.options,
        fieldBinding: g.fieldBinding satisfies VariantFieldBinding | undefined,
      })),
    [variantGroupsRaw],
  );

  // Groups whose active option is data-bound (MOR-886) — resolved per
  // generated document from that document's field values at payload build.
  const boundVariantGroups = useMemo(
    () => variantGroupsForPicker.filter((g) => g.fieldBinding),
    [variantGroupsForPicker],
  );

  /** Bound groups by slug → the field key their option is resolved from. */
  const boundGroupFieldKeyBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of boundVariantGroups) {
      if (g.fieldBinding?.fieldKey) map.set(g.slug, g.fieldBinding.fieldKey);
    }
    return map;
  }, [boundVariantGroups]);

  // Title-cases slugs/names so user-entered values like "practioners" or
  // "number of appointees" render as "Practioners" / "Number Of Appointees".
  const toTitleCase = useCallback(
    (s: string) =>
      s
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(
          /\w\S*/g,
          (w) => w[0].toUpperCase() + w.slice(1).toLowerCase(),
        ),
    [],
  );

  // slug → display name lookup for the Variants column in the Select Templates
  // table view.
  const variantGroupNameBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of variantGroupsForPicker) {
      map.set(g.slug, toTitleCase(g.name));
    }
    return map;
  }, [variantGroupsForPicker, toTitleCase]);

  const { data: variantSnippetsRaw } = useQuery({
    ...convexQuery(api.settings.precedents.variantSnippets.listSnippets, {
      orgId: orgId as Id<"organizations">,
    }),
    enabled: !!orgId,
  });
  const variantSnippetsForPicker = useMemo(
    () =>
      (variantSnippetsRaw ?? []).map((s) => ({
        _id: String(s._id),
        name: s.name,
        groupSlug: s.groupSlug,
        optionValue: s.optionValue,
        contentHtml: s.contentHtml,
        slotLabel: s.slotLabel,
      })),
    [variantSnippetsRaw],
  );

  // Per-template snippet overrides chosen in the wizard.
  const [snippetOverrides, setSnippetOverrides] = useState<
    Record<string, Record<string, string>>
  >({});

  const setSnippetOverride = useCallback(
    (templateKey: string, key: string, snippetId: string) => {
      setSnippetOverrides((prev) => ({
        ...prev,
        [templateKey]: {
          ...(prev[templateKey] ?? {}),
          [key]: snippetId,
        },
      }));
    },
    [],
  );

  const clearSnippetOverride = useCallback(
    (templateKey: string, key: string) => {
      setSnippetOverrides((prev) => {
        const next = { ...(prev[templateKey] ?? {}) };
        delete next[key];
        return { ...prev, [templateKey]: next };
      });
    },
    [],
  );

  /**
   * Collects field placeholder keys from variant snippet contentHtml that
   * is selected (or defaulted) for a given template.
   */
  const collectSnippetFieldKeys = useCallback(
    (templateKey: string, templateHtml: string): Set<string> => {
      const keys = new Set<string>();
      if (!templateHtml.includes('data-type="variant-block"')) return keys;

      const jobVS = job?.variantSelections as
        Record<string, string> | undefined;
      const tplVS = variantOverrides[templateKey] ?? {};
      const merged = { ...(jobVS ?? {}), ...tplVS };
      const tplSnippetSel = snippetOverrides[templateKey] ?? {};
      const byId = new Map(variantSnippetsForPicker.map((s) => [s._id, s]));

      // Resolve the effective snippet for each block (per-block override by
      // vbId, else the slot's snippet for the selected option) and collect its
      // field placeholders so the right inputs surface.
      for (const block of extractVariantBlockInstances(templateHtml)) {
        const optionValue = merged[block.groupSlug];
        const overrideId = block.vbId ? tplSnippetSel[block.vbId] : undefined;
        const effId =
          overrideId ??
          (optionValue
            ? (block.snippetRefs[optionValue] ?? undefined)
            : undefined);
        const snippet = effId ? byId.get(effId) : undefined;
        if (!snippet) continue;
        for (const k of collectPlaceholderKeysFromStoredTemplate(
          snippet.contentHtml,
          "",
        )) {
          keys.add(k);
        }
      }
      return keys;
    },
    [
      job?.variantSelections,
      variantOverrides,
      snippetOverrides,
      variantSnippetsForPicker,
    ],
  );

  const wizardBundleNames = useMemo<string[]>(() => {
    if (!letterheadAssetsRaw) return [];
    const seen = new Set<string>();
    for (const a of letterheadAssetsRaw) {
      const name = a.bundleName ?? a.sourceDocumentName ?? null;
      if (name) seen.add(name);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [letterheadAssetsRaw]);

  const { startUpload: startAttachmentUpload } = useUploadFiles(
    async () => await generateDocumentUploadUrl({ jobId: jobId as Id<"jobs"> }),
    {
      onUploadBegin: () => {
        const pending = pendingAttachmentUploadRef.current;
        if (pending) {
          setUploadingAttachmentKey(
            `${pending.templateKey}:${pending.slotKey}`,
          );
        }
      },
      onUploadComplete: async (uploaded) => {
        const pending = pendingAttachmentUploadRef.current;
        if (!pending) return;
        const file = uploaded[0];
        if (!file) return;

        try {
          const storageId = (file.response as { storageId: Id<"_storage"> })
            .storageId;
          const category = documentCategoryForAttachment(pending.requirement);
          const documentId = await saveDocument({
            jobId: jobId as Id<"jobs">,
            storageId,
            fileName: file.name,
            fileType: file.type || "application/octet-stream",
            fileSize: file.size,
            category,
          });

          setAttachmentSelections((prev) => ({
            ...prev,
            [pending.templateKey]: {
              ...(prev[pending.templateKey] ?? {}),
              [pending.slotKey]: {
                name: pending.requirement.name,
                description: pending.requirement.description,
                required: pending.requirement.required,
                attachmentKey: pending.requirement.attachmentKey,
                sourceKind: "upload",
                status: "resolved",
                documentCategory: category,
                documentId,
                storageId,
                fileName: file.name,
                fileType: file.type || "application/octet-stream",
                fileSize: file.size,
                resolvedAt: Date.now(),
              },
            },
          }));
          toast.success(`${file.name} attached`);
        } catch (err) {
          toast.error(extractErrorMessage(err, "Failed to attach file"));
        } finally {
          pendingAttachmentUploadRef.current = null;
          setUploadingAttachmentKey(null);
        }
      },
      onUploadError: (error: unknown) => {
        const message =
          error instanceof Error ? error.message : "Unknown upload error";
        pendingAttachmentUploadRef.current = null;
        setUploadingAttachmentKey(null);
        toast.error(`Attachment upload failed: ${message}`);
      },
    },
  );

  // Pre-populate template selections and field values from existing precedents
  const [reselectInitialized, setReselectInitialized] = useState(false);
  useMemo(() => {
    if (
      reselect &&
      existingPrecedents &&
      existingPrecedents.length > 0 &&
      !reselectInitialized
    ) {
      const keys = new Set(existingPrecedents.map((p) => p.templateKey));
      setUserSelectedKeys(keys);

      // Restore the analyst's manual edits — including intentional blanks
      // (MOR-2342 QA-08) — and the primary contact person the documents
      // were generated with (MOR-2358). Resolved defaults are NOT edits: a
      // reopened wizard must let a contact-source switch refresh the name,
      // email and direct line exactly as a fresh generation would.
      const restored = rehydrateWizardState(existingPrecedents);
      setFieldOverrides(restored.fieldOverrides);
      if (restored.contactPerson) {
        setContactPersonChoice(restored.contactPerson);
      }

      // Seed attachmentSelections so the wizard's regenerate path doesn't
      // silently drop attachments the user already configured (MOR-389).
      // The rebuild useEffect downstream preserves entries that come in as
      // `resolved` or `skipped`, so populating once here is enough.
      const initialAttachments: Record<
        string,
        Record<string, AttachmentSelection>
      > = {};
      for (const p of existingPrecedents) {
        const stored = p.attachments as JobPrecedentAttachment[] | undefined;
        if (!stored?.length) continue;
        const slotCounts: Record<string, number> = {};
        initialAttachments[p.templateKey] = {};
        for (const att of stored) {
          const idx = slotCounts[att.attachmentKey] ?? 0;
          slotCounts[att.attachmentKey] = idx + 1;
          const slotKey = buildAttachmentSlotKey(
            att.attachmentKey as PrecedentAttachmentKey,
            idx,
          );
          // A generated-precedent slot that was clobbered to sourceKind
          // "none" by the shared-reference propagation bug (MOR-779) still
          // carries its generatedTemplateKey. Skip seeding it so the rebuild
          // effect re-resolves the link instead of freezing it as skipped.
          // (A genuine user skip clears generatedTemplateKey, so it is
          // preserved by this check and seeds normally.)
          if (att.generatedTemplateKey && att.sourceKind === "none") {
            continue;
          }
          initialAttachments[p.templateKey][slotKey] = {
            name: att.name,
            description: att.description,
            required: att.required,
            attachmentKey: att.attachmentKey as PrecedentAttachmentKey,
            sourceKind: att.sourceKind as PrecedentAttachmentSourceKind,
            status: att.status,
            documentCategory: att.documentCategory as
              DocumentCategory | undefined,
            documentId: att.documentId,
            generatedPrecedentId: att.generatedPrecedentId,
            generatedTemplateKey: att.generatedTemplateKey,
            storageId: att.storageId,
            fileName: att.fileName,
            fileType: att.fileType,
            fileSize: att.fileSize,
            resolvedAt: att.resolvedAt,
          };
        }
      }
      setAttachmentSelections(initialAttachments);

      setReselectInitialized(true);
    }
  }, [reselect, existingPrecedents, reselectInitialized]);

  // Auto-select default presets once loaded
  const [defaultsInitialized, setDefaultsInitialized] = useState(false);
  useMemo(() => {
    if (allPresets && !defaultsInitialized) {
      const defaults: Record<string, Id<"fieldPresets">> = {};
      for (const p of allPresets) {
        if (p.isDefault && !defaults[p.categoryId]) {
          defaults[p.categoryId] = p._id;
        }
      }
      setPresetSelections(defaults);
      setDefaultsInitialized(true);
    }
  }, [allPresets, defaultsInitialized]);

  // Build orgDefaults from selected presets (client-side, instant reactivity).
  // Only keys that belong to the preset's category (or its explicit
  // cross-category allowlist) are included — prevents ghost data in presets
  // from bleeding into generated documents.
  // Includes both canonical and custom field category presets.
  const customCategoryKeys = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!customFieldDefs) return map;
    for (const def of customFieldDefs) {
      const set = map.get(def.categoryId) ?? new Set<string>();
      map.set(def.categoryId, set);
      set.add(def.key);
    }
    return map;
  }, [customFieldDefs]);

  // Custom field categories grouped for rendering in Common Fields.
  // Each category becomes a CommonFieldGroup with its own preset selector,
  // so org-defined custom fields ("Industry Title", "Address", etc.) flow
  // through the wizard the same way as canonical default categories.
  type CustomCategoryGroup = {
    id: string;
    label: string;
    fields: CanonicalField[];
  };

  const customCategoriesGrouped = useMemo((): Map<
    string,
    CustomCategoryGroup
  > => {
    const map = new Map<string, CustomCategoryGroup>();
    if (!customFieldDefs) return map;
    for (const def of customFieldDefs) {
      const cat = map.get(def.categoryId) ?? {
        id: def.categoryId,
        label: def.categoryLabel,
        fields: [],
      };
      map.set(def.categoryId, cat);
      cat.fields.push({
        key: def.key,
        label: def.label,
        fieldType: def.fieldType as CanonicalField["fieldType"],
        placeholder: def.placeholder,
      });
    }
    return map;
  }, [customFieldDefs]);

  // Lookup field metadata for any key — canonical or custom.
  // Returns undefined for unknown keys (e.g. stale placeholders).
  const customFieldsByKey = useMemo(() => {
    const map = new Map<string, CanonicalField>();
    for (const cat of customCategoriesGrouped.values()) {
      for (const f of cat.fields) map.set(f.key, f);
    }
    return map;
  }, [customCategoriesGrouped]);

  const lookupField = useCallback(
    (key: string): CanonicalField | undefined => {
      return CANONICAL_FIELDS_MAP[key] ?? customFieldsByKey.get(key);
    },
    [customFieldsByKey],
  );

  // Reverse map: custom field key → its category id, used to surface the
  // matching preset selector inside per-template cards when a custom field
  // is only used by that one template.
  const customFieldCategoryByKey = useMemo(() => {
    const map = new Map<string, string>();
    if (!customFieldDefs) return map;
    for (const def of customFieldDefs) {
      map.set(def.key, def.categoryId);
    }
    return map;
  }, [customFieldDefs]);

  const presetDefaults = useMemo(() => {
    if (!allPresets) return {};
    const map: Record<string, string> = {};

    const allCategoryIds = new Set([
      ...DEFAULT_CATEGORIES,
      ...customCategoryKeys.keys(),
    ]);

    for (const categoryId of allCategoryIds) {
      const selectedId = presetSelections[categoryId];
      const preset = selectedId
        ? allPresets.find((p) => p._id === selectedId)
        : allPresets.find((p) => p.categoryId === categoryId && p.isDefault);
      if (!preset) continue;
      const validKeys = getValidKeysForCategory(
        categoryId,
        customCategoryKeys.get(categoryId),
      );
      for (const [key, value] of Object.entries(
        preset.values as Record<string, string>,
      )) {
        if (!value) continue;
        if (validKeys.has(key) && !(key in map)) {
          map[key] = value;
        }
      }
    }

    return map;
  }, [allPresets, presetSelections, customCategoryKeys]);

  const configuredKeys = useMemo(() => {
    const set = new Set<string>();
    if (templates) {
      for (const t of templates) {
        if (t.extractionStatus === "completed") set.add(t.templateKey);
      }
    }
    for (const t of allTemplateTypes) {
      if (t.hardcodedFields) set.add(t.key);
    }
    return set;
  }, [templates, allTemplateTypes]);

  // Templates whose tags match this job (by appointment type, status, or
  // custom tags), keyed by template key with the *list of reasons* the
  // template was suggested. Used both for auto-selection on first load and
  // for rendering per-template "Match" chips that explain *why* each
  // template was suggested.
  const tagMatchReasons = useMemo(() => {
    const map = new Map<string, MatchReason[]>();
    if (!templates || !job) return map;
    for (const t of templates) {
      if (!configuredKeys.has(t.templateKey)) continue;
      const reasons = getJobMatchReasons(
        t.tags as string[] | undefined,
        job.appointmentType,
        job.customTagSelections as Record<string, string[]> | undefined,
        selectedPhase ?? job.status,
        jobContactRoles,
        jobContactCustomRoleIds,
        customJobRoleNames,
      );
      // Only auto-suggest templates that match on BOTH the job-type and
      // status dimensions — a template tagged with only a job type (e.g.
      // `cvl`, no `js:` status tag) is no longer surfaced as suggested.
      if (reasons !== null && reasonsCoverTypeAndStatus(reasons)) {
        map.set(t.templateKey, reasons);
      }
    }
    return map;
  }, [
    templates,
    job,
    selectedPhase,
    configuredKeys,
    jobContactRoles,
    jobContactCustomRoleIds,
    customJobRoleNames,
  ]);

  // Phases (job statuses) the wizard offers in its Phase selector: every
  // phase any configured template is `js:`-tagged for, plus the job's current
  // phase. Ordered by `JOB_STATUS_ORDER` for a stable display order.
  const availablePhases = useMemo<JobStatus[]>(() => {
    const set = new Set<JobStatus>();
    if (job) set.add(job.status);
    if (templates) {
      for (const t of templates) {
        for (const tag of (t.tags ?? []) as string[]) {
          if (!tag.startsWith(JOB_STATUS_TAG_PREFIX)) continue;
          const status = tag.slice(JOB_STATUS_TAG_PREFIX.length);
          if ((JOB_STATUS_ORDER as ReadonlyArray<string>).includes(status)) {
            set.add(status as JobStatus);
          }
        }
      }
    }
    return JOB_STATUS_ORDER.filter((s) => set.has(s));
  }, [templates, job]);

  // Surface an "advance phase" prompt when the job is in a phase that has a
  // sensible next step AND documents for the current phase already exist — so
  // the practitioner isn't re-generating Day One documents indefinitely.
  const advancePhaseTarget = useMemo<JobStatus | null>(() => {
    if (!job) return null;
    const next = NEXT_PHASE[job.status];
    if (!next) return null;
    const hasPhaseDocs = (existingPrecedents ?? []).some((p) =>
      (p.phaseTags ?? []).includes(job.status),
    );
    return hasPhaseDocs ? next : null;
  }, [job, existingPrecedents]);

  const handleAdvancePhase = useCallback(async () => {
    if (!job || !advancePhaseTarget) return;
    try {
      await updateJob({
        jobId: jobId as Id<"jobs">,
        status: advancePhaseTarget,
      });
      setAdvancePromptDismissed(true);
      toast.success(
        `Phase updated to ${JOB_STATUS_LABELS[advancePhaseTarget] ?? advancePhaseTarget}`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update phase"));
    }
  }, [job, advancePhaseTarget, updateJob, jobId]);

  // Templates narrowed to companies in external administration
  // (`jcf:external_admin`) — drives EXAD-aware labelling in the fan-out
  // preview so it matches the editor chips.
  const externalAdminTemplateKeys = useMemo<Set<string>>(() => {
    const keys = new Set<string>();
    for (const t of templates ?? []) {
      if (((t.tags ?? []) as string[]).some(isJobContactExternalAdminTag)) {
        keys.add(t.templateKey);
      }
    }
    return keys;
  }, [templates]);

  // For each template that carries `cr:*` (job) and/or `fc:*` (firm) tags,
  // resolve the list of matching active contacts as normalised
  // `FanOutContact`s — deduplicated by Convex id across roles/categories.
  // Templates without contact tags produce no entry (single, non-fanned
  // row). Job and firm contacts share one list so the picker and the
  // generation loop stay single-path.
  // Job's own address state, for templates tagged `fcs:job` ("match the
  // job's state"). Resolved once; `null` when the job has no state yet, in
  // which case the tag imposes no restriction (see `jobStateTemplateKeys`).
  const jobState = useMemo(() => jobAddressState(job), [job]);

  const matchingContactsByTemplate = useMemo<
    Map<string, FanOutContact[]>
  >(() => {
    const map = new Map<string, FanOutContact[]>();
    if (!templates) return map;
    for (const t of templates) {
      const tags = (t.tags ?? []) as string[];
      const matchJobState = tags.some((tag) => isFirmContactJobStateTag(tag));
      const jobRoles = tags
        .map((tag) => parseContactRoleTag(tag))
        .filter((k): k is ContactRole => k !== null);
      const jobCustomRoleIds = tags
        .map((tag) => parseJobContactCustomRoleTag(tag))
        .filter((id): id is string => id !== null);
      const firmCategories = tags
        .map((tag) => parseFirmContactTag(tag))
        .filter((c): c is FirmContactCategory => c !== null);
      const customCategoryIds = tags
        .map((tag) => parseFirmContactCustomCategoryTag(tag))
        .filter((id): id is string => id !== null);
      const firmStates = tags
        .map((tag) => parseFirmContactStateTag(tag))
        .filter((s): s is string => s !== null);
      const externalAdminOnly = tags.some((tag) =>
        isJobContactExternalAdminTag(tag),
      );
      const tagCounts = {
        jobRoles: jobRoles.length,
        jobCustomRoles: jobCustomRoleIds.length,
        firmCategories: firmCategories.length,
        firmCustomCategories: customCategoryIds.length,
        // The dynamic `fcs:job` tag contributes the job's own address state
        // (see `stateSet` below), so it counts as a state tag whenever the
        // job has one recorded; with no state it contributes nothing.
        firmStates: firmStates.length + (matchJobState && jobState ? 1 : 0),
      };
      // `jcf:external_admin` selects on its own when it is the only tag.
      if (!hasAnyFanOutTag(tagCounts) && !externalAdminOnly) continue;

      const seen = new Set<string>();
      const matched: FanOutContact[] = [];

      // External-administration narrowing (`jcf:external_admin`). Like the
      // firm-contact state tags, this restricts the roles ticked alongside
      // it; with no roles it selects on its own.
      const passesExternalAdminFilter = (c: {
        contactType: string;
        externalAdminStatus?: string;
      }) =>
        !externalAdminOnly ||
        (c.contactType !== "person" && Boolean(c.externalAdminStatus?.trim()));

      const jobContactData = (
        c: NonNullable<typeof jobContacts>[number],
      ): ContactDataForFields => ({
        displayName: c.displayName,
        contactType: c.contactType,
        firstName: c.firstName,
        middleName: c.middleName,
        lastName: c.lastName,
        dateOfBirth: c.dateOfBirth,
        abn: c.abn,
        acn: c.acn,
        email: c.effectiveEmail ?? c.email,
        phone: c.effectivePhone ?? c.phone,
        addresses: c.addresses,
        externalAdminStatus: c.externalAdminStatus,
        externalAdminAppointeeTitle: c.externalAdminAppointeeTitle,
        externalAdminAppointee: c.externalAdminAppointee,
        primaryContactPerson: c.primaryContactPerson,
      });

      for (const role of jobRoles) {
        for (const c of contactsByRole.get(role) ?? []) {
          if (seen.has(c._id)) continue;
          if (!passesExternalAdminFilter(c)) continue;
          seen.add(c._id);
          const matchedRole =
            ((c.roles ?? []) as ContactRole[]).find((r) =>
              jobRoles.includes(r),
            ) ?? role;
          matched.push({
            _id: c._id,
            domain: "job",
            role: matchedRole,
            displayName: c.displayName,
            roleLabels: ((c.roles ?? []) as ContactRole[]).map(
              (r) => ROLE_META[r]?.label ?? r,
            ),
            data: jobContactData(c),
          });
        }
      }

      // Custom-role fan-out (`jcr:*`). A job contact matched purely via a
      // custom role has no built-in `role`, so `role` is left empty (custom
      // roles carry no source-kind mapping) and `customRole` drives the
      // picker label/badge. Contacts already matched by a built-in `cr:*`
      // role above are skipped via `seen` — the built-in match wins.
      for (const roleId of jobCustomRoleIds) {
        const role = customJobRolesById.get(roleId);
        for (const c of contactsByCustomRoleId.get(roleId) ?? []) {
          if (seen.has(c._id)) continue;
          if (!passesExternalAdminFilter(c)) continue;
          seen.add(c._id);
          matched.push({
            _id: c._id,
            domain: "job",
            role: "",
            displayName: c.displayName,
            roleLabels: role ? [role.name] : [],
            data: jobContactData(c),
            customRole: role
              ? {
                  label: role.name,
                  color: role.color,
                  Icon: roleIconComponent(role.icon) ?? Tag,
                }
              : undefined,
          });
        }
      }

      // External-administration-only tagging: no role selected, so the
      // narrowing tag is the selector — every active company on the job that
      // is in external administration gets a document.
      if (
        externalAdminOnly &&
        jobRoles.length === 0 &&
        jobCustomRoleIds.length === 0
      ) {
        for (const c of jobContacts ?? []) {
          if (c.status !== "active" || seen.has(c._id)) continue;
          if (!passesExternalAdminFilter(c)) continue;
          seen.add(c._id);
          matched.push({
            _id: c._id,
            domain: "job",
            role: "",
            displayName: c.displayName,
            roleLabels: ((c.roles ?? []) as ContactRole[]).map(
              (r) => ROLE_META[r]?.label ?? r,
            ),
            data: jobContactData(c),
          });
        }
      }

      // Firm-contact fan-out. Category tags (built-in `fc:*` and custom
      // `fcc:*`) choose WHICH contacts get a document; address-state tags
      // (`fcs:*`, MOR-913) NARROW that selection. With no state tag the
      // template covers every state — states are a restriction, never a
      // requirement. A template tagged with states but no category uses the
      // states to select on their own. The dynamic `fcs:job` tag contributes
      // the job's own state; when the job has none recorded it contributes
      // nothing, so the template falls back to every state.
      const stateSet = new Set([
        ...firmStates,
        ...(matchJobState && jobState ? [jobState] : []),
      ]);
      const contactState = (c: {
        addresses: ReadonlyArray<{ state?: string }>;
      }) =>
        c.addresses
          .map((a) => a.state?.trim().toUpperCase())
          .find((s): s is string => Boolean(s)) ?? null;
      const passesStateFilter = (c: {
        addresses: ReadonlyArray<{ state?: string }>;
      }) => {
        if (stateSet.size === 0) return true;
        const st = contactState(c);
        return !!st && stateSet.has(st);
      };

      const pushFirmMatch = (
        c: NonNullable<typeof firmContacts>[number],
        roleLabels: string[],
      ) => {
        seen.add(c._id);
        matched.push({
          _id: c._id,
          domain: "firm",
          role: c.category,
          displayName: c.displayName,
          roleLabels,
          data: {
            displayName: c.displayName,
            abn: c.abn,
            acn: c.acn,
            email: c.email,
            phone: c.phone,
            addresses: c.addresses,
          },
          category: c.category,
          customFields: c.customFields,
        });
      };

      for (const category of firmCategories) {
        for (const c of firmContactsByCategory.get(category) ?? []) {
          if (seen.has(c._id)) continue;
          if (!passesStateFilter(c)) continue;
          pushFirmMatch(c, [
            FIRM_CATEGORY_META[c.category]?.label ?? "Firm Contact",
          ]);
        }
      }

      if (customCategoryIds.length > 0) {
        const customSet = new Set(customCategoryIds);
        for (const c of firmContacts ?? []) {
          if (c.status !== "active" || seen.has(c._id)) continue;
          if (
            !c.customCategoryId ||
            !customSet.has(String(c.customCategoryId))
          ) {
            continue;
          }
          if (!passesStateFilter(c)) continue;
          pushFirmMatch(c, [
            firmCustomCategoryById.get(String(c.customCategoryId))?.name ??
              "Custom Category",
          ]);
        }
      }

      // State-only tagging: the template has NO other selector at all, so
      // the state tags select firm contacts directly. A template that also
      // names a job role (e.g. "Bankrupt" + NSW) fans out to that role only —
      // states never broaden it into unrelated firm contacts (MOR-2342
      // BKY-10).
      if (firmStatesSelectOnTheirOwn(tagCounts)) {
        for (const c of firmContacts ?? []) {
          if (c.status !== "active" || seen.has(c._id)) continue;
          const st = contactState(c);
          if (!st || !stateSet.has(st)) continue;
          pushFirmMatch(c, [
            `${FIRM_CATEGORY_META[c.category]?.shortLabel ?? "Firm"} - ${st}`,
          ]);
        }
      }

      if (matched.length > 0) {
        map.set(t.templateKey, matched);
      }
    }
    return map;
  }, [
    templates,
    jobState,
    contactsByRole,
    contactsByCustomRoleId,
    customJobRolesById,
    jobContacts,
    firmContactsByCategory,
    firmContacts,
    firmCustomCategoryById,
  ]);

  // Templates that match the job's state (`fcs:job`) while the job has no
  // state recorded yet. Their fan-out silently widened to every state, so
  // the preview tells the practitioner why the list is longer than expected.
  const jobStateFallbackTemplateKeys = useMemo<Set<string>>(() => {
    const keys = new Set<string>();
    if (jobState) return keys;
    for (const t of templates ?? []) {
      if (((t.tags ?? []) as string[]).some(isFirmContactJobStateTag)) {
        keys.add(t.templateKey);
      }
    }
    return keys;
  }, [templates, jobState]);

  // Firm-contact REFERENCES — categories each template references via
  // `firmref_<category>_*` placeholders. Independent of `fc:*` fan-out
  // tagging: a reference pulls a single firm contact the user picks at
  // generate time (one picker per referenced category), so the wizard
  // scans the template's placeholder keys (letter HTML + extracted/PDF
  // fields) rather than its tags. Ordered by `FIRM_CATEGORY_ORDER` for a
  // stable picker layout.
  const firmRefCategoriesByTemplate = useMemo<
    Map<string, FirmContactCategory[]>
  >(() => {
    const map = new Map<string, FirmContactCategory[]>();
    if (!templates) return map;
    for (const t of templates) {
      const keys = new Set<string>();
      for (const k of collectPlaceholderKeysFromStoredTemplate(
        t.templateHtml ?? "",
        t.extractedContent,
      )) {
        keys.add(k);
      }
      for (const f of (t.extractedFields ?? []) as Array<{
        key: string;
        canonicalKey?: string;
      }>) {
        keys.add(f.key);
        if (f.canonicalKey) keys.add(f.canonicalKey);
      }
      for (const m of (t.pdfFieldMappings ?? []) as Array<{
        canonicalKey?: string;
      }>) {
        if (m.canonicalKey) keys.add(m.canonicalKey);
      }
      for (const fp of (t.fieldPositions ?? []) as Array<{
        canonicalKey?: string;
      }>) {
        if (fp.canonicalKey) keys.add(fp.canonicalKey);
      }

      const referenced = new Set<FirmContactCategory>();
      for (const k of keys) {
        const category = firmRefKeyCategory(k);
        if (category) referenced.add(category);
      }
      if (referenced.size > 0) {
        map.set(
          t.templateKey,
          FIRM_CATEGORY_ORDER.filter((c) => referenced.has(c)),
        );
      }
    }
    return map;
  }, [templates]);

  // For each template, the set of contact-source kinds referenced in
  // its body. Drives the per-(contact × source link) fan-out below: a
  // template that uses `contact_job_security_interest_*` fields gets
  // one generated doc per linked security interest, while a template
  // that only uses basic `contact_job_*` fields keeps the existing
  // one-doc-per-contact behaviour. The intersection with each
  // template's tagged roles (`ROLE_TO_SOURCE_KINDS`) is applied at
  // fan-out time so a template tagged `cr:secured_creditor` can only
  // pull `security_interest` data, never (e.g.) a liability row.
  const templateSourceKindsByKey = useMemo<
    Map<string, Set<ContactSourceKind>>
  >(() => {
    const map = new Map<string, Set<ContactSourceKind>>();
    if (!templates) return map;
    for (const t of templates) {
      const placeholderKeys = collectPlaceholderKeysFromStoredTemplate(
        t.templateHtml ?? "",
        t.extractedContent,
      );
      const kinds = new Set<ContactSourceKind>();
      for (const key of placeholderKeys) {
        const parsed = parseContactSourceFieldKey(key);
        // Only `job`-domain source fields are V1 — `firm` overlay is
        // documented in `contactFields.ts` for future expansion.
        if (parsed && parsed.domain === "job") kinds.add(parsed.sourceKind);
      }
      // A data trigger (MOR-886) reads a source field per document
      // exactly like a body placeholder does, but its key lives on the
      // variant group rather than in the template text. Without counting
      // it here the template never fans out over the contact's source
      // links, the `contact_job_<kind>_*` values are never resolved, and
      // every document falls back to "no selection".
      for (const slug of extractVariantGroupSlugs(
        t.templateHtml as string | undefined,
      )) {
        const boundKey = boundGroupFieldKeyBySlug.get(slug);
        if (!boundKey) continue;
        const parsed = parseContactSourceFieldKey(boundKey);
        if (parsed && parsed.domain === "job") kinds.add(parsed.sourceKind);
      }
      if (kinds.size > 0) map.set(t.templateKey, kinds);
    }
    return map;
  }, [templates, boundGroupFieldKeyBySlug]);

  // Per-template, per-source-kind set of `part` segments actually
  // referenced by the template body. Used to scope the editable source-
  // field cards in the wizard preview to just the parts the document
  // will use — showing all 11 security-interest parts when the template
  // only uses 2 of them would clutter the panel.
  const templateSourceFieldPartsByKey = useMemo<
    Map<string, Map<ContactSourceKind, Set<string>>>
  >(() => {
    const map = new Map<string, Map<ContactSourceKind, Set<string>>>();
    if (!templates) return map;
    for (const t of templates) {
      const placeholderKeys = collectPlaceholderKeysFromStoredTemplate(
        t.templateHtml ?? "",
        t.extractedContent,
      );
      const parts = new Map<ContactSourceKind, Set<string>>();
      for (const key of placeholderKeys) {
        const parsed = parseContactSourceFieldKey(key);
        if (!parsed || parsed.domain !== "job") continue;
        const set = parts.get(parsed.sourceKind) ?? new Set<string>();
        parts.set(parsed.sourceKind, set);
        set.add(parsed.part);
      }
      if (parts.size > 0) map.set(t.templateKey, parts);
    }
    return map;
  }, [templates]);

  /**
   * Per-template, per-contact list of source-link selections that the
   * fan-out will emit one document for. Built from
   * `matchingContactsByTemplate` ∩ `templateSourceKindsByKey` ∩
   * `ROLE_TO_SOURCE_KINDS[contact.role]` ∩ `jobContactSourceRows`.
   *
   *   - Templates without any source-field keys do NOT appear here at
   *     all — they keep the existing single-row-per-contact behaviour.
   *   - Job contacts with at least one matching source row produce N
   *     entries (one per link). The wizard renders them as N preview
   *     rows and the per-row override Select.
   *   - Job contacts with zero matching source rows produce a single
   *     entry with `link: null` so the wizard can surface a warning
   *     and the doc still generates (with empty source-field values).
   *   - Firm contacts are skipped — source links live on `jobContacts`
   *     only in V1; a firm contact's job-level overlay (if any) is
   *     handled via the `cr:` role tags, not `fc:` category tags.
   */
  const sourceFanOutByTemplate = useMemo<
    Map<
      string,
      Map<
        string,
        Array<{
          sourceKind: ContactSourceKind;
          link: {
            sourceId: string;
            roleLabel: string;
            detail?: string;
            amount?: number;
          } | null;
          row: unknown;
        }>
      >
    >
  >(() => {
    const result = new Map<
      string,
      Map<
        string,
        Array<{
          sourceKind: ContactSourceKind;
          link: {
            sourceId: string;
            roleLabel: string;
            detail?: string;
            amount?: number;
          } | null;
          row: unknown;
        }>
      >
    >();
    if (templateSourceKindsByKey.size === 0) return result;
    if (!jobContactSourceRows) return result;

    for (const [templateKey, kinds] of templateSourceKindsByKey) {
      const contacts = matchingContactsByTemplate.get(templateKey) ?? [];
      const perContact = new Map<
        string,
        Array<{
          sourceKind: ContactSourceKind;
          link: {
            sourceId: string;
            roleLabel: string;
            detail?: string;
            amount?: number;
          } | null;
          row: unknown;
        }>
      >();
      for (const contact of contacts) {
        if (contact.domain !== "job") continue;
        const sources = jobContactSourceRows[contact._id as Id<"jobContacts">];
        const roleKinds =
          ROLE_TO_SOURCE_KINDS[contact.role as ContactRole] ?? [];
        const allowedKinds = new Set<ContactSourceKind>(
          roleKinds.filter((k) => kinds.has(k)),
        );
        const rows: Array<{
          sourceKind: ContactSourceKind;
          link: {
            sourceId: string;
            roleLabel: string;
            detail?: string;
            amount?: number;
          } | null;
          row: unknown;
        }> = [];
        for (const kind of allowedKinds) {
          const entries = sources?.[kind] ?? [];
          for (const entry of entries) {
            rows.push({
              sourceKind: kind,
              link: {
                sourceId: entry.link.sourceId,
                roleLabel: entry.link.roleLabel,
                detail: entry.link.detail,
                amount: entry.link.amount,
              },
              row: entry.row,
            });
          }
        }
        if (rows.length === 0) {
          // Surface as a single empty row so the warning shows in the
          // wizard rather than the contact being silently skipped.
          rows.push({ sourceKind: [...kinds][0], link: null, row: null });
        }
        perContact.set(contact._id, rows);
      }
      if (perContact.size > 0) result.set(templateKey, perContact);
    }
    return result;
  }, [
    templateSourceKindsByKey,
    matchingContactsByTemplate,
    jobContactSourceRows,
  ]);

  // Per-row source-link override state. Keys are
  // `${templateKey}::${contactId}::${rowIndex}` (rowIndex is the
  // position of the row in `sourceFanOutByTemplate` for that
  // (template × contact) pair). Value is the user's chosen `sourceId`
  // for that row — used at generate time to swap which link a fan-out
  // row resolves from. When the key is absent, the row uses its
  // natural (default) link from `sourceFanOutByTemplate`.
  const [sourceLinkOverrides, setSourceLinkOverrides] = useState<
    Map<string, { sourceKind: ContactSourceKind; sourceId: string }>
  >(() => new Map());

  const buildSourceFanOutRowKey = useCallback(
    (templateKey: string, contactId: string, rowIndex: number): string =>
      `${templateKey}::${contactId}::${rowIndex}`,
    [],
  );

  const setSourceLinkOverride = useCallback(
    (rowKey: string, sourceKind: ContactSourceKind, sourceId: string) => {
      setSourceLinkOverrides((prev) => {
        const next = new Map(prev);
        next.set(rowKey, { sourceKind, sourceId });
        return next;
      });
      // Reset per-field overrides for this row when the underlying
      // source link is swapped — the previous edits applied to the old
      // source row and would be confusing if carried over (e.g. an
      // overridden PPSR number from SI #1 silently replacing SI #2's
      // real number).
      setSourceFieldOverrides((prev) => {
        if (!prev.has(rowKey)) return prev;
        const next = new Map(prev);
        next.delete(rowKey);
        return next;
      });
    },
    [],
  );

  // Per-row source-field override state. Keys are the same row key as
  // `sourceLinkOverrides` (`${templateKey}::${contactId}::${rowIndex}`).
  // Value is `Record<canonicalFieldKey, string>` — the user's manual
  // override for one or more `contact_job_<kind>_<part>` fields on that
  // (template × contact × source link) row. Merged on top of the
  // resolved source values at generate time so an edited cell wins.
  const [sourceFieldOverrides, setSourceFieldOverrides] = useState<
    Map<string, Record<string, string>>
  >(() => new Map());

  const setSourceFieldOverride = useCallback(
    (rowKey: string, fieldKey: string, value: string) => {
      setSourceFieldOverrides((prev) => {
        const next = new Map(prev);
        const existing = next.get(rowKey) ?? {};
        next.set(rowKey, { ...existing, [fieldKey]: value });
        return next;
      });
    },
    [],
  );

  // Every contact id this wizard session has already offered per template.
  // Distinguishes a contact that is genuinely NEW to the job (auto-tick it)
  // from one the user deliberately unticked (leave it alone) — comparing
  // against the current selection can't tell those apart, and re-ticking
  // every unticked contact on each recompute silently re-selected all 43
  // creditors after the user narrowed the run to one.
  const seenContactIdsByTemplateRef = useRef<Map<string, Set<string>>>(
    new Map(),
  );

  // Lazily seed each fan-out template's contact-selection set with "all
  // ticked" the first time we see it. The user's subsequent toggles are
  // preserved between re-renders. Templates that lose their fan-out
  // (e.g. no matching contacts after a contact archive) are pruned.
  useEffect(() => {
    setContactSelectionsByTemplate((prev) => {
      let changed = false;
      const next = new Map(prev);

      for (const [templateKey, contacts] of matchingContactsByTemplate) {
        const validIds = new Set(contacts.map((c) => c._id));
        const existing = next.get(templateKey);
        const seen =
          seenContactIdsByTemplateRef.current.get(templateKey) ?? new Set();
        if (!existing) {
          next.set(templateKey, new Set(validIds));
          seenContactIdsByTemplateRef.current.set(
            templateKey,
            new Set(validIds),
          );
          changed = true;
          continue;
        }
        // Contacts added to the job mid-flow (never offered before) are
        // auto-ticked so the user doesn't lose them by default. Contacts the
        // user unticked stay unticked. Removed contacts are pruned.
        const trimmed = new Set<string>();
        for (const id of existing) if (validIds.has(id)) trimmed.add(id);
        for (const id of validIds) if (!seen.has(id)) trimmed.add(id);
        for (const id of validIds) seen.add(id);
        seenContactIdsByTemplateRef.current.set(templateKey, seen);
        if (
          trimmed.size !== existing.size ||
          [...trimmed].some((id) => !existing.has(id))
        ) {
          next.set(templateKey, trimmed);
          changed = true;
        }
      }

      // Drop selections for templates that no longer fan out (e.g. tag was
      // removed from the template, or all matching contacts disappeared).
      for (const key of [...next.keys()]) {
        if (!matchingContactsByTemplate.has(key)) {
          next.delete(key);
          seenContactIdsByTemplateRef.current.delete(key);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [matchingContactsByTemplate]);

  const toggleContactPickerExpanded = useCallback((templateKey: string) => {
    setExpandedContactPickers((prev) => {
      const next = new Set(prev);
      if (next.has(templateKey)) next.delete(templateKey);
      else next.add(templateKey);
      return next;
    });
  }, []);

  const toggleAttachmentGroupExpanded = useCallback((templateKey: string) => {
    setExpandedAttachmentGroups((prev) => {
      const next = new Set(prev);
      if (next.has(templateKey)) next.delete(templateKey);
      else next.add(templateKey);
      return next;
    });
  }, []);

  const toggleContactSelection = useCallback(
    (templateKey: string, contactId: string) => {
      setContactSelectionsByTemplate((prev) => {
        const next = new Map(prev);
        const current = new Set(next.get(templateKey) ?? []);
        if (current.has(contactId)) current.delete(contactId);
        else current.add(contactId);
        next.set(templateKey, current);
        return next;
      });
    },
    [],
  );

  const setAllContactsForTemplate = useCallback(
    (templateKey: string, selectAll: boolean) => {
      setContactSelectionsByTemplate((prev) => {
        const next = new Map(prev);
        const all = matchingContactsByTemplate.get(templateKey) ?? [];
        next.set(
          templateKey,
          selectAll ? new Set(all.map((c) => c._id)) : new Set(),
        );
        return next;
      });
    },
    [matchingContactsByTemplate],
  );

  // Tracks templates the user has explicitly unselected so the auto-select
  // and dependency-auto-add effects below never re-add them. Without this,
  // a "Suggested" template stays selected forever because the auto-select
  // re-fires, and a generated_precedent dependency is re-added every time
  // its parent template's attachments are recomputed.
  const [manuallyUnselectedKeys, setManuallyUnselectedKeys] = useState<
    Set<string>
  >(new Set());

  // Map each generated-precedent attachment dependency → the display names of
  // the user-selected document(s) that pull it in. Derived purely from the
  // templates the user picked (`userSelectedKeys`), so it can't form a cycle
  // with the effective selection below. A document attaches another via a
  // template-level attachment whose `sourceKind === "generated_precedent"`.
  const dependencyParentNamesByKey = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!templates) return map;
    for (const template of templates) {
      if (!userSelectedKeys.has(template.templateKey)) continue;
      for (const att of (template.attachments ??
        []) as TemplateAttachmentFromDb[]) {
        if (
          att.sourceKind !== "generated_precedent" ||
          !att.generatedTemplateKey
        ) {
          continue;
        }
        const parents = map.get(att.generatedTemplateKey) ?? [];
        parents.push(template.name ?? template.templateKey);
        map.set(att.generatedTemplateKey, parents);
      }
    }
    return map;
  }, [templates, userSelectedKeys]);

  // Static attachment relationships across ALL templates, independent of the
  // user's current selection. Lets the Select Templates step persistently flag
  // which documents are attachments of a principal document (vs standalone
  // principal documents) — mirroring the paperclip "Attachment" treatment in
  // the Document Generator so the distinction is visible before anything is
  // picked. Maps an attachment template key → the display names of the
  // principal document(s) that attach it.
  const attachmentParentNamesByKey = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!templates) return map;
    for (const template of templates) {
      for (const att of (template.attachments ??
        []) as TemplateAttachmentFromDb[]) {
        if (
          att.sourceKind !== "generated_precedent" ||
          !att.generatedTemplateKey
        ) {
          continue;
        }
        const parents = map.get(att.generatedTemplateKey) ?? [];
        const parentName =
          template.displayName ?? template.name ?? template.templateKey;
        if (!parents.includes(parentName)) parents.push(parentName);
        map.set(att.generatedTemplateKey, parents);
      }
    }
    return map;
  }, [templates]);

  // Forward view of the same static relationships: principal template key →
  // the set of attachment template keys it generates. Used to nest each
  // attachment beside its principal in the Select Templates step.
  const attachmentChildKeysByParentKey = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!templates) return map;
    for (const template of templates) {
      for (const att of (template.attachments ??
        []) as TemplateAttachmentFromDb[]) {
        if (
          att.sourceKind !== "generated_precedent" ||
          !att.generatedTemplateKey
        ) {
          continue;
        }
        const children = map.get(template.templateKey) ?? new Set<string>();
        children.add(att.generatedTemplateKey);
        map.set(template.templateKey, children);
      }
    }
    return map;
  }, [templates]);

  // The dependency template keys required by the current user selection.
  const requiredDependencyKeys = useMemo(
    () => new Set(dependencyParentNamesByKey.keys()),
    [dependencyParentNamesByKey],
  );

  // The EFFECTIVE selection used everywhere downstream (checkboxes, field
  // review, generation): the user's picks PLUS every required attachment
  // dependency that hasn't been deliberately dismissed. Unselecting the parent
  // shrinks `requiredDependencyKeys`, so a dependency-only document drops out
  // automatically.
  const selectedKeys = useMemo(() => {
    const next = new Set(userSelectedKeys);
    for (const dep of requiredDependencyKeys) {
      if (!dismissedDependencyKeys.has(dep)) next.add(dep);
    }
    return next;
  }, [userSelectedKeys, requiredDependencyKeys, dismissedDependencyKeys]);

  // Confirm-step summary, shaped as packs: a selected template that another
  // selected template attaches via `generated_precedent` is nested under that
  // parent instead of listed on its own, mirroring the Day One documents table
  // so the user confirms the same hierarchy they later send.
  const confirmSummaryRows = useMemo(() => {
    const attachmentKeysByParent = new Map<string, string[]>();
    const nestedKeys = new Set<string>();

    for (const template of templates ?? []) {
      if (!selectedKeys.has(template.templateKey)) continue;
      const attachmentKeys: string[] = [];
      for (const att of (template.attachments ??
        []) as TemplateAttachmentFromDb[]) {
        const childKey = att.generatedTemplateKey;
        if (att.sourceKind !== "generated_precedent" || !childKey) continue;
        if (childKey === template.templateKey || !selectedKeys.has(childKey))
          continue;
        if (attachmentKeys.includes(childKey)) continue;
        attachmentKeys.push(childKey);
        nestedKeys.add(childKey);
      }
      if (attachmentKeys.length > 0) {
        attachmentKeysByParent.set(template.templateKey, attachmentKeys);
      }
    }

    const parentKeys = Array.from(selectedKeys).filter(
      (key) => !nestedKeys.has(key),
    );
    // Mutually-attaching templates would nest every row and hide the list, so
    // fall back to a flat summary rather than rendering nothing.
    const topLevelKeys =
      parentKeys.length > 0 ? parentKeys : Array.from(selectedKeys);

    return topLevelKeys.map((key) => ({
      key,
      attachmentKeys: attachmentKeysByParent.get(key) ?? [],
    }));
  }, [templates, selectedKeys]);

  const confirmAttachmentCount = useMemo(
    () =>
      confirmSummaryRows.reduce(
        (total, row) => total + row.attachmentKeys.length,
        0,
      ),
    [confirmSummaryRows],
  );

  // What the Select Templates step actually renders: the search/category
  // filtered list, further narrowed to selected templates unless the user
  // flipped the "All" toggle. Defined after `selectedKeys` because the
  // condensed view keys off the effective selection.
  const visibleWizardCategories = useMemo((): WizardCategory[] => {
    if (showAllTemplates) return filteredWizardCategories;
    const out: WizardCategory[] = [];
    for (const group of filteredWizardCategories) {
      const types = group.types.filter((type) => selectedKeys.has(type.key));
      if (types.length === 0) continue;
      out.push({ ...group, types });
    }
    return out;
  }, [filteredWizardCategories, showAllTemplates, selectedKeys]);

  // Group each visible category's templates so an attachment sits with the
  // principal document that generates it (mirrors the Document Generator's
  // nesting): a principal followed by the in-category attachments it pulls in.
  // Attachments whose principal isn't in this category stay top-level so
  // they're never hidden. Nesting is a single level — a grandchild attachment
  // is promoted back to top-level rather than buried under a nested row.
  //
  // Derived from the Convex-backed template data, so it is computed once per
  // change rather than on every grid/table render.
  const categoryUnitsByKey = useMemo(() => {
    const byCategory = new Map<string, CategoryUnit[]>();
    for (const group of visibleWizardCategories) {
      const types = group.types;
      const childToParent = new Map<string, string>();
      for (const parent of types) {
        const childKeys = attachmentChildKeysByParentKey.get(parent.key);
        if (!childKeys) continue;
        for (const candidate of types) {
          if (
            candidate.key !== parent.key &&
            childKeys.has(candidate.key) &&
            !childToParent.has(candidate.key)
          ) {
            childToParent.set(candidate.key, parent.key);
          }
        }
      }
      for (const [child, parent] of [...childToParent]) {
        if (childToParent.has(parent)) childToParent.delete(child);
      }
      const units: CategoryUnit[] = [];
      for (const type of types) {
        if (childToParent.has(type.key)) continue;
        units.push({
          principal: type,
          attachments: types.filter(
            (candidate) => childToParent.get(candidate.key) === type.key,
          ),
        });
      }
      byCategory.set(group.key, units);
    }
    return byCategory;
  }, [visibleWizardCategories, attachmentChildKeysByParentKey]);

  // The same hierarchy flattened into indent-tagged rows for the table view,
  // so both views read one shared computation.
  const categoryRowsByKey = useMemo(() => {
    const byCategory = new Map<string, CategoryRow[]>();
    for (const [key, units] of categoryUnitsByKey) {
      const rows: CategoryRow[] = [];
      for (const unit of units) {
        rows.push({ type: unit.principal, depth: 0 });
        for (const att of unit.attachments) rows.push({ type: att, depth: 1 });
      }
      byCategory.set(key, rows);
    }
    return byCategory;
  }, [categoryUnitsByKey]);

  // Auto-select tag-matched templates as they appear in `tagMatchReasons`.
  //
  // Originally this fired exactly once via a `tagAutoSelectDone` boolean,
  // but `jobContacts` loads asynchronously after `templates` — so on first
  // render `cr:*` tagged templates (e.g. "Letter to Director" via
  // `cr:corporate`) didn't yet appear in `tagMatchReasons` and the
  // single-shot auto-select missed them. Tracking each key the effect has
  // already ticked lets us tick newly-arrived suggestions on subsequent
  // recomputes without re-selecting templates the user has explicitly
  // deselected.
  const [autoSelectedKeys, setAutoSelectedKeys] = useState<Set<string>>(
    () => new Set(),
  );
  useEffect(() => {
    if (reselect || tagMatchReasons.size === 0) return;
    const newlyMatched: string[] = [];
    for (const key of tagMatchReasons.keys()) {
      if (autoSelectedKeys.has(key)) continue;
      if (manuallyUnselectedKeys.has(key)) continue;
      newlyMatched.push(key);
    }
    if (newlyMatched.length === 0) return;
    setUserSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const key of newlyMatched) next.add(key);
      return next;
    });
    setAutoSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const key of newlyMatched) next.add(key);
      return next;
    });
  }, [reselect, tagMatchReasons, manuallyUnselectedKeys, autoSelectedKeys]);

  // Switching phase recomputes `tagMatchReasons`, so the previous phase's
  // suggestions must stop counting as selected — otherwise a document
  // suggested only for the old phase is still generated for the new one.
  //
  // Only *automatic* selections are swept. Every handler that turns a template
  // on (or off) deliberately drops it from `autoSelectedKeys` first, so an
  // explicit pick survives the switch; `manuallyUnselectedKeys` is left intact
  // so a deliberate deselect is never undone. Emptying `autoSelectedKeys` lets
  // the effect above re-tick the new phase's matches on the next pass.
  const previousPhaseRef = useRef<JobStatus | null>(selectedPhase);
  useEffect(() => {
    if (previousPhaseRef.current === selectedPhase) return;
    previousPhaseRef.current = selectedPhase;
    if (autoSelectedKeys.size === 0) return;
    setUserSelectedKeys((prev) => {
      const next = new Set(prev);
      for (const key of autoSelectedKeys) next.delete(key);
      return next;
    });
    setAutoSelectedKeys(new Set());
  }, [selectedPhase, autoSelectedKeys]);

  const attachmentCandidatesByKey = useMemo(() => {
    const map = new Map<PrecedentAttachmentKey, AttachmentCandidate[]>();
    for (const group of attachmentCandidateGroups ?? []) {
      map.set(group.attachmentKey as PrecedentAttachmentKey, group.candidates);
    }
    return map;
  }, [attachmentCandidateGroups]);

  // For `custom` (named) attachments: all job documents as picker candidates,
  // plus the current job-level resolution keyed by reference key.
  const referenceCandidates = useMemo(
    () =>
      (attachableJobDocuments ?? []).map((doc) => ({
        documentId: doc.documentId,
        fileName: doc.fileName,
      })),
    [attachableJobDocuments],
  );
  const resolutionByRefKey = useMemo(() => {
    const map = new Map<
      string,
      {
        status: "resolved" | "skipped";
        documentId: Id<"documents"> | null;
        fileName: string | null;
      }
    >();
    for (const row of resolvedReferences ?? []) {
      map.set(row.referenceKey, {
        status: row.status,
        documentId: row.documentId,
        fileName: row.fileName,
      });
    }
    return map;
  }, [resolvedReferences]);

  const selectedTemplateAttachments = useMemo(() => {
    const map = new Map<string, AttachmentRequirement[]>();
    if (!templates) return map;

    for (const template of templates) {
      if (!selectedKeys.has(template.templateKey)) continue;
      const requirements = (
        (template.attachments ?? []) as TemplateAttachmentFromDb[]
      ).map((attachment) => ({
        ...normaliseAttachmentRequirement({
          name: attachment.name,
          description: attachment.description,
          required: attachment.required,
        }),
        ...attachment,
      })) as AttachmentRequirement[];
      if (requirements.length > 0) {
        map.set(template.templateKey, requirements);
      }
    }

    return map;
  }, [templates, selectedKeys]);

  const hasSelectedAttachments = selectedTemplateAttachments.size > 0;

  // NOTE: generated-precedent attachment dependencies are now folded into the
  // derived `selectedKeys` (via `requiredDependencyKeys`) rather than pushed
  // into state by an effect. This guarantees a selected document's attachment
  // is always generated, and that a bulk "Unselect all" can never strand it.

  useEffect(() => {
    if (selectedTemplateAttachments.size === 0) {
      setAttachmentSelections({});
      return;
    }

    setAttachmentSelections((prev) => {
      const next: Record<string, Record<string, AttachmentSelection>> = {};

      for (const [templateKey, requirements] of selectedTemplateAttachments) {
        const current = prev[templateKey] ?? {};
        next[templateKey] = {};

        for (const [index, requirement] of requirements.entries()) {
          const slotKey = buildAttachmentSlotKey(
            requirement.attachmentKey,
            index,
          );
          const existing = current[slotKey];
          // A template-default slot with a configured file can only be
          // "skipped" by the old shared-reference clobber bug (the wizard
          // offers no Skip for a resolved default), so a skipped entry seeded
          // from a previously generated precedent is re-resolved below rather
          // than frozen.
          const isTemplateDefaultWithFile =
            requirement.sourceKind === "template_default" &&
            Boolean(requirement.storageId || requirement.fileName);
          if (
            existing?.status === "resolved" ||
            (existing?.status === "skipped" && !isTemplateDefaultWithFile)
          ) {
            next[templateKey][slotKey] = existing;
            continue;
          }

          // A template default file ships with the template, so it satisfies
          // the requirement on its own. Resolve it whenever a default is
          // configured by storage *or* name — a default that lacks a
          // storageId must not fall through to "missing" (MOR-568).
          if (isTemplateDefaultWithFile) {
            next[templateKey][slotKey] = {
              name: requirement.name,
              description: requirement.description,
              required: requirement.required,
              attachmentKey: requirement.attachmentKey,
              sourceKind: "template_default",
              status: "resolved",
              documentCategory: requirement.documentCategory as
                DocumentCategory | undefined,
              storageId: requirement.storageId as Id<"_storage"> | undefined,
              fileName: requirement.fileName,
              fileType: requirement.fileType,
              fileSize: requirement.fileSize,
              resolvedAt: Date.now(),
            };
            continue;
          }

          if (requirement.sourceKind === "generated_precedent") {
            if (
              requirement.generatedTemplateKey &&
              selectedKeys.has(requirement.generatedTemplateKey)
            ) {
              const generatedType = allTemplateTypes.find(
                (type) => type.key === requirement.generatedTemplateKey,
              );
              next[templateKey][slotKey] = {
                name: requirement.name,
                description: requirement.description,
                required: requirement.required,
                attachmentKey: requirement.attachmentKey,
                sourceKind: "generated_precedent",
                status: "resolved",
                generatedTemplateKey: requirement.generatedTemplateKey,
                fileName:
                  generatedType?.name ?? requirement.generatedTemplateKey,
                resolvedAt: Date.now(),
              };
            } else {
              // The referenced template isn't part of this run. Do NOT fall
              // through to the job-document candidates below — that used to
              // attach an arbitrary first candidate file in its place.
              next[templateKey][slotKey] = {
                name: requirement.name,
                description: requirement.description,
                required: requirement.required,
                attachmentKey: requirement.attachmentKey,
                sourceKind: "generated_precedent",
                status: "missing",
                generatedTemplateKey: requirement.generatedTemplateKey,
                documentCategory: requirement.documentCategory as
                  DocumentCategory | undefined,
              };
            }
            continue;
          }

          const candidates =
            attachmentCandidatesByKey.get(requirement.attachmentKey) ?? [];
          const candidate = candidates[0];
          if (candidate) {
            next[templateKey][slotKey] = {
              name: requirement.name,
              description: requirement.description,
              required: requirement.required,
              attachmentKey: requirement.attachmentKey,
              sourceKind: requirement.sourceKind || "job_document",
              status: "resolved",
              documentCategory: candidate.category,
              documentId: candidate.documentId,
              storageId: candidate.storageId,
              fileName: candidate.fileName,
              fileType: candidate.fileType,
              fileSize: candidate.fileSize,
              resolvedAt: Date.now(),
            };
          } else {
            next[templateKey][slotKey] = {
              name: requirement.name,
              description: requirement.description,
              required: requirement.required,
              attachmentKey: requirement.attachmentKey,
              sourceKind: requirement.sourceKind || "none",
              status: "missing",
              documentCategory: requirement.documentCategory as
                DocumentCategory | undefined,
            };
          }
        }
      }

      return next;
    });
  }, [
    selectedTemplateAttachments,
    attachmentCandidatesByKey,
    selectedKeys,
    allTemplateTypes,
  ]);

  // Auto-populate fields from job + org defaults
  const jobFields = useMemo(() => {
    if (!job) return {};
    return mapJobToCanonicalFields(job);
  }, [job]);

  // Whether the job Overview "Background" summary has been generated/entered.
  // Drives the soft, non-blocking flag on the `background_summary` field in
  // the Review step (banner + inline note), so users know a document using it
  // will render blank until an overview exists. Derived from the canonical
  // `background_summary` field (post `htmlToPlainText`) rather than the raw
  // `overviewSummaryHtml` string, since whitespace-only markup like
  // "<p>   </p>" is truthy as HTML but maps to an empty canonical field.
  const hasOverviewSummary = Boolean(jobFields.background_summary?.trim());

  // Build practitioner name options for the signatory dropdown
  const practitionerOptions = useMemo(() => {
    if (!job) return [];
    const names = new Set<string>();
    for (const name of job.appointees ?? []) if (name) names.add(name);
    for (const name of job.practitioners ?? []) if (name) names.add(name);
    return Array.from(names);
  }, [job]);

  // Joint appointees must be registered practitioners the firm has configured
  // under Settings → Fields → Practitioner Details, not arbitrary job team
  // members, so the second practitioner picker draws on presets instead.
  const presetPractitionerOptions = useMemo(
    () => getPresetPractitionerNames(allPresets),
    [allPresets],
  );

  const companyNameOptions = useMemo(() => {
    if (!job) return [];
    const options: Array<{ value: string; label: string }> = [];
    const registeredName = jobFields.company_name || job.entityName;
    if (registeredName) {
      options.push({ value: registeredName, label: "Registered company name" });
    }
    const concatenatedName = buildConcatenatedCompanyName(
      registeredName,
      job.companyNameVariants ?? [],
    );
    if (concatenatedName) {
      options.push({
        value: concatenatedName,
        label: "Registered name with variants",
      });
    }

    for (const variant of job.companyNameVariants ?? []) {
      const name = variant.name?.trim();
      if (!name) continue;
      options.push({
        value: name,
        label: COMPANY_NAME_VARIANT_TYPE_LABELS[variant.type],
      });
    }

    const seen = new Set<string>();
    return options.filter((option) => {
      const key = option.value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [job, jobFields]);

  const currentUserAsSignatory = useMemo(() => {
    if (!currentUser) return null;
    const name =
      [currentUser.firstName, currentUser.lastName].filter(Boolean).join(" ") ||
      currentUser.email;
    return {
      userId: currentUser._id,
      name,
      signatureText: currentUser.signatureText,
      signatureImageUrl: currentUser.signatureImageUrl,
      signatureScale: currentUser.signatureScale,
    };
  }, [currentUser]);

  const signatureOptions = useMemo(() => {
    if (membersWithSignatures && membersWithSignatures.length > 0) {
      return membersWithSignatures;
    }
    if (
      currentUserAsSignatory &&
      (currentUserAsSignatory.signatureText ||
        currentUserAsSignatory.signatureImageUrl)
    ) {
      return [currentUserAsSignatory];
    }
    return [];
  }, [membersWithSignatures, currentUserAsSignatory]);

  const selectedSignatory = useMemo(() => {
    if (selectedSignatoryId === "__none__") return null;
    if (!selectedSignatoryId) return currentUserAsSignatory;
    return (
      signatureOptions.find((m) => m.userId === selectedSignatoryId) ??
      currentUserAsSignatory
    );
  }, [selectedSignatoryId, signatureOptions, currentUserAsSignatory]);

  /**
   * Whether any selected template body carries a repeat over the
   * `signatories` collection — gates the multi-signer picker in the
   * review step's signature card.
   */
  const signatoriesCollectionUsed = useMemo(() => {
    for (const key of selectedKeys) {
      const tpl = templates?.find((t) => t.templateKey === key);
      const html = (tpl?.templateHtml as string | undefined) ?? "";
      if (html.includes('data-collection="signatories"')) return true;
    }
    return false;
  }, [selectedKeys, templates]);

  const toggleRepeatSignatory = (userId: string) => {
    setPickedSignatoryIds((prev) => {
      // First interaction narrows "everyone" down to everyone-but-this.
      const base = prev ?? new Set(signatureOptions.map((m) => m.userId));
      const next = new Set(base);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // MOR-2342 — who the day-one primary contact is. The chosen person's
  // name / email / direct phone sit above the firm's Contact preset and
  // below any per-document edit, so the analyst can still change the values
  // in the review grid.
  const effectiveContactPerson = useMemo<DayOneContactDefault>(
    () => contactPersonChoice ?? firmContactDefault ?? { mode: "firm_preset" },
    [contactPersonChoice, firmContactDefault],
  );
  const contactPersonFields = useMemo(() => {
    const candidates = contactPersonCandidates ?? [];
    const staffMember =
      effectiveContactPerson.mode === "staff_member"
        ? (candidates.find((c) => c.userId === effectiveContactPerson.userId) ??
          null)
        : null;
    const roleHolder =
      effectiveContactPerson.mode === "job_role"
        ? (jobRoleAssignments ?? []).find(
            (a) => a.roleId === effectiveContactPerson.roleId,
          )?.user
        : undefined;
    const jobRoleMember = roleHolder
      ? (candidates.find((c) => c.userId === roleHolder._id) ?? {
          name: memberDisplayName(roleHolder),
          email: roleHolder.email,
        })
      : null;
    return resolveContactPersonFields({
      mode: effectiveContactPerson.mode,
      jobRoleMember,
      preparingUser: currentUser
        ? {
            name: memberDisplayName(currentUser),
            email: currentUser.email,
            phone: currentUser.phone,
          }
        : null,
      staffMember,
      appointeeName: jobFields.practitioner_name,
      members: candidates,
    });
  }, [
    effectiveContactPerson.mode,
    effectiveContactPerson.userId,
    effectiveContactPerson.roleId,
    contactPersonCandidates,
    jobRoleAssignments,
    currentUser,
    jobFields.practitioner_name,
  ]);
  const orgDefaults = useMemo(
    () => ({ ...presetDefaults, ...contactPersonFields }),
    [presetDefaults, contactPersonFields],
  );

  const baseMergedFields = useMemo(
    () =>
      mergeFieldValues(jobFields, orgDefaults, fieldOverrides, undefined, {
        appointmentType: job?.appointmentType,
      }),
    [jobFields, orgDefaults, fieldOverrides, job?.appointmentType],
  );
  // Title overrides the firm fixed for THIS job's appointment type
  // (MOR-2354) — reported as preset defaults in the review grid.
  const scopedTitleOverrides = useMemo(
    () => resolveScopedTitles(orgDefaults, job?.appointmentType),
    [orgDefaults, job?.appointmentType],
  );

  /**
   * Date calcs configured on each selected template's date fields, keyed by
   * template key. Set in the letter editor's field bubble menu and the PDF
   * field editor.
   */
  const dateCalcsByTemplate = useMemo(() => {
    const map = new Map<string, Record<string, DateCalc>>();
    for (const key of selectedKeys) {
      const tpl = templates?.find((t) => t.templateKey === key);
      const calcs = tpl?.dateCalcs as Record<string, DateCalc> | undefined;
      if (calcs && Object.keys(calcs).length > 0) map.set(key, calcs);
    }
    return map;
  }, [selectedKeys, templates]);

  /**
   * Dates derived from the selected templates' calcs, so the review step
   * shows the computed value instead of an empty "Needs Input" box. Generation
   * re-resolves per template against that document's own field map; this is
   * the union used for review, where two templates rarely disagree about the
   * same date.
   */
  const calculatedFields = useMemo<Record<string, string>>(() => {
    if (dateCalcsByTemplate.size === 0) return {};
    const out: Record<string, string> = {};
    for (const calcs of dateCalcsByTemplate.values()) {
      Object.assign(
        out,
        resolveTemplateDateCalcs(calcs, baseMergedFields, dateCalcContext),
      );
    }
    return out;
  }, [dateCalcsByTemplate, baseMergedFields, dateCalcContext]);

  // Derived dates sit below anything the user or the job supplies, so a calc
  // only ever fills a gap — including a key present but blank in the base map.
  const mergedFields = useMemo(() => {
    if (Object.keys(calculatedFields).length === 0) return baseMergedFields;
    const out = { ...baseMergedFields };
    for (const [key, value] of Object.entries(calculatedFields)) {
      if (!out[key]) out[key] = value;
    }
    return out;
  }, [calculatedFields, baseMergedFields]);

  // Canonical field key → label, so preview chips read "Court / Matter
  // Number" instead of the raw key.
  const fieldsLabelMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(CANONICAL_FIELDS_MAP).map(([k, v]) => [k, v.label]),
      ),
    [],
  );

  // Field-source classifier (job/org/custom/template/manual/contact) so preview
  // chips get the same colour coding as the editor. Built from the same data
  // the wizard already loads.
  const customFieldKeySet = useMemo(
    () => new Set(customFieldsByKey.keys()),
    [customFieldsByKey],
  );
  const orgPresetKeySet = useMemo(
    () => new Set(Object.keys(orgDefaults)),
    [orgDefaults],
  );

  // Build one preview row per variant block in a template, resolved for the
  // currently-selected option (job default ← per-template override). Mirrors
  // the day-one picker: fields render as their resolved value where known, and
  // as a colour-coded chip only where the value is still missing.
  const buildVariantBlocks = useCallback(
    (templateKey: string, html: string) => {
      const instances = extractVariantBlockInstances(html).filter(
        (b) => b.vbId,
      );
      const jobVS = job?.variantSelections as
        Record<string, string> | undefined;
      const merged = {
        ...(jobVS ?? {}),
        ...(variantOverrides[templateKey] ?? {}),
      };
      const docSnips = snippetOverrides[templateKey] ?? {};
      const byId = new Map(variantSnippetsForPicker.map((s) => [s._id, s]));

      const tpl = templates?.find((t) => t.templateKey === templateKey);
      const tplFieldsWithExample = new Set(
        (
          (tpl?.extractedFields as
            Array<{ key: string; exampleValue?: string }> | undefined) ?? []
        )
          .filter((f) => f.exampleValue?.trim())
          .map((f) => f.key),
      );
      const classify = (key: string) =>
        classifyFieldSource({
          key,
          customFieldKeys: customFieldKeySet,
          orgPresetKeys: orgPresetKeySet,
          templateFieldsWithExample: tplFieldsWithExample,
        });

      // This template's own field labels win over the canonical defaults, so a
      // chip reads the same here as it does in the library preview and the
      // editor (see `preferTemplateFieldLabel`).
      const tplLabelMap = {
        ...fieldsLabelMap,
        ...buildFieldsLabelMap(
          tpl?.extractedFields as
            | Array<{ key: string; label: string; canonicalKey?: string }>
            | undefined,
        ),
      };

      const renderPreview = (contentHtml: string) => {
        // 1. Fill resolved values inline (escaped). Leave the {{token}} in
        //    place when the value is still empty so step 2 chips it.
        const filled = stripInlineHighlights(contentHtml).replace(
          /\{\{(\w+)((?:\|[a-z0-9_]+)*)\}\}/g,
          (token, key: string, modsRaw: string) => {
            const v = mergedFields[key];
            if (v == null || String(v).trim() === "") return token;
            const escaped = String(v)
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;");
            // `stack` layout modifier renders the value one entry per line —
            // mirror the day-one substitution path.
            if (parseModifiers(modsRaw).includes("stack")) {
              return applyStackLayout(escaped, key);
            }
            // Name-list fields render inline as a grammatical list rather than
            // a space-collapsed run-on (MOR-777) — mirror day-one.
            if (NAME_LIST_FIELDS.has(key)) return formatInlineNameList(escaped);
            return escaped;
          },
        );
        // Lift a company status suffix onto the stacked company name's first
        // line and unwrap the data-company-stack marker before chipping the
        // remaining tokens (MOR-634).
        const reflowed = reflowCompanyStatus(filled);
        // 2. Chip the remaining (empty) placeholders, then colour by source.
        return annotateFieldSources(
          decorateFieldChipModifiers(
            prepareHtmlForEditor(reflowed, tplLabelMap),
          ),
          classify,
        );
      };

      return instances.map((b) => {
        const vbId = b.vbId as string;
        const selectedOption = merged[b.groupSlug];
        const overrideId = docSnips[vbId];
        const effId =
          overrideId ??
          (selectedOption
            ? (b.snippetRefs[selectedOption] ?? undefined)
            : undefined);
        const snip = effId ? byId.get(effId) : undefined;
        return {
          vbId,
          groupSlug: b.groupSlug,
          slotLabel: slotLabelForRefs(variantSnippetsForPicker, b.snippetRefs),
          previewHtml: snip ? renderPreview(snip.contentHtml) : undefined,
        };
      });
    },
    [
      job?.variantSelections,
      variantOverrides,
      snippetOverrides,
      variantSnippetsForPicker,
      templates,
      mergedFields,
      fieldsLabelMap,
      customFieldKeySet,
      orgPresetKeySet,
    ],
  );

  // Extract per-template default values from extractedFields.exampleValue.
  // These fill in recipient-specific fields (email, name, address) that are
  // constant for a given letter type but differ between templates.
  const templateDefaultsByKey = useMemo(() => {
    const map = new Map<string, Record<string, string>>();
    if (!templates) return map;
    for (const t of templates) {
      if (!t.extractedFields) continue;
      const defaults: Record<string, string> = {};
      for (const f of t.extractedFields) {
        if (f.exampleValue && ALWAYS_PER_DOCUMENT.has(f.key)) {
          defaults[f.key] = f.exampleValue;
        }
      }
      if (Object.keys(defaults).length > 0) {
        map.set(t.templateKey, defaults);
      }
    }
    return map;
  }, [templates]);

  // MOR-1003 — checkbox-group defaults configured on each template's PDF
  // placements ("Selected by default" in the Map Fields editor), keyed
  // `templateKey → { groupKey: optionValue }`. Materialised into condition
  // evaluation, the review form display, and the generated field values so
  // template defaults behave like explicit answers instead of invisible
  // fill-time fallbacks.
  const templateGroupDefaultsByKey = useMemo(() => {
    const map = new Map<string, Record<string, string>>();
    for (const t of (templates ?? []) as Array<{
      templateKey: string;
      fieldPositions?: CheckboxGroupPlacement[];
    }>) {
      const defaults = resolveCheckboxGroupDefaults(t.fieldPositions);
      if (Object.keys(defaults).length > 0) map.set(t.templateKey, defaults);
    }
    return map;
  }, [templates]);

  // Build per-template field mappings and reverse index for grouping.
  // Also tracks fieldType overrides from PDF mappings (e.g., "checkbox")
  // so the wizard renders the correct input for each field.
  const { fieldToTemplates, templateExtraFields, fieldTypeOverrides } =
    useMemo(() => {
      const fieldToTpls = new Map<string, Set<string>>();
      const tplExtras = new Map<
        string,
        Array<{
          key: string;
          label: string;
          fieldType: string;
          description?: string;
          options?: string[];
        }>
      >();
      const typeOverrides = new Map<string, string>();

      // Get-or-create the template set for a field key.
      const templatesForField = (key: string): Set<string> => {
        const set = fieldToTpls.get(key) ?? new Set<string>();
        fieldToTpls.set(key, set);
        return set;
      };

      // A `checkbox` type on a placement or an AcroForm mapping only says how
      // the value is stamped onto the PDF (tick vs. text). It must not turn a
      // value-bearing field into a tick box here: the review step would then
      // hide the real value and write "true"/"false" into every document that
      // uses the field. Only honour it for fields genuinely declared as
      // checkboxes, plus unknown keys (template extras, group bundles) where
      // the placement is the only type information we have.
      const applyCheckboxOverride = (key: string) => {
        const declaredType = lookupField(key)?.fieldType;
        if (declaredType && declaredType !== "checkbox") return;
        typeOverrides.set(key, "checkbox");
      };

      for (const tplKey of selectedKeys) {
        const type = allTemplateTypes.find((t) => t.key === tplKey);
        const extras: Array<{
          key: string;
          label: string;
          fieldType: string;
          description?: string;
          options?: string[];
        }> = [];
        const seenExtras = new Set<string>();
        const dbTemplate = templates?.find((t) => t.templateKey === tplKey);
        const hiddenGallagherFields = new Set(
          tplKey === "form_gallagher"
            ? (
                (dbTemplate?.fieldConfig as
                  GallagherFieldConfig[] | undefined) ?? []
              )
                .filter((entry) => entry.hidden)
                .map((entry) => entry.fieldKey)
            : [],
        );

        // Keys actually referenced by this template right now. Used to drop
        // stale `extractedFields` rows (legacy placeholders that have since
        // been removed from the body) so the wizard only surfaces fields a
        // user can still influence — MOR-465 / MOR-427.
        const documentKeys = new Set<string>();
        // Legacy AcroForm mappings stop driving the output the moment visual
        // placements exist: `fillPdfForm` flattens the form and fills
        // exclusively from `fieldPositions`. Their keys must not surface as
        // fields here (nor legitimise `extractedFields` rows, which
        // `updateMappings` derives from the same list) or the wizard shows
        // rows that can never reach the document — e.g. a permanently blank
        // Postal Address on forms whose address boxes are placed visually.
        const mappingsDriveOutput =
          ((dbTemplate?.fieldPositions as unknown[] | undefined)?.length ??
            0) === 0;
        if (type?.hardcodedFields) {
          for (const f of type.hardcodedFields) {
            if (f.canonicalKey) documentKeys.add(f.canonicalKey);
          }
        }
        if (dbTemplate?.pdfFieldMappings && mappingsDriveOutput) {
          for (const m of dbTemplate.pdfFieldMappings as Array<{
            canonicalKey?: string;
          }>) {
            if (m.canonicalKey) documentKeys.add(m.canonicalKey);
          }
        }
        if (dbTemplate?.fieldPositions) {
          for (const fp of dbTemplate.fieldPositions as Array<{
            canonicalKey?: string;
            groupKey?: string;
          }>) {
            if (fp.canonicalKey) documentKeys.add(fp.canonicalKey);
            if (fp.groupKey) documentKeys.add(fp.groupKey);
          }
        }
        // MOR-790 — PDF-overlay templates (mirrors the fill dispatch in
        // convex/jobs/precedents/mutations.ts) generate exclusively from
        // `fieldPositions` / `pdfFieldMappings`; their `templateHtml` and
        // `extractedContent` are extraction artefacts, not render inputs. A
        // `{{key}}` token surviving in that text must NOT resurrect a stale
        // `extractedFields` row (e.g. an AI-invented Recipient Address on the
        // APP Consent To Short Notice form) — the field would show in the
        // wizard yet have no bearing on the generated document.
        const isPdfFillTemplate =
          dbTemplate?.templateType !== "insurance" &&
          tplKey !== "form_gallagher" &&
          Boolean(
            (dbTemplate?.fieldPositions as unknown[] | undefined)?.length ||
            (dbTemplate?.pdfFieldMappings as unknown[] | undefined)?.length,
          );
        if (dbTemplate?.templateHtml && !isPdfFillTemplate) {
          for (const k of collectPlaceholderKeysFromStoredTemplate(
            dbTemplate.templateHtml as string,
            dbTemplate.extractedContent as string | undefined,
          )) {
            documentKeys.add(k);
          }
        }

        if (type?.hardcodedFields) {
          for (const f of type.hardcodedFields) {
            if (hiddenGallagherFields.has(f.key)) continue;
            if (f.canonicalKey) {
              templatesForField(f.canonicalKey).add(tplKey);
              if (f.fieldType === "checkbox") {
                applyCheckboxOverride(f.canonicalKey);
              }
            } else if (!seenExtras.has(f.key)) {
              seenExtras.add(f.key);
              extras.push({
                key: f.key,
                label: f.label,
                fieldType: f.fieldType,
                description: f.description,
              });
            }
          }
        }

        // PDF form templates with pdfFieldMappings.
        // Mappings without a canonicalKey are intentionally unassigned in the
        // Map Fields editor — skip them so they don't surface as "Needs Input"
        // rows in the wizard (they can never be auto-populated anyway).
        if (dbTemplate?.pdfFieldMappings && mappingsDriveOutput) {
          for (const m of dbTemplate.pdfFieldMappings as Array<{
            pdfFieldName: string;
            canonicalKey?: string;
            fieldType: string;
            checkboxValue?: string;
          }>) {
            if (!m.canonicalKey) continue;
            templatesForField(m.canonicalKey).add(tplKey);
            if (m.fieldType === "checkbox") {
              applyCheckboxOverride(m.canonicalKey);
            }
          }
        }

        // Visual field positions from the PDF editor. As well as recording
        // checkbox type overrides for canonical-bound boxes, this is where
        // we collect single-select groups (checkbox placements sharing a
        // `groupKey`) and synthesize one form field per group with the
        // group's options derived from each member's `optionValue`.
        if (dbTemplate?.fieldPositions) {
          const positions = dbTemplate.fieldPositions as Array<{
            canonicalKey: string;
            fieldType?: string;
            checkboxValue?: string;
            groupKey?: string;
            groupLabel?: string;
            optionValue?: string;
            defaultChecked?: boolean;
          }>;
          for (const fp of positions) {
            if (fp.canonicalKey && fp.fieldType === "checkbox") {
              applyCheckboxOverride(fp.canonicalKey);
            }
          }
          // Build a per-group bundle from the placements. We only emit a
          // group field when its boxes actually exist on this template.
          const groupsForTpl = new Map<
            string,
            { label: string; options: string[] }
          >();
          for (const fp of positions) {
            if (!fp.groupKey || fp.fieldType !== "checkbox") continue;
            const entry = groupsForTpl.get(fp.groupKey) ?? {
              label: fp.groupLabel ?? fp.groupKey,
              options: [],
            };
            groupsForTpl.set(fp.groupKey, entry);
            // Adopt a label from any member that has one.
            if (fp.groupLabel && !entry.label) entry.label = fp.groupLabel;
            if (fp.groupLabel) entry.label = fp.groupLabel;
            const optVal = (fp.optionValue ?? "").trim();
            if (optVal && !entry.options.includes(optVal)) {
              entry.options.push(optVal);
            }
          }
          for (const [groupKey, { label, options }] of groupsForTpl) {
            if (options.length === 0) continue;
            templatesForField(groupKey).add(tplKey);
            typeOverrides.set(groupKey, "select");
            if (!seenExtras.has(groupKey)) {
              seenExtras.add(groupKey);
              extras.push({
                key: groupKey,
                label,
                fieldType: "select",
                options,
              });
            }
          }
        }

        if (dbTemplate?.extractedFields) {
          for (const f of dbTemplate.extractedFields) {
            if (hiddenGallagherFields.has(f.key)) continue;
            if (!documentKeys.has(f.key)) continue;
            templatesForField(f.key).add(tplKey);
          }
        }

        // Scan selected variant snippet contentHtml for field placeholders
        // so fields that live only in snippet content appear in the wizard.
        // Skipped for PDF-overlay templates (MOR-790) — their templateHtml is
        // an extraction artefact and never rendered, so keys found in it
        // would surface as ghost fields with no effect on the output.
        if (dbTemplate?.templateHtml && !isPdfFillTemplate) {
          for (const k of collectSnippetFieldKeys(
            tplKey,
            dbTemplate.templateHtml as string,
          )) {
            templatesForField(k).add(tplKey);
          }
          // Contact field keys (contact_job_*, contact_firm_*) may be missing
          // from extractedFields if the template was saved before the backend
          // learned to persist them. Scan the HTML directly as a fallback.
          for (const k of collectPlaceholderKeysFromStoredTemplate(
            dbTemplate.templateHtml as string,
          )) {
            if (isContactFieldKey(k) && !fieldToTpls.has(k)) {
              templatesForField(k).add(tplKey);
            }
          }
          // MOR-2314 — body-only manual placeholders. A key inserted into the
          // letter body after extraction (e.g. `{{accountants_initials}}` in
          // an "Our ref:" line) has no `extractedFields` row — the template
          // save mutation only registers canonical, contact and custom keys —
          // so the loop above never sees it and the input was missing until
          // the document had been generated and opened in Edit Fields (which
          // scans the body directly). `collectManualBodyFieldKeys` scans the
          // CURRENT body only — never the `extractedContent` fallback — so a
          // placeholder the user deleted is not resurrected.
          for (const k of collectManualBodyFieldKeys(dbTemplate, {
            isPdfFillTemplate,
            hiddenKeys: hiddenGallagherFields,
          })) {
            templatesForField(k).add(tplKey);
          }
        }

        if (extras.length > 0) tplExtras.set(tplKey, extras);
      }

      return {
        fieldToTemplates: fieldToTpls,
        templateExtraFields: tplExtras,
        fieldTypeOverrides: typeOverrides,
      };
    }, [
      selectedKeys,
      templates,
      allTemplateTypes,
      collectSnippetFieldKeys,
      lookupField,
    ]);

  // Partition canonical fields into common vs form-only (per-document).
  // Fields from DEFAULT_CATEGORIES (practitioner, firm, contact, etc.) are
  // always treated as common so they stay visible and editable even when
  // only one template uses them or no preset is configured.
  const { commonFieldKeys, formOnlyByTemplate } = useMemo(() => {
    const defaultCategoryFieldKeys = new Set<string>();
    for (const cat of FIELD_CATEGORIES) {
      if (
        DEFAULT_CATEGORIES.has(cat.id) &&
        !TEMPLATE_SPECIFIC_CATEGORIES.has(cat.id)
      ) {
        for (const f of cat.fields) defaultCategoryFieldKeys.add(f.key);
      }
    }
    // Custom fields follow the standard "used by 2+ templates" rule — they
    // only become common when actually shared. A custom field used by a
    // single template stays inside that template's card to keep context tight.

    // A shared key is only promoted to Common Fields when it has a canonical
    // or org custom definition — the Common Fields card renders from category
    // definitions, so a body-only manual placeholder shared by two templates
    // would otherwise vanish from every card (MOR-2314).
    return partitionWizardFieldKeys(fieldToTemplates, {
      hasDefinition: (fieldKey) => lookupField(fieldKey) !== undefined,
      isDefaultCategoryKey: (fieldKey) =>
        defaultCategoryFieldKeys.has(fieldKey),
      isAlwaysPerDocument: (fieldKey) => ALWAYS_PER_DOCUMENT.has(fieldKey),
    });
  }, [fieldToTemplates, lookupField]);

  // Group common fields by category with inline preset selectors.
  // Practitioner is split into lead/secondary so each can pick a different preset.
  type CommonFieldGroup = {
    category: (typeof FIELD_CATEGORIES)[number];
    fields: CanonicalField[];
    presetCategoryId: string;
    subLabel?: string;
  };

  const commonFieldsWithPresets = useMemo((): CommonFieldGroup[] => {
    const groups: CommonFieldGroup[] = [];
    for (const cat of FIELD_CATEGORIES) {
      if (TEMPLATE_SPECIFIC_CATEGORIES.has(cat.id)) continue;

      const catPresets = presetsByCategory.get(cat.id);
      const hasPresets = !!(
        catPresets &&
        catPresets.length > 0 &&
        DEFAULT_CATEGORIES.has(cat.id)
      );

      const fields = cat.fields.filter(
        (f) => commonFieldKeys.has(f.key) && f.fieldType !== "signature",
      );
      if (fields.length > 0 || hasPresets) {
        groups.push({ category: cat, fields, presetCategoryId: cat.id });
      }
    }

    // Custom field categories surface in Common Fields ONLY when their
    // fields are shared across multiple selected templates. Single-template
    // and orphan-category preset selectors (with no template usage) get
    // attached to per-document cards instead — see perDocumentCards below.
    for (const customCat of customCategoriesGrouped.values()) {
      const sharedFields = customCat.fields.filter(
        (f) => commonFieldKeys.has(f.key) && f.fieldType !== "signature",
      );
      if (sharedFields.length > 0) {
        groups.push({
          category: {
            id: customCat.id,
            label: customCat.label,
            description: "",
            fields: customCat.fields,
          },
          fields: sharedFields,
          presetCategoryId: customCat.id,
        });
      }
    }

    return groups;
  }, [commonFieldKeys, presetsByCategory, customCategoriesGrouped]);

  const perDocumentCards = useMemo(() => {
    const ordered: Array<{
      type: (typeof allTemplateTypes)[number];
      canonicalFields: CanonicalField[];
      extras: Array<{
        key: string;
        label: string;
        fieldType: string;
        description?: string;
        options?: string[];
      }>;
      customCategoryIds: string[];
    }> = [];

    for (const type of allTemplateTypes) {
      if (!selectedKeys.has(type.key)) continue;

      const onlyKeys = formOnlyByTemplate.get(type.key);
      const extras = templateExtraFields.get(type.key) ?? [];

      // Build a quick lookup of this template's extractedFields metadata so
      // we can fall back to it when the org's customFieldDefinitions row
      // has been removed (or hasn't loaded yet) — keeps the field visible
      // with its label/type instead of disappearing.
      const dbTemplate = templates?.find((t) => t.templateKey === type.key);
      const extractedByKey = new Map<
        string,
        { label: string; fieldType: string; description?: string }
      >();
      if (dbTemplate?.extractedFields) {
        for (const f of dbTemplate.extractedFields) {
          extractedByKey.set(f.key, {
            label: f.label,
            fieldType: f.fieldType,
            description: f.description,
          });
        }
      }

      // Keys this card already renders as extras (checkbox-group bundles)
      // must not ALSO fall through to a generic text input.
      const extraKeys = new Set(extras.map((f) => f.key));

      const canonicalFields: CanonicalField[] = [];
      const customCategorySet = new Set<string>();
      if (onlyKeys) {
        for (const fieldKey of onlyKeys) {
          // Canonical / org custom definition first (template wording
          // overlaid), then the template's own extractedFields row, then a
          // contact-binding label, then — MOR-2314 — the same title-cased
          // text fallback the day-one Edit Fields panel applies to any other
          // manual placeholder, so the field reads identically before and
          // after generation.
          const meta = resolvePerDocumentFieldMeta(fieldKey, {
            definition: lookupField(fieldKey),
            extracted: extractedByKey.get(fieldKey),
            extraKeys,
          });
          if (meta && meta.fieldType !== "signature") {
            const overrideType = fieldTypeOverrides.get(fieldKey);
            const baseResolved = overrideType
              ? {
                  ...meta,
                  fieldType: overrideType as CanonicalField["fieldType"],
                }
              : meta;
            // Gallagher card: show the AJG portal's own field names ("5. Type
            // of Appointment", "9. Principal Risk Address", …) instead of the
            // generic Morgan canonical labels, so the input form matches the
            // preview, the portal-fields config, and the live portal. Keep the
            // canonical helper text when Gallagher has none of its own.
            const gallagher =
              type.key === "form_gallagher"
                ? GALLAGHER_LABELS_BY_CANONICAL_KEY[fieldKey]
                : undefined;
            const resolved = gallagher
              ? {
                  ...baseResolved,
                  label: gallagher.label,
                  description:
                    gallagher.description ?? baseResolved.description,
                }
              : baseResolved;
            canonicalFields.push(resolved);
            const catId = customFieldCategoryByKey.get(fieldKey);
            if (catId) customCategorySet.add(catId);
          }
        }
      }

      // Orphan custom categories (presets exist but no template field
      // references them) are no longer attached — they're irrelevant to
      // the document and clutter the card.

      const filteredExtras = extras.filter((f) => f.fieldType !== "signature");
      // Always emit a card for every selected non-third-party-form template so
      // the user has a place to pick a letterhead even when the template only
      // uses common fields (e.g. Letter to Icare). For form-only templates
      // (PDFs with no merge fields), the card body is empty and just hosts the
      // letterhead picker in the header.
      const includeForLetterhead = !type.thirdPartyForm;
      if (
        canonicalFields.length > 0 ||
        filteredExtras.length > 0 ||
        customCategorySet.size > 0 ||
        includeForLetterhead
      ) {
        ordered.push({
          type,
          canonicalFields,
          extras: filteredExtras,
          customCategoryIds: Array.from(customCategorySet),
        });
      }
    }

    return ordered;
  }, [
    selectedKeys,
    formOnlyByTemplate,
    templateExtraFields,
    fieldTypeOverrides,
    allTemplateTypes,
    lookupField,
    customFieldCategoryByKey,
    templates,
  ]);

  // ── Step 2 review helpers ───────────────────────────────────────────────
  // Reverse map: field key → { categoryId, label } for both canonical
  // (FIELD_CATEGORIES) and custom (customCategoriesGrouped) fields. Used to
  // group a per-document card's fields into labelled sections.
  const fieldCategoryByKey = useMemo(() => {
    const map = new Map<string, { categoryId: string; label: string }>();
    for (const cat of FIELD_CATEGORIES) {
      for (const f of cat.fields) {
        map.set(f.key, { categoryId: cat.id, label: cat.label });
      }
    }
    for (const cat of customCategoriesGrouped.values()) {
      for (const f of cat.fields) {
        map.set(f.key, { categoryId: cat.id, label: cat.label });
      }
    }
    return map;
  }, [customCategoriesGrouped]);

  // Resolve a review field's provenance. Mirrors the inline logic used in the
  // Common Fields and per-document grids so counts and rendering stay in sync.
  const resolveReviewSource = useCallback(
    (fieldKey: string, tplDefault?: string): FieldReviewSource => {
      if (fieldOverrides[fieldKey] !== undefined) return "edited";
      // A firm house-style title for this appointment type beats the
      // job-derived one (MOR-2354): report it as the preset default.
      if (
        (fieldKey === "signatory_title" || fieldKey === "practitioner_title") &&
        scopedTitleOverrides[fieldKey]
      ) {
        return "default";
      }
      if (jobFields[fieldKey]) return "job";
      if (orgDefaults[fieldKey]) return "default";
      if (tplDefault) return "template";
      // A date the template derives from another date is supplied by the
      // template, not missing.
      if (calculatedFields[fieldKey]) return "template";
      return "empty";
    },
    [
      fieldOverrides,
      jobFields,
      orgDefaults,
      calculatedFields,
      scopedTitleOverrides,
    ],
  );

  // Case-insensitive label match against the current search box. Empty search
  // matches everything. Resolves the appointment-type-aware label first so a
  // search for e.g. "trustee" hits bankruptcy relabelled fields too.
  const matchesReviewSearch = useCallback(
    (field: { key: string; label: string }): boolean => {
      const q = reviewSearch.trim().toLowerCase();
      if (!q) return true;
      const label = resolveFieldLabel(
        field.key,
        field.label,
        job?.appointmentType,
      ).toLowerCase();
      return label.includes(q) || field.key.toLowerCase().includes(q);
    },
    [reviewSearch, job?.appointmentType],
  );

  // Group an ordered list of fields into labelled category sections, keeping
  // the FIELD_CATEGORIES order first, then custom categories, then a trailing
  // Conditional fields (MOR-885): per-template set of dependent field keys
  // whose controlling answer doesn't currently match, evaluated against the
  // live merged values + user edits so answering "Yes" reveals the
  // dependent field immediately. Mirrored by the PDF fill action, which
  // re-evaluates per document at fill time.
  const suppressedKeysByTemplate = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const effectiveValues: Record<string, string> = {
      ...mergedFields,
      ...fieldOverrides,
    };
    const templateRows = (templates ?? []) as Array<{
      templateKey: string;
      fieldConditions?: FieldConditionMap;
    }>;
    for (const t of templateRows) {
      const conditions = t.fieldConditions;
      if (!conditions || Object.keys(conditions).length === 0) continue;
      // MOR-1003 — a controlling question answered only by a template
      // checkbox-group default still counts as answered, so its dependent
      // fields surface here exactly as they will fill in the PDF.
      map.set(
        t.templateKey,
        resolveSuppressedFieldKeys(
          conditions,
          applyGroupDefaultsToValues(
            effectiveValues,
            templateGroupDefaultsByKey.get(t.templateKey) ?? {},
          ),
        ),
      );
    }
    return map;
  }, [templates, mergedFields, fieldOverrides, templateGroupDefaultsByKey]);

  // Raw per-template conditions, used by the review form to nest each
  // dependent field under the field that controls it so the link between a
  // question and the follow-ups it reveals is visible.
  const conditionsByTemplate = useMemo(() => {
    const map = new Map<string, FieldConditionMap>();
    const templateRows = (templates ?? []) as Array<{
      templateKey: string;
      fieldConditions?: FieldConditionMap;
    }>;
    for (const t of templateRows) {
      if (t.fieldConditions && Object.keys(t.fieldConditions).length > 0) {
        map.set(t.templateKey, t.fieldConditions);
      }
    }
    return map;
  }, [templates]);

  // "Form-specific" bucket for anything without a known category (e.g. PDF
  // extras). Preserves each field's original relative order within its group.
  const groupFieldsByCategory = useCallback(
    <T extends { key: string }>(
      fields: T[],
    ): Array<{ categoryId: string; label: string; fields: T[] }> => {
      const byCat = new Map<
        string,
        { categoryId: string; label: string; fields: T[] }
      >();
      const knownCategoryIds = new Set(FIELD_CATEGORIES.map((c) => c.id));
      const customCategoryIds = [...customCategoriesGrouped.values()]
        .map((cat) => cat.id)
        .filter((id) => !knownCategoryIds.has(id));
      const FORM_SPECIFIC = "__form_specific__";
      const orderIndex = new Map(
        [
          ...FIELD_CATEGORIES.map((c) => c.id),
          ...customCategoryIds,
          FORM_SPECIFIC,
        ].map((id, i) => [id, i]),
      );

      for (const field of fields) {
        const meta = fieldCategoryByKey.get(field.key);
        const categoryId = meta?.categoryId ?? FORM_SPECIFIC;
        const label = meta
          ? resolveCategoryLabel(
              meta.categoryId,
              meta.label,
              job?.appointmentType,
            )
          : "Form-specific";
        const group = byCat.get(categoryId) ?? {
          categoryId,
          label,
          fields: [],
        };
        byCat.set(categoryId, group);
        group.fields.push(field);
      }

      return Array.from(byCat.values()).sort(
        (a, b) =>
          (orderIndex.get(a.categoryId) ?? 999) -
          (orderIndex.get(b.categoryId) ?? 999),
      );
    },
    [fieldCategoryByKey, customCategoriesGrouped, job?.appointmentType],
  );

  // Per-section field statistics (total + needs-input) for the Common Fields
  // card and each per-document card, plus grand totals. Drives the progress
  // bar, the tab count badges, and the auto-collapse heuristic. Mirrors the
  // visibility rules applied in the render (contact/fan-out fields excluded).
  const reviewFieldStats = useMemo(() => {
    const perCard = new Map<string, { total: number; needsInput: number }>();
    const commonFields = commonFieldsWithPresets.flatMap(
      (group) => group.fields,
    );
    const commonTotal = commonFields.length;
    const commonNeedsInput = commonFields.filter(
      (field) => resolveReviewSource(field.key) === "empty",
    ).length;

    for (const card of perDocumentCards) {
      const tplDefaults = templateDefaultsByKey.get(card.type.key) ?? {};
      const fanOutContacts = matchingContactsByTemplate.get(card.type.key);
      const ticked = contactSelectionsByTemplate.get(card.type.key);
      const isFanOut =
        (fanOutContacts?.filter((c) => ticked?.has(c._id)).length ?? 0) > 0;
      const suppressedForCard = suppressedKeysByTemplate.get(card.type.key);
      const visibleCanonical = card.canonicalFields.filter(
        (f) =>
          !isContactFieldKey(f.key) &&
          !suppressedForCard?.has(f.key) &&
          (!isFanOut || !CONTACT_DERIVABLE_FIELD_KEYS.has(f.key)),
      );
      const visibleExtras = card.extras.filter(
        (f) => !isContactFieldKey(f.key) && !suppressedForCard?.has(f.key),
      );
      const cardGroupDefaults =
        templateGroupDefaultsByKey.get(card.type.key) ?? {};
      const total = visibleCanonical.length + visibleExtras.length;
      const canonicalNeedsInput = visibleCanonical.filter(
        (field) =>
          resolveReviewSource(field.key, tplDefaults[field.key]) === "empty",
      ).length;
      // A question answered by a template default is not "Needs Input" —
      // mirrors the extras rendering in the per-document card (MOR-1003).
      const extrasNeedsInput = visibleExtras.filter((field) => {
        const value =
          fieldOverrides[field.key] ?? mergedFields[field.key] ?? "";
        return !value && !cardGroupDefaults[field.key];
      }).length;
      const needsInput = canonicalNeedsInput + extrasNeedsInput;
      perCard.set(card.type.key, { total, needsInput });
    }

    const grandTotal =
      commonTotal +
      Array.from(perCard.values()).reduce((sum, s) => sum + s.total, 0);
    const grandNeedsInput =
      commonNeedsInput +
      Array.from(perCard.values()).reduce((sum, s) => sum + s.needsInput, 0);

    return {
      common: { total: commonTotal, needsInput: commonNeedsInput },
      perCard,
      grandTotal,
      grandNeedsInput,
    };
  }, [
    commonFieldsWithPresets,
    perDocumentCards,
    templateDefaultsByKey,
    templateGroupDefaultsByKey,
    matchingContactsByTemplate,
    contactSelectionsByTemplate,
    resolveReviewSource,
    fieldOverrides,
    mergedFields,
    suppressedKeysByTemplate,
  ]);

  // Auto-collapse fully-completed cards once per template-selection change,
  // but only when the run is large enough to warrant it. Keeps the review
  // step compact without overriding the user's manual toggles afterwards.
  // Common Fields is exempt: it applies to every document, so it stays open.
  useEffect(() => {
    if (step !== "review") return;
    const signature = perDocumentCards.map((c) => c.type.key).join("|");
    if (autoCollapseSignatureRef.current === signature) return;
    autoCollapseSignatureRef.current = signature;

    if (reviewFieldStats.grandTotal <= AUTO_COLLAPSE_FIELD_THRESHOLD) {
      setCollapsedCards(new Set());
      return;
    }
    const next = new Set<string>();
    for (const card of perDocumentCards) {
      const stats = reviewFieldStats.perCard.get(card.type.key);
      if (stats && stats.total > 0 && stats.needsInput === 0) {
        next.add(card.type.key);
      }
    }
    setCollapsedCards(next);
  }, [step, perDocumentCards, reviewFieldStats]);

  const toggleCardCollapsed = useCallback((key: string) => {
    setCollapsedCards((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleToggleTemplate = useCallback(
    (key: string) => {
      const isOn = selectedKeys.has(key);
      if (isOn) {
        // Turning off. If this document is only present because a selected
        // document attaches it, record a deliberate dismissal so it stays off
        // (the badge tells the user it won't be generated). Either way clear it
        // from the user's explicit picks.
        if (requiredDependencyKeys.has(key)) {
          setDismissedDependencyKeys((prev) => new Set(prev).add(key));
        }
        setUserSelectedKeys((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        setManuallyUnselectedKeys((prev) => new Set(prev).add(key));
      } else {
        // Turning on clears any prior unselect/dismissal so it sticks.
        setUserSelectedKeys((prev) => new Set(prev).add(key));
        // An explicit pick stops being an *automatic* one, so the phase-change
        // sweep below never takes it back with the old phase's suggestions.
        setAutoSelectedKeys((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        setManuallyUnselectedKeys((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        setDismissedDependencyKeys((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [selectedKeys, requiredDependencyKeys],
  );

  const allConfiguredSelected =
    configuredKeys.size > 0 &&
    [...configuredKeys].every((k) => selectedKeys.has(k));

  const handleSelectAll = useCallback(() => {
    setUserSelectedKeys(new Set(configuredKeys));
    setManuallyUnselectedKeys(new Set());
    setDismissedDependencyKeys(new Set());
    // Every key is now an explicit pick, so none of them is swept on a phase
    // change.
    setAutoSelectedKeys(new Set());
  }, [configuredKeys]);

  const handleUnselectAll = useCallback(() => {
    setUserSelectedKeys(new Set());
    setManuallyUnselectedKeys(new Set(configuredKeys));
    setAutoSelectedKeys(new Set());
    // Clearing dismissals here is what fixes MOR-678: after a bulk clear, the
    // next time a parent document is selected its attachment dependency is
    // pulled back in rather than staying permanently suppressed.
    setDismissedDependencyKeys(new Set());
    // The "Selected" view hides unselected templates, so after a bulk clear it
    // would render an empty list. Flip to "All" so the user still sees the pick
    // list they can re-select from.
    setShowAllTemplates(true);
  }, [configuredKeys]);

  const handleSelectSuggested = useCallback(() => {
    setUserSelectedKeys(new Set(tagMatchReasons.keys()));
    setManuallyUnselectedKeys((prev) => {
      const next = new Set(prev);
      for (const key of tagMatchReasons.keys()) next.delete(key);
      return next;
    });
    setDismissedDependencyKeys(new Set());
    setAutoSelectedKeys(new Set());
  }, [tagMatchReasons]);

  // Select / unselect every configured template within a single category.
  const handleToggleCategory = useCallback(
    (keys: string[]) => {
      const allSelected =
        keys.length > 0 && keys.every((k) => selectedKeys.has(k));
      setUserSelectedKeys((prev) => {
        const next = new Set(prev);
        for (const k of keys) {
          if (allSelected) next.delete(k);
          else next.add(k);
        }
        return next;
      });
      setManuallyUnselectedKeys((prev) => {
        const next = new Set(prev);
        for (const k of keys) {
          if (allSelected) next.add(k);
          else next.delete(k);
        }
        return next;
      });
      setDismissedDependencyKeys((prev) => {
        const next = new Set(prev);
        for (const k of keys) {
          if (allSelected) {
            if (requiredDependencyKeys.has(k)) next.add(k);
          } else {
            next.delete(k);
          }
        }
        return next;
      });
      // Toggling the category is an explicit action in either direction, so
      // none of its templates stays an automatic selection.
      setAutoSelectedKeys((prev) => {
        if (!keys.some((k) => prev.has(k))) return prev;
        const next = new Set(prev);
        for (const k of keys) next.delete(k);
        return next;
      });
    },
    [selectedKeys, requiredDependencyKeys],
  );

  // Tri-state for a category's header checkbox: only counts configured (i.e.
  // selectable) templates that are currently visible in the group.
  const getCategorySelection = (group: WizardCategory) => {
    const keys = group.types
      .filter((t) => configuredKeys.has(t.key))
      .map((t) => t.key);
    const selectedCount = keys.filter((k) => selectedKeys.has(k)).length;
    const checked: boolean | "indeterminate" =
      keys.length > 0 && selectedCount === keys.length
        ? true
        : selectedCount > 0
          ? "indeterminate"
          : false;
    return { keys, checked, disabled: keys.length === 0 };
  };

  const handleFieldChange = useCallback((key: string, value: string) => {
    setFieldOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  /**
   * Explicit reset (MOR-2342 QA-08): drop the analyst's edits for a preset
   * category so its preset / source values show again. This is the ONLY
   * path that discards a manual override — a preset or contact-source
   * switch never does.
   */
  const resetCategoryOverrides = useCallback((keys: ReadonlyArray<string>) => {
    setFieldOverrides((prev) => withoutKeys(prev, keys));
  }, []);

  const handleAttachmentCandidateChange = useCallback(
    (
      templateKey: string,
      slotKey: string,
      requirement: AttachmentRequirement,
      candidateId: string,
    ) => {
      const candidates =
        attachmentCandidatesByKey.get(requirement.attachmentKey) ?? [];
      const candidate = candidates.find(
        (item) => item.documentId === candidateId,
      );
      if (!candidate) return;
      setAttachmentSelections((prev) => ({
        ...prev,
        [templateKey]: {
          ...(prev[templateKey] ?? {}),
          [slotKey]: {
            name: requirement.name,
            description: requirement.description,
            required: requirement.required,
            attachmentKey: requirement.attachmentKey,
            sourceKind: "job_document",
            status: "resolved",
            documentCategory: candidate.category,
            documentId: candidate.documentId,
            storageId: candidate.storageId,
            fileName: candidate.fileName,
            fileType: candidate.fileType,
            fileSize: candidate.fileSize,
            resolvedAt: Date.now(),
          },
        },
      }));
    },
    [attachmentCandidatesByKey],
  );

  const handleGeneratedPrecedentChange = useCallback(
    (
      templateKey: string,
      slotKey: string,
      requirement: AttachmentRequirement,
      generatedTemplateKey: string,
    ) => {
      const generatedType = allTemplateTypes.find(
        (type) => type.key === generatedTemplateKey,
      );
      setAttachmentSelections((prev) => ({
        ...prev,
        [templateKey]: {
          ...(prev[templateKey] ?? {}),
          [slotKey]: {
            name: requirement.name,
            description: requirement.description,
            required: requirement.required,
            attachmentKey: requirement.attachmentKey,
            sourceKind: "generated_precedent",
            status: "resolved",
            generatedTemplateKey,
            fileName: generatedType?.name ?? generatedTemplateKey,
            resolvedAt: Date.now(),
          },
        },
      }));
    },
    [allTemplateTypes],
  );

  const handleSkipAttachment = useCallback(
    (
      templateKey: string,
      slotKey: string,
      requirement: AttachmentRequirement,
    ) => {
      setAttachmentSelections((prev) => ({
        ...prev,
        [templateKey]: {
          ...(prev[templateKey] ?? {}),
          [slotKey]: {
            name: requirement.name,
            description: requirement.description,
            required: requirement.required,
            attachmentKey: requirement.attachmentKey,
            sourceKind: "none",
            status: "skipped",
            documentCategory: requirement.documentCategory as
              DocumentCategory | undefined,
          },
        },
      }));
    },
    [],
  );

  // Clear an attachment back to its unset/missing baseline. Used both for
  // "Remove" (drop a user-selected document) and "Undo" (un-skip). The
  // resolution effect doesn't depend on attachmentSelections, so this reset
  // persists rather than being auto-re-picked.
  const handleResetAttachment = useCallback(
    (
      templateKey: string,
      slotKey: string,
      requirement: AttachmentRequirement,
    ) => {
      setAttachmentSelections((prev) => ({
        ...prev,
        [templateKey]: {
          ...(prev[templateKey] ?? {}),
          [slotKey]: {
            name: requirement.name,
            description: requirement.description,
            required: requirement.required,
            attachmentKey: requirement.attachmentKey,
            sourceKind: requirement.sourceKind || "none",
            status: "missing",
            documentCategory: requirement.documentCategory as
              DocumentCategory | undefined,
          },
        },
      }));
    },
    [],
  );

  const handleUploadAttachment = useCallback(
    (
      templateKey: string,
      slotKey: string,
      requirement: AttachmentRequirement,
      file: File | null,
    ) => {
      if (!file) return;
      pendingAttachmentUploadRef.current = {
        templateKey,
        slotKey,
        requirement,
      };
      startAttachmentUpload([file]);
    },
    [startAttachmentUpload],
  );

  const unresolvedRequiredAttachments = useMemo(() => {
    const missing: string[] = [];
    for (const [templateKey, requirements] of selectedTemplateAttachments) {
      for (const [index, requirement] of requirements.entries()) {
        const slotKey = buildAttachmentSlotKey(
          requirement.attachmentKey,
          index,
        );
        const selection = attachmentSelections[templateKey]?.[slotKey];
        if (requirement.required && selection?.status !== "resolved") {
          missing.push(`${templateKey}:${slotKey}`);
        }
      }
    }
    return missing;
  }, [selectedTemplateAttachments, attachmentSelections]);

  const runGenerate = useCallback(async () => {
    if (!orgId || !job) return;

    type Selection = {
      templateKey: string;
      name: string;
      category: string;
      isThirdPartyForm?: boolean;
      fieldValues: Record<string, string>;
      attachments: AttachmentSelection[];
      jobContactId?: Id<"jobContacts">;
      contactRoleKind?: ContactRole;
      contactNameSnapshot?: string;
      firmContactId?: Id<"firmContacts">;
      firmContactCategory?: FirmContactCategory;
      contactSourceLinkId?: string;
      contactSourceLinkKind?: ContactSourceKind;
      /** Resolved template collections (repeat/join/plural markup data). */
      collectionData?: Record<string, Array<Record<string, string>>>;
      firmReferenceContactIds?: Array<{
        category: FirmContactCategory;
        firmContactId: Id<"firmContacts">;
      }>;
      letterheadOverride?:
        | { kind: "none" }
        | { kind: "letterhead"; bundleName: string }
        | { kind: "variant"; variantId: Id<"letterheadVariants"> };
      variantSelections?: Record<string, string>;
      snippetSelections?: Record<string, Id<"variantSnippets">>;
    };

    const selections: Selection[] = [];

    for (const key of selectedKeys) {
      const type = allTemplateTypes.find((t) => t.key === key);
      const tplDefaults = templateDefaultsByKey.get(key) ?? {};
      const dbTemplate = templates?.find((t) => t.templateKey === key);

      // Firm-contact REFERENCES — resolve the user-picked (or auto-single)
      // firm contact per referenced category into `firmref_<category>_*`
      // values. These augment the document WITHOUT fanning it out, so they
      // sit in the base map every fan-out row inherits. User edits in the
      // review step (carried in `mergedFields`) still win — hence firmRef
      // is spread BEFORE `mergedFields`.
      const firmRefFields: Record<string, string> = {};
      const firmReferenceContactIds: Array<{
        category: FirmContactCategory;
        firmContactId: Id<"firmContacts">;
      }> = [];
      for (const category of firmRefCategoriesByTemplate.get(key) ?? []) {
        const contacts = firmContactsByCategory.get(category) ?? [];
        const pickedId =
          firmRefSelectionsByTemplate.get(key)?.get(category) ??
          (contacts.length === 1 ? contacts[0]._id : undefined);
        if (!pickedId) continue;
        const contact = contacts.find((c) => c._id === pickedId);
        if (!contact) continue;
        Object.assign(
          firmRefFields,
          resolveFirmReferenceFields(
            category,
            {
              displayName: contact.displayName,
              abn: contact.abn,
              acn: contact.acn,
              email: contact.email,
              phone: contact.phone,
              addresses: contact.addresses,
            },
            contact.customFields,
            (firmFieldDefsByCategory.get(category) ??
              []) as ContactCustomFieldDef[],
          ),
        );
        firmReferenceContactIds.push({ category, firmContactId: pickedId });
      }

      // MOR-1003 — template checkbox-group defaults ("Selected by default")
      // fill any question left unanswered in the review step, matching what
      // the review form displayed. Baking them into fieldValues makes the
      // default answer visible and editable in the day-one Edit Fields
      // panel, and lets "Show only when…" conditions treat it as answered
      // at PDF fill time. Explicit answers always win (the helper only
      // fills missing/blank keys).
      const baseFieldValues: Record<string, string> =
        applyGroupDefaultsToValues(
          {
            ...tplDefaults,
            ...firmRefFields,
            ...mergedFields,
          },
          templateGroupDefaultsByKey.get(key) ?? {},
        );

      // Strip CUSTOM-FIELD spillover. orgDefaults dumps preset values for
      // every custom category (DOGS, PPSR, etc.) into mergedFields regardless
      // of whether this template references those fields. Without this
      // filter, every generated precedent carries unrelated custom-field
      // values that then surface in the day-one "Edit Fields" sidebar.
      //
      // Only custom-field keys are filtered. Canonical fields, PDF field
      // name aliases, signature, recipient_*, etc. all pass through
      // untouched. A custom-field key is kept if it's referenced by any of
      // this template's structured artifacts (hardcoded fields, PDF
      // mappings, visual field positions, or extracted letter fields).
      const spilloverFilteredFieldValues: Record<string, string> =
        customFieldCategoryByKey.size > 0
          ? (() => {
              const referencedKeys = new Set<string>();
              if (type?.hardcodedFields) {
                for (const f of type.hardcodedFields) {
                  referencedKeys.add(f.key);
                  if (f.canonicalKey) referencedKeys.add(f.canonicalKey);
                }
              }
              if (dbTemplate?.pdfFieldMappings) {
                for (const m of dbTemplate.pdfFieldMappings as Array<{
                  pdfFieldName: string;
                  canonicalKey?: string;
                }>) {
                  if (m.canonicalKey) referencedKeys.add(m.canonicalKey);
                  referencedKeys.add(m.pdfFieldName);
                }
              }
              if (dbTemplate?.fieldPositions) {
                for (const fp of dbTemplate.fieldPositions as Array<{
                  canonicalKey?: string;
                  groupKey?: string;
                }>) {
                  if (fp.canonicalKey) referencedKeys.add(fp.canonicalKey);
                  // Checkbox groups reference their answer via `groupKey`
                  // (which may be a custom field for field-linked groups) —
                  // without this a custom-field-linked group's answer is
                  // stripped as "unreferenced custom-field spillover".
                  if (fp.groupKey) referencedKeys.add(fp.groupKey);
                }
              }
              if (dbTemplate?.extractedFields) {
                for (const f of dbTemplate.extractedFields as Array<{
                  key: string;
                  canonicalKey?: string;
                }>) {
                  referencedKeys.add(f.key);
                  if (f.canonicalKey) referencedKeys.add(f.canonicalKey);
                }
              }

              // Letter templates often carry placeholders in `templateHtml`
              // (TipTap chips) before `extractedFields` is refreshed —
              // without this, the custom-field spillover filter strips keys
              // the document actually uses.
              for (const k of collectPlaceholderKeysFromStoredTemplate(
                dbTemplate?.templateHtml ?? "",
                dbTemplate?.extractedContent,
              )) {
                referencedKeys.add(k);
              }

              // Include fields from variant snippet contentHtml so custom
              // fields referenced only in snippets survive the spillover
              // filter.
              for (const k of collectSnippetFieldKeys(
                key,
                dbTemplate?.templateHtml ?? "",
              )) {
                referencedKeys.add(k);
              }

              return Object.fromEntries(
                Object.entries(baseFieldValues).filter(([k]) => {
                  const isCustom = customFieldCategoryByKey.has(k);
                  if (!isCustom) return true;
                  return referencedKeys.has(k);
                }),
              );
            })()
          : baseFieldValues;

      if (selectedSignatory?.signatureImageUrl) {
        spilloverFilteredFieldValues.signature = buildSignatureImageToken(
          selectedSignatory.signatureImageUrl,
          selectedSignatory.signatureScale,
        );
      } else if (selectedSignatory?.signatureText) {
        spilloverFilteredFieldValues.signature =
          selectedSignatory.signatureText;
      }

      const fieldValues: Record<string, string> =
        key === "form_gallagher"
          ? resolveGallagherFieldValues({
              baseValues: spilloverFilteredFieldValues,
              overrides: fieldOverrides,
              config: dbTemplate?.fieldConfig as
                GallagherFieldConfig[] | undefined,
            })
          : spilloverFilteredFieldValues;

      // Helper: apply contact-derived `recipient_*` overrides + PDF-name
      // bridging on top of the per-template base field map. Used for both
      // the single-row path and each row of a contact-role fan-out so the
      // PDF-name aliasing happens *after* contact merge (so e.g. a PDF
      // form with `recipient_name`-mapped fields still picks up the
      // contact's name).
      const finaliseFields = (
        base: Record<string, string>,
        contactFields?: Record<string, string>,
      ): Record<string, string> => {
        // Contact values sit between job and userOverrides per
        // `mergeFieldValues`; here we already have mergedFields so we
        // inject contactFields then re-apply user edits to keep the
        // documented priority (orgDefaults → job → contact → user).
        const merged = contactFields
          ? { ...base, ...contactFields, ...fieldOverrides }
          : { ...base };
        // Fill any date field this template derives from another date. Runs
        // after every other layer so an entered value always wins, and before
        // PDF-name bridging below so split date boxes pick the value up too.
        const templateCalcs = dbTemplate?.dateCalcs as
          Record<string, DateCalc> | undefined;
        for (const [fieldKey, derived] of Object.entries(
          resolveTemplateDateCalcs(templateCalcs, merged, dateCalcContext),
        )) {
          merged[fieldKey] = derived;
        }
        if (dbTemplate?.pdfFieldMappings) {
          for (const m of dbTemplate.pdfFieldMappings as Array<{
            pdfFieldName: string;
            canonicalKey?: string;
          }>) {
            if (m.canonicalKey && merged[m.canonicalKey] !== undefined) {
              merged[m.pdfFieldName] = merged[m.canonicalKey];
            }
          }
        }
        return merged;
      };

      const matchingContacts = matchingContactsByTemplate.get(key);
      const tickedContactIds = contactSelectionsByTemplate.get(key);
      const fanOutContacts = matchingContacts?.filter((c) =>
        tickedContactIds?.has(c._id),
      );

      // Wizard-level letterhead override; stamped onto the jobPrecedents
      // row. `null` (user explicitly cleared) and undefined (untouched)
      // both translate to "no per-document override" — at render time
      // the resolver still consults the template default + firm default.
      const wizardOverride = letterheadOverrides[key];

      // Per-doc variant overrides selected in this wizard step. Empty
      // record collapses to undefined so the row doesn't carry an empty
      // override that the validator treats as "explicitly empty".
      const wizardVariants = variantOverrides[key];
      const variantSelectionsForRow =
        wizardVariants && Object.keys(wizardVariants).length > 0
          ? wizardVariants
          : undefined;

      const wizardSnippets = snippetOverrides[key];
      const snippetSelectionsForRow =
        wizardSnippets && Object.keys(wizardSnippets).length > 0
          ? (wizardSnippets as Record<string, Id<"variantSnippets">>)
          : undefined;

      // Data-driven variant selection (MOR-886): groups referenced by this
      // template may bind their active option to a field value. Resolve the
      // binding against each document's final field values and stamp the
      // result into the row's variantSelections, so the per-doc choice wins
      // over the job-wide default at render time. Explicit wizard picks
      // always take precedence over the binding.
      const templateVariantSlugs =
        boundVariantGroups.length > 0
          ? [
              ...extractVariantGroupSlugs(
                dbTemplate?.templateHtml as string | undefined,
              ),
            ]
          : [];
      const resolveRowVariantSelections = (
        docFields: Record<string, string>,
      ): Record<string, string> | undefined => {
        const bound = resolveBoundVariantSelections(
          boundVariantGroups,
          templateVariantSlugs,
          docFields,
          wizardVariants,
        );
        const combined = { ...bound, ...(variantSelectionsForRow ?? {}) };
        return Object.keys(combined).length > 0 ? combined : undefined;
      };

      const baseSelectionFields = {
        templateKey: key,
        name: type?.name ?? key,
        category: type?.category ?? "day_one",
        isThirdPartyForm: type?.thirdPartyForm,
        // MOR-2358 — stamp the run's manual edits and contact person so a
        // reopened wizard restores exactly this state.
        fieldOverrides,
        contactPerson: effectiveContactPerson,
        attachments: Object.values(attachmentSelections[key] ?? {}),
        letterheadOverride: wizardOverride ?? undefined,
        variantSelections: variantSelectionsForRow,
        snippetSelections: snippetSelectionsForRow,
        firmReferenceContactIds:
          firmReferenceContactIds.length > 0
            ? firmReferenceContactIds
            : undefined,
      } as const;

      const sourceFanOut = sourceFanOutByTemplate.get(key);

      if (fanOutContacts && fanOutContacts.length > 0) {
        // Fan-out: emit one selection per ticked contact (job or firm).
        // Each row gets two layers of contact field values:
        //   1. legacy `recipient_*` (+ role aliases) — keeps templates
        //      written before explicit contact fields auto-filling.
        //   2. explicit `contact:<domain>:<role>:<part>` keys — the new
        //      Job Contact / Firm Contact field sources.
        for (const contact of fanOutContacts) {
          const baseContactFields: Record<string, string> = {
            ...mapContactToCanonicalFields(contact.data, contact.role),
            ...resolveContactFields(contact.data, contact.domain),
            // Firm-contact custom fields (`contact_firm_cf_*`), formatted
            // per their category's definitions.
            ...(contact.domain === "firm" && contact.category
              ? resolveContactCustomFields(
                  contact.customFields,
                  (firmFieldDefsByCategory.get(contact.category) ??
                    []) as ContactCustomFieldDef[],
                  "firm",
                )
              : {}),
          };

          // Second-level fan-out: when the template body references any
          // `contact_job_<kind>_*` source field AND this job contact
          // has matching source links, emit one selection PER LINK
          // (with `contact_job_<kind>_*` values resolved from that
          // specific source row). The per-row override (if set) wins
          // over the natural default link order — see
          // `sourceLinkOverrides` for the override key shape.
          const contactSourceRows =
            contact.domain === "job"
              ? (sourceFanOut?.get(contact._id) ?? null)
              : null;

          if (contactSourceRows && contactSourceRows.length > 0) {
            contactSourceRows.forEach((natural, rowIndex) => {
              const overrideKey = buildSourceFanOutRowKey(
                key,
                contact._id,
                rowIndex,
              );
              const override = sourceLinkOverrides.get(overrideKey);
              const effective = override
                ? // Re-find the row payload that the user picked. Falls
                  // back to the natural assignment if the override no
                  // longer matches (e.g. the source row was deleted).
                  (contactSourceRows.find(
                    (r) =>
                      r.sourceKind === override.sourceKind &&
                      r.link?.sourceId === override.sourceId,
                  ) ?? natural)
                : natural;

              const sourceFields: Record<string, string> = effective.row
                ? resolveContactSourceFields(
                    effective.sourceKind,
                    effective.row,
                    "job",
                    effective.link?.sourceId,
                  )
                : {};

              // Per-row manual overrides win over the resolved source
              // values — the user may have edited a PPSR number or
              // owed amount in the wizard's editable source cards.
              const fieldOverridesForRow =
                sourceFieldOverrides.get(overrideKey) ?? {};

              const rowFieldValues = finaliseFields(fieldValues, {
                ...baseContactFields,
                ...sourceFields,
                ...fieldOverridesForRow,
              });
              selections.push({
                ...baseSelectionFields,
                fieldValues: rowFieldValues,
                variantSelections: resolveRowVariantSelections(rowFieldValues),
                contactNameSnapshot: contact.displayName,
                jobContactId: contact._id as Id<"jobContacts">,
                // Empty for custom-role (`jcr:*`) matches — no built-in role.
                contactRoleKind: contact.role
                  ? (contact.role as ContactRole)
                  : undefined,
                contactSourceLinkId: effective.link?.sourceId,
                contactSourceLinkKind: effective.link
                  ? effective.sourceKind
                  : undefined,
              });
            });
            continue;
          }

          const contactRowFieldValues = finaliseFields(
            fieldValues,
            baseContactFields,
          );
          selections.push({
            ...baseSelectionFields,
            fieldValues: contactRowFieldValues,
            variantSelections: resolveRowVariantSelections(
              contactRowFieldValues,
            ),
            contactNameSnapshot: contact.displayName,
            ...(contact.domain === "job"
              ? {
                  jobContactId: contact._id as Id<"jobContacts">,
                  // Empty for custom-role (`jcr:*`) matches — no built-in role.
                  contactRoleKind: contact.role
                    ? (contact.role as ContactRole)
                    : undefined,
                }
              : {
                  firmContactId: contact._id as Id<"firmContacts">,
                  firmContactCategory: contact.role as FirmContactCategory,
                }),
          });
        }
      } else {
        const singleRowFieldValues = finaliseFields(fieldValues);
        selections.push({
          ...baseSelectionFields,
          fieldValues: singleRowFieldValues,
          variantSelections: resolveRowVariantSelections(singleRowFieldValues),
        });
      }
    }

    // Resolve template collections (repeat blocks / inline joins / plural
    // tokens / count conditions) for every selection whose template body
    // carries collection markup. Cached per (recipient × key-set) so a
    // template fanning out across N contacts costs at most N queries and
    // recipient-independent templates cost one.
    const collectionKeysByTemplate = new Map<string, string[]>();
    const collectionCache = new Map<
      string,
      Record<string, Array<Record<string, string>>>
    >();
    for (const sel of selections) {
      const cachedKeys = collectionKeysByTemplate.get(sel.templateKey);
      const keys =
        cachedKeys ??
        (() => {
          const tpl = templates?.find((t) => t.templateKey === sel.templateKey);
          const html = (tpl?.templateHtml as string | undefined) ?? "";
          return hasCollectionMarkup(html)
            ? [...collectCollectionKeys(html)]
            : [];
        })();
      if (!cachedKeys) collectionKeysByTemplate.set(sel.templateKey, keys);
      if (keys.length === 0) continue;
      const cacheKey = `${sel.jobContactId ?? ""}|${[...keys].sort().join(",")}`;
      const cachedData = collectionCache.get(cacheKey);
      const data =
        cachedData ??
        (await convex.query(
          api.jobs.precedents.collections.resolveCollectionsForRecipient,
          {
            jobId: jobId as Id<"jobs">,
            jobContactId: sel.jobContactId,
            collectionKeys: keys,
            // Run-time signer pick for repeat-signatory blocks; null =
            // everyone with a saved signature.
            signatoryUserIds: pickedSignatoryIds
              ? ([...pickedSignatoryIds] as Id<"users">[])
              : undefined,
          },
        ));
      if (!cachedData) collectionCache.set(cacheKey, data);
      sel.collectionData = data;
    }

    try {
      if (reselect) {
        await deleteAllForJob({ jobId: jobId as Id<"jobs"> });
      }
      await generatePrecedents({
        jobId: jobId as Id<"jobs">,
        orgId: orgId as Id<"organizations">,
        selections,
      });
      toast.success(
        `Generated ${selections.length} document${selections.length > 1 ? "s" : ""}`,
      );
      navigate({
        to: "/dashboard/jobs/$jobId/day-one",
        params: { jobId },
      });
    } catch (err) {
      const rawMsg = err instanceof Error ? err.message : String(err);
      if (/cannot modify|permission|viewer/i.test(rawMsg)) {
        toast.error(
          "You don't have permission to modify this job's documents. Ask a team member with editor access to update your role.",
        );
      } else {
        toast.error(extractErrorMessage(err, "Failed to generate documents"));
      }
    }
  }, [
    orgId,
    job,
    selectedKeys,
    mergedFields,
    fieldOverrides,
    dateCalcContext,
    selectedSignatory,
    templateDefaultsByKey,
    templateGroupDefaultsByKey,
    allTemplateTypes,
    templates,
    attachmentSelections,
    letterheadOverrides,
    variantOverrides,
    snippetOverrides,
    generatePrecedents,
    deleteAllForJob,
    reselect,
    jobId,
    navigate,
    customFieldCategoryByKey,
    matchingContactsByTemplate,
    contactSelectionsByTemplate,
    collectSnippetFieldKeys,
    sourceFanOutByTemplate,
    sourceLinkOverrides,
    sourceFieldOverrides,
    buildSourceFanOutRowKey,
    firmFieldDefsByCategory,
    firmRefCategoriesByTemplate,
    firmRefSelectionsByTemplate,
    firmContactsByCategory,
    convex,
    pickedSignatoryIds,
    boundVariantGroups,
    effectiveContactPerson,
  ]);

  // Empty referenced contact fields across every selected fan-out template.
  // Uses the same helper as the per-template badge so the two never disagree.
  const computeAllMissingContactFields = useCallback(() => {
    const out: Array<{ displayName: string; part: ContactFieldPart }> = [];
    const seen = new Set<string>();
    for (const key of selectedKeys) {
      const tpl = templates?.find((t) => t.templateKey === key);
      if (!tpl) continue;
      const placeholderKeys = collectPlaceholderKeysFromStoredTemplate(
        tpl.templateHtml ?? "",
        tpl.extractedContent,
      );
      const parts = Array.from(placeholderKeys)
        .map((k) => parseContactFieldKey(k)?.part)
        .filter((p): p is ContactFieldPart => p != null);
      if (parts.length === 0) continue;
      const ticked = (matchingContactsByTemplate.get(key) ?? []).filter((c) =>
        contactSelectionsByTemplate.get(key)?.has(c._id),
      );
      for (const m of missingContactFields(ticked, parts)) {
        const dedupeKey = `${m.contactId}::${m.part}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        out.push({ displayName: m.displayName, part: m.part });
      }
    }
    return out;
  }, [
    selectedKeys,
    templates,
    matchingContactsByTemplate,
    contactSelectionsByTemplate,
  ]);

  const [missingConfirmOpen, setMissingConfirmOpen] = useState(false);

  const handleGenerate = useCallback(() => {
    if (computeAllMissingContactFields().length > 0) {
      setMissingConfirmOpen(true);
      return;
    }
    void runGenerate();
  }, [computeAllMissingContactFields, runGenerate]);

  const handleSkip = useCallback(() => {
    navigate({ to: "/dashboard/jobs/$jobId/plan", params: { jobId } });
  }, [navigate, jobId]);

  if (jobLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (!job) return null;

  // Grid-view card for a single template. Shared by top-level principals and
  // nested attachments so an attachment renders with its full selection UI
  // (checkbox, fan-out, firm-ref) directly beneath the principal that pulls
  // it in — giving the physical proximity a flat grid can't.
  const renderGridCard = (
    type: WizardCategory["types"][number],
    nestedAttachments?: WizardCategory["types"][number][],
    isNested?: boolean,
  ) => {
    const configured = configuredKeys.has(type.key);
    const selected = selectedKeys.has(type.key);
    const reasons = tagMatchReasons.get(type.key);
    const matchingContacts = matchingContactsByTemplate.get(type.key);
    const fanOut = selected && matchingContacts && matchingContacts.length > 0;
    const firmRefCats = selected
      ? firmRefCategoriesByTemplate.get(type.key)
      : undefined;
    return (
      <div
        key={type.key}
        className={cn(
          "rounded-lg border p-3 transition",
          !configured && "opacity-40",
          selected
            ? "border-primary/30 bg-primary/5"
            : "border-border/50 hover:border-border/80",
        )}
      >
        <label
          className={cn(
            "flex items-start gap-3",
            !configured ? "cursor-not-allowed" : "cursor-pointer",
          )}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={() => configured && handleToggleTemplate(type.key)}
            disabled={!configured}
            className="mt-0.5"
          />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-medium text-primary/80">
                {type.shortName}
              </span>
              {reasons && reasons.length === 0 && (
                <Badge
                  variant="secondary"
                  className="px-1 py-0 text-[8px] leading-tight"
                >
                  Suggested
                </Badge>
              )}
              {reasons?.map((reason, idx) => (
                <MatchReasonBadge
                  key={`${reason.kind}:${idx}`}
                  reason={reason}
                  tagGroups={tagGroups}
                />
              ))}
              {(() => {
                // Nested cards live under a drop-down already labelled with
                // the principal, so the per-card "Attachment of …" badge is
                // redundant there — only show it on top-level cards.
                if (isNested) return null;
                const parents = attachmentParentNamesByKey.get(type.key);
                if (!parents || parents.length === 0) return null;
                const autoIncluded =
                  requiredDependencyKeys.has(type.key) &&
                  !dismissedDependencyKeys.has(type.key);
                return (
                  <Badge
                    color="blue"
                    size="sm"
                    className="gap-1"
                    title={
                      autoIncluded
                        ? `Auto-included because ${parents.join(", ")} attach${parents.length === 1 ? "es" : ""} it. Unselect to skip generating it.`
                        : `This is an attachment of ${parents.join(", ")} — it is generated inside its principal document${parents.length === 1 ? "" : "s"} rather than on its own.`
                    }
                  >
                    <Paperclip className="size-3 shrink-0" />
                    Attachment of {parents[0]}
                    {parents.length > 1 ? ` +${parents.length - 1}` : ""}
                  </Badge>
                );
              })()}
            </div>
            <div className="text-[11px] text-primary/40">
              {type.description}
            </div>
            {!configured && (
              <NotConfiguredHint
                hasSourceFile={type.hasSourceFile}
                extractionStatus={type.extractionStatus}
                extractionError={type.extractionError}
                templateKey={type.key}
              />
            )}
          </div>
        </label>

        {fanOut && (
          <ContactFanOutPicker
            templateKey={type.key}
            matchingContacts={matchingContacts}
            selectedContactIds={
              contactSelectionsByTemplate.get(type.key) ?? new Set()
            }
            expanded={expandedContactPickers.has(type.key)}
            onToggleExpanded={() => toggleContactPickerExpanded(type.key)}
            onToggleContact={(contactId) =>
              toggleContactSelection(type.key, contactId)
            }
            onSelectAll={(selectAll) =>
              setAllContactsForTemplate(type.key, selectAll)
            }
          />
        )}

        {firmRefCats && firmRefCats.length > 0 && (
          <FirmRefPicker
            categories={firmRefCats}
            contactsByCategory={firmContactsByCategory}
            selections={firmRefSelectionsByTemplate.get(type.key)}
            onSelect={(category, contactId) =>
              setFirmRefSelection(type.key, category, contactId)
            }
          />
        )}

        {nestedAttachments &&
          nestedAttachments.length > 0 &&
          (() => {
            const attExpanded = expandedAttachmentGroups.has(type.key);
            return (
              <div className="ml-7 mt-2 rounded-md border border-primary/15 bg-muted/30">
                <button
                  type="button"
                  onClick={() => toggleAttachmentGroupExpanded(type.key)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left"
                >
                  <span className="flex items-center gap-1.5 text-[11px] font-medium text-primary/70">
                    <Paperclip className="size-3 shrink-0" />
                    {nestedAttachments.length} attachment
                    {nestedAttachments.length === 1 ? "" : "s"} also generated
                  </span>
                  <ChevronDown
                    className={cn(
                      "size-3.5 shrink-0 text-primary/50 transition-transform",
                      attExpanded && "rotate-180",
                    )}
                  />
                </button>
                {attExpanded && (
                  <div className="space-y-2 border-t border-primary/10 px-3 py-2">
                    {nestedAttachments.map((att) =>
                      renderGridCard(att, undefined, true),
                    )}
                  </div>
                )}
              </div>
            );
          })()}
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-col bg-muted p-6">
      <div className="mx-auto w-full max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-primary">
              Document Generator
            </h2>
            <button
              type="button"
              onClick={handleSkip}
              className="flex items-center gap-1 text-sm text-primary/40 hover:text-primary/60"
            >
              <SkipForward className="size-3.5" />
              Skip
            </button>
          </div>
          <p className="mt-1 text-sm text-primary/50">
            {reselect
              ? "Review and update field values below, then regenerate your documents."
              : "Select documents, review auto-populated fields and then generate."}
          </p>
        </div>

        {/* Step indicator */}
        <div className="mb-6 flex items-center gap-2 text-xs">
          {(
            [
              "select",
              "review",
              ...(hasSelectedAttachments ? (["attachments"] as const) : []),
              "confirm",
            ] as Step[]
          ).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              {i > 0 && <ChevronRight className="size-3 text-primary/20" />}
              <span
                className={cn(
                  "rounded-full px-3 py-1 font-medium transition",
                  step === s
                    ? "bg-primary text-primary-foreground"
                    : "text-primary/40",
                )}
              >
                {i + 1}.{" "}
                {s === "select"
                  ? "Select Templates"
                  : s === "review"
                    ? "Review Fields"
                    : s === "attachments"
                      ? "Attach"
                      : "Confirm"}
              </span>
            </div>
          ))}
        </div>

        {/* Step 1: Template Selection */}
        {step === "select" && (
          <div className="space-y-6">
            {/* Advance-phase prompt — non-blocking. Appears when the current
                phase's documents already exist so the user can move the job
                onward instead of re-surfacing the same suggestions. */}
            {job && advancePhaseTarget && !advancePromptDismissed && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3">
                <p className="flex-1 text-sm text-amber-800 dark:text-amber-200">
                  You&apos;ve already generated{" "}
                  {JOB_STATUS_LABELS[job.status] ?? job.status} documents for
                  this job. Move it to{" "}
                  <span className="font-medium">
                    {JOB_STATUS_LABELS[advancePhaseTarget] ??
                      advancePhaseTarget}
                  </span>
                  ?
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 border-amber-500/40 text-xs text-amber-800 hover:bg-amber-500/10 dark:text-amber-200"
                  onClick={handleAdvancePhase}
                  disabled={advancingPhase}
                >
                  {advancingPhase ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <ArrowRight className="size-3.5" />
                  )}
                  Move to{" "}
                  {JOB_STATUS_LABELS[advancePhaseTarget] ?? advancePhaseTarget}
                </Button>
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => setAdvancePromptDismissed(true)}
                  className="rounded p-1 text-amber-700/70 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-300/70 dark:hover:text-amber-200"
                >
                  <XIcon className="size-3.5" />
                </button>
              </div>
            )}
            {/* Toolbar: search + view toggle + category tabs */}
            <div className={GROUP_CARD_CLASS}>
              <div className="flex flex-col gap-2 px-5 py-3">
                {/* Row 1: search + view-mode toggle */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative min-w-[200px] flex-1">
                    <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-primary/40" />
                    <Input
                      placeholder="Search templates by name or description…"
                      value={selectSearchText}
                      onChange={(e) => setSelectSearchText(e.target.value)}
                      className="h-9 pl-9 pr-9 text-sm"
                    />
                    {selectSearchText && (
                      <button
                        type="button"
                        aria-label="Clear search"
                        onClick={() => setSelectSearchText("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-primary/40 hover:bg-secondary/60 hover:text-primary/70"
                      >
                        <XIcon className="size-3" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center rounded-md border border-border/60">
                    <button
                      type="button"
                      onClick={() => toggleSelectViewMode("grid")}
                      className={cn(
                        "flex items-center justify-center rounded-l-md px-2 py-1.5 transition-colors",
                        selectViewMode === "grid"
                          ? "bg-primary/10 text-primary"
                          : "text-primary/40 hover:text-primary/60",
                      )}
                      aria-label="Grid view"
                    >
                      <LayoutGrid className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleSelectViewMode("table")}
                      className={cn(
                        "flex items-center justify-center rounded-r-md px-2 py-1.5 transition-colors",
                        selectViewMode === "table"
                          ? "bg-primary/10 text-primary"
                          : "text-primary/40 hover:text-primary/60",
                      )}
                      aria-label="Table view"
                    >
                      <List className="size-3.5" />
                    </button>
                  </div>
                </div>

                {/* Row 2: bulk-select actions */}
                <div className="flex flex-wrap items-center gap-2">
                  {job && availablePhases.length > 1 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-primary/50">
                        Phase
                      </span>
                      <Select
                        value={selectedPhase ?? job.status}
                        onValueChange={(v) => setSelectedPhase(v as JobStatus)}
                      >
                        <SelectTrigger className="h-8 w-[150px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePhases.map((s) => (
                            <SelectItem key={s} value={s}>
                              {JOB_STATUS_LABELS[s] ?? s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {tagMatchReasons.size > 0 && (
                    <button
                      type="button"
                      onClick={handleSelectSuggested}
                      className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-border/60 bg-background px-3 py-1.5 text-xs font-medium text-primary/70 shadow-sm transition hover:border-border hover:text-primary"
                    >
                      Select suggested
                      <span className="rounded bg-secondary/60 px-1.5 py-px text-[10px] tabular-nums text-primary/50">
                        {tagMatchReasons.size}
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    disabled={allConfiguredSelected}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-border/60 bg-background px-3 py-1.5 text-xs font-medium text-primary/70 shadow-sm transition hover:border-border hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border/60 disabled:hover:text-primary/70"
                  >
                    Select all configured
                    <span className="rounded bg-secondary/60 px-1.5 py-px text-[10px] tabular-nums text-primary/50">
                      {configuredKeys.size}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleUnselectAll}
                    disabled={selectedKeys.size === 0}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-border/60 bg-background px-3 py-1.5 text-xs font-medium text-primary/70 shadow-sm transition hover:border-border hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border/60 disabled:hover:text-primary/70"
                  >
                    Unselect all
                    <span className="rounded bg-secondary/60 px-1.5 py-px text-[10px] tabular-nums text-primary/50">
                      {selectedKeys.size}
                    </span>
                  </button>
                </div>
              </div>

              {/* View + category filter tabs. "Selected" shows only the
                chosen templates; "All" and each category show the full
                pick list, filtered to that category. */}
              <div className="border-t border-border/60 px-5 py-2">
                <div className="flex gap-1 overflow-x-auto rounded-lg bg-secondary/40 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAllTemplates(false);
                      setSelectCategoryTab("all");
                    }}
                    className={cn(
                      "flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition",
                      !showAllTemplates
                        ? "bg-background text-primary shadow-sm"
                        : "text-primary/50 hover:text-primary/70",
                    )}
                  >
                    Selected
                    <span
                      className={cn(
                        "rounded px-1.5 py-px text-[10px] tabular-nums transition",
                        !showAllTemplates
                          ? "bg-primary/10 text-primary/60"
                          : "bg-primary/5 text-primary/40",
                      )}
                    >
                      {selectedKeys.size}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAllTemplates(true);
                      setSelectCategoryTab("all");
                    }}
                    className={cn(
                      "flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition",
                      showAllTemplates && selectCategoryTab === "all"
                        ? "bg-background text-primary shadow-sm"
                        : "text-primary/50 hover:text-primary/70",
                    )}
                  >
                    All
                    <span
                      className={cn(
                        "rounded px-1.5 py-px text-[10px] tabular-nums transition",
                        showAllTemplates && selectCategoryTab === "all"
                          ? "bg-primary/10 text-primary/60"
                          : "bg-primary/5 text-primary/40",
                      )}
                    >
                      {totalTemplateCount}
                    </span>
                  </button>
                  {wizardCategories.length > 1 &&
                    wizardCategories.map((cat) => {
                      const catActive =
                        showAllTemplates && selectCategoryTab === cat.key;
                      return (
                        <button
                          key={cat.key}
                          type="button"
                          onClick={() => {
                            setShowAllTemplates(true);
                            setSelectCategoryTab(cat.key);
                          }}
                          className={cn(
                            "flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition",
                            catActive
                              ? "bg-background text-primary shadow-sm"
                              : "text-primary/50 hover:text-primary/70",
                          )}
                        >
                          {cat.label}
                          <span
                            className={cn(
                              "rounded px-1.5 py-px text-[10px] tabular-nums transition",
                              catActive
                                ? "bg-primary/10 text-primary/60"
                                : "bg-primary/5 text-primary/40",
                            )}
                          >
                            {cat.types.length}
                          </span>
                        </button>
                      );
                    })}
                </div>
              </div>
            </div>

            {visibleWizardCategories.length === 0 &&
              (!showAllTemplates && filteredWizardCategories.length > 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="size-10 text-primary/15" />
                  <p className="mt-3 text-sm text-primary/50">
                    {selectedKeys.size === 0
                      ? "No templates selected yet"
                      : "No selected templates match your filters"}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowAllTemplates(true)}
                    className="mt-2 text-xs font-medium text-primary/60 underline underline-offset-2 hover:text-primary/80"
                  >
                    Show all templates
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="size-10 text-primary/15" />
                  <p className="mt-3 text-sm text-primary/50">
                    No templates match your filters
                  </p>
                  {(selectSearchText || selectCategoryTab !== "all") && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectSearchText("");
                        setSelectCategoryTab("all");
                      }}
                      className="mt-2 text-xs font-medium text-primary/60 underline underline-offset-2 hover:text-primary/80"
                    >
                      Clear search & filters
                    </button>
                  )}
                </div>
              ))}

            {selectViewMode === "grid" &&
              visibleWizardCategories.map((group) => {
                const categorySelection = getCategorySelection(group);
                return (
                  <div key={group.key} className={GROUP_CARD_CLASS}>
                    <label
                      className={cn(
                        "flex items-center gap-3 border-b border-border/50 px-5 py-3",
                        categorySelection.disabled
                          ? "cursor-not-allowed"
                          : "cursor-pointer",
                      )}
                    >
                      <Checkbox
                        checked={categorySelection.checked}
                        onCheckedChange={() =>
                          handleToggleCategory(categorySelection.keys)
                        }
                        disabled={categorySelection.disabled}
                        className="size-4 border-primary/30"
                      />
                      <span>
                        <span className="text-sm font-medium text-primary/80">
                          {group.label}
                        </span>
                        {group.subtitle && (
                          <span className="ml-2 text-xs text-primary/40">
                            {group.subtitle}
                          </span>
                        )}
                      </span>
                    </label>
                    <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
                      {(categoryUnitsByKey.get(group.key) ?? []).map((unit) =>
                        // Every principal is a same-size grid card; its
                        // attachments live in a collapsible drop-down inside
                        // the card (like the contact fan-out picker) so the
                        // grid stays uniform.
                        renderGridCard(unit.principal, unit.attachments),
                      )}
                    </div>
                  </div>
                );
              })}

            {selectViewMode === "table" &&
              visibleWizardCategories.length > 0 && (
                <div className={cn(GROUP_CARD_CLASS, "px-2")}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-border/60 text-[11px] font-medium uppercase tracking-wide text-primary/50">
                          <th className="w-10 py-2 pl-3 pr-0" />
                          <th
                            className="relative px-2 py-2"
                            style={{ width: nameColWidth }}
                          >
                            Name
                            <span
                              role="separator"
                              aria-orientation="vertical"
                              onMouseDown={handleNameColResizeStart}
                              className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none hover:bg-primary/20"
                            />
                          </th>
                          <th className="w-[140px] px-2 py-2">Category</th>
                          <th className="w-[180px] px-2 py-2">Contacts</th>
                          <th className="w-[220px] px-2 py-2">Variants</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleWizardCategories.flatMap((group) => {
                          const categorySelection = getCategorySelection(group);
                          return [
                            <tr
                              key={`group:${group.key}`}
                              className="border-b border-border/40 bg-muted/30"
                            >
                              <td className="py-2 pl-3 pr-0">
                                <Checkbox
                                  checked={categorySelection.checked}
                                  onCheckedChange={() =>
                                    handleToggleCategory(categorySelection.keys)
                                  }
                                  disabled={categorySelection.disabled}
                                  className="size-4 border-primary/30"
                                />
                              </td>
                              <td
                                colSpan={4}
                                className="px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-primary/60"
                              >
                                {group.label}
                                {group.subtitle && (
                                  <span className="ml-2 normal-case text-primary/40">
                                    {group.subtitle}
                                  </span>
                                )}
                              </td>
                            </tr>,
                            ...(categoryRowsByKey.get(group.key) ?? []).map(
                              ({ type, depth }) => {
                                const configured = configuredKeys.has(type.key);
                                const selected = selectedKeys.has(type.key);
                                const reasons = tagMatchReasons.get(type.key);
                                const matchingContacts =
                                  matchingContactsByTemplate.get(type.key);
                                const fanOut =
                                  selected &&
                                  matchingContacts &&
                                  matchingContacts.length > 0;
                                const firmRefCats = selected
                                  ? firmRefCategoriesByTemplate.get(type.key)
                                  : undefined;
                                return (
                                  <tr
                                    key={type.key}
                                    className={cn(
                                      "border-b border-border/30 transition",
                                      !configured && "opacity-40",
                                      depth === 1 && !selected && "bg-muted/20",
                                      selected && "bg-primary/5",
                                    )}
                                  >
                                    <td
                                      className={cn(
                                        "py-2.5 pr-0 align-top",
                                        depth === 1
                                          ? "border-l-2 border-l-primary/30 pl-8"
                                          : "pl-3",
                                      )}
                                    >
                                      <Checkbox
                                        checked={selected}
                                        onCheckedChange={() =>
                                          configured &&
                                          handleToggleTemplate(type.key)
                                        }
                                        disabled={!configured}
                                        className="mt-0.5 size-4 border-primary/30"
                                      />
                                    </td>
                                    <td
                                      className="px-2 py-2.5 align-top"
                                      style={{ width: nameColWidth }}
                                    >
                                      <div className="flex flex-col gap-1">
                                        <div className="flex flex-wrap items-center gap-1.5">
                                          {depth === 1 && (
                                            <Paperclip className="size-3 shrink-0 text-primary/40" />
                                          )}
                                          <span
                                            className="truncate font-medium text-primary"
                                            style={{
                                              // Nested rows show a leading
                                              // paperclip (~12px) + gap, so
                                              // reserve extra room to keep the
                                              // icon and name on one line.
                                              maxWidth:
                                                nameColWidth -
                                                (depth === 1 ? 44 : 24),
                                            }}
                                          >
                                            {type.shortName}
                                          </span>
                                          {reasons && reasons.length === 0 && (
                                            <Badge
                                              color="gray"
                                              variant="soft"
                                              size="sm"
                                            >
                                              Suggested
                                            </Badge>
                                          )}
                                          {reasons
                                            ?.filter(
                                              (r) =>
                                                r.kind !== "contactRole" &&
                                                r.kind !==
                                                  "jobContactCustomRole",
                                            )
                                            .map((reason, idx) => (
                                              <MatchReasonBadge
                                                key={`${reason.kind}:${idx}`}
                                                reason={reason}
                                                tagGroups={tagGroups}
                                              />
                                            ))}
                                          {(() => {
                                            // Indented rows already sit under
                                            // their principal, so the badge is
                                            // redundant — only show it on
                                            // top-level rows.
                                            if (depth === 1) return null;
                                            const parents =
                                              attachmentParentNamesByKey.get(
                                                type.key,
                                              );
                                            if (
                                              !parents ||
                                              parents.length === 0
                                            )
                                              return null;
                                            const autoIncluded =
                                              requiredDependencyKeys.has(
                                                type.key,
                                              ) &&
                                              !dismissedDependencyKeys.has(
                                                type.key,
                                              );
                                            return (
                                              <Badge
                                                color="blue"
                                                variant="soft"
                                                size="sm"
                                                className="gap-1"
                                                title={
                                                  autoIncluded
                                                    ? `Auto-included because ${parents.join(", ")} attach${parents.length === 1 ? "es" : ""} it. Unselect to skip generating it.`
                                                    : `This is an attachment of ${parents.join(", ")} — it is generated inside its principal document${parents.length === 1 ? "" : "s"} rather than on its own.`
                                                }
                                              >
                                                <Paperclip className="size-3 shrink-0" />
                                                Attachment of {parents[0]}
                                                {parents.length > 1
                                                  ? ` +${parents.length - 1}`
                                                  : ""}
                                              </Badge>
                                            );
                                          })()}
                                        </div>
                                        {!configured && (
                                          <NotConfiguredHint
                                            hasSourceFile={type.hasSourceFile}
                                            extractionStatus={
                                              type.extractionStatus
                                            }
                                            extractionError={
                                              type.extractionError
                                            }
                                            templateKey={type.key}
                                          />
                                        )}
                                        {fanOut && (
                                          <ContactFanOutPicker
                                            templateKey={type.key}
                                            matchingContacts={matchingContacts}
                                            selectedContactIds={
                                              contactSelectionsByTemplate.get(
                                                type.key,
                                              ) ?? new Set()
                                            }
                                            expanded={expandedContactPickers.has(
                                              type.key,
                                            )}
                                            onToggleExpanded={() =>
                                              toggleContactPickerExpanded(
                                                type.key,
                                              )
                                            }
                                            onToggleContact={(contactId) =>
                                              toggleContactSelection(
                                                type.key,
                                                contactId,
                                              )
                                            }
                                            onSelectAll={(selectAll) =>
                                              setAllContactsForTemplate(
                                                type.key,
                                                selectAll,
                                              )
                                            }
                                          />
                                        )}
                                        {firmRefCats &&
                                          firmRefCats.length > 0 && (
                                            <FirmRefPicker
                                              categories={firmRefCats}
                                              contactsByCategory={
                                                firmContactsByCategory
                                              }
                                              selections={firmRefSelectionsByTemplate.get(
                                                type.key,
                                              )}
                                              onSelect={(category, contactId) =>
                                                setFirmRefSelection(
                                                  type.key,
                                                  category,
                                                  contactId,
                                                )
                                              }
                                            />
                                          )}
                                      </div>
                                    </td>
                                    <td className="px-2 py-2.5 align-top text-xs text-primary/60">
                                      {group.label}
                                    </td>
                                    <td className="px-2 py-2.5 align-top">
                                      {type.contacts &&
                                      type.contacts.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                          {type.contacts.map((contact) => {
                                            const Icon = contact.icon;
                                            return (
                                              <Badge
                                                key={contact.key}
                                                color={contact.color}
                                                variant="soft"
                                                size="sm"
                                                className="inline-flex items-center gap-1 whitespace-nowrap"
                                                title={contact.label}
                                              >
                                                <Icon className="size-3 shrink-0" />
                                                {contact.label}
                                              </Badge>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-primary/30">
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-2 py-2.5 align-top">
                                      {type.variantGroupSlugs &&
                                      type.variantGroupSlugs.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                          {type.variantGroupSlugs.map(
                                            (slug) => (
                                              <Badge
                                                key={slug}
                                                color="indigo"
                                                variant="soft"
                                                size="sm"
                                                className="whitespace-nowrap"
                                              >
                                                {variantGroupNameBySlug.get(
                                                  slug,
                                                ) ?? toTitleCase(slug)}
                                              </Badge>
                                            ),
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-xs text-primary/30">
                                          —
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              },
                            ),
                          ];
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            <div className="flex items-center justify-between pt-4">
              <button
                type="button"
                onClick={handleSkip}
                className="flex items-center gap-1 text-sm text-primary/40 hover:text-primary/60"
              >
                <SkipForward className="size-3.5" />
                Skip
              </button>
              <Button
                onClick={() => setStep("review")}
                disabled={selectedKeys.size === 0}
              >
                Next: Review Fields
                <ArrowRight className="ml-1.5 size-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Field Review */}
        {step === "review" &&
          (() => {
            const reviewTabs: Array<{
              id: string;
              label: string;
              needsInput: number;
            }> = [
              {
                id: "all",
                label: "All",
                needsInput: reviewFieldStats.grandNeedsInput,
              },
            ];
            if (commonFieldsWithPresets.length > 0) {
              reviewTabs.push({
                id: "common",
                label: "Common Fields",
                needsInput: reviewFieldStats.common.needsInput,
              });
            }
            for (const card of perDocumentCards) {
              reviewTabs.push({
                id: card.type.key,
                label: card.type.shortName ?? card.type.name,
                needsInput:
                  reviewFieldStats.perCard.get(card.type.key)?.needsInput ?? 0,
              });
            }

            const showCommon = reviewTab === "all" || reviewTab === "common";
            const showDocument = (key: string) =>
              reviewTab === "all" || reviewTab === key;
            // In the "all" view a tab click jumps to that section instead of
            // filtering; in a focused view it switches which section is shown.
            const handleReviewTabClick = (id: string) => {
              if (reviewTab === "all" && id !== "all") {
                scrollToReviewCard(id === "common" ? COMMON_CARD_KEY : id);
                return;
              }
              setReviewTab(id);
            };
            const { grandTotal, grandNeedsInput } = reviewFieldStats;
            const completed = grandTotal - grandNeedsInput;
            const progressPct =
              grandTotal > 0 ? Math.round((completed / grandTotal) * 100) : 100;
            // While searching, force every section open so matches aren't hidden
            // behind a collapsed card.
            const searchActive = reviewSearch.trim() !== "";

            // Soft flag: does any selected document reference the Company
            // Background field? Only warn when it's actually used and the job
            // has no overview summary yet.
            const backgroundSummaryUsed =
              commonFieldsWithPresets.some((group) =>
                group.fields.some((f) => f.key === "background_summary"),
              ) ||
              perDocumentCards.some(
                (card) =>
                  card.canonicalFields.some(
                    (f) => f.key === "background_summary",
                  ) || card.extras.some((f) => f.key === "background_summary"),
              );

            return (
              <div className="space-y-6">
                <p className="text-sm text-primary/50">
                  Fields auto-populated from job data and your firm presets.
                  Select which preset to use for each category, then edit any
                  values below.
                </p>

                {backgroundSummaryUsed &&
                  !hasOverviewSummary &&
                  !backgroundFlagDismissed && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <p className="flex-1">
                        No business background has been generated for this job
                        yet — documents using the Background Summary field will
                        be blank.{" "}
                        <Link
                          to="/dashboard/jobs/$jobId/plan"
                          params={{ jobId }}
                          className="font-medium underline underline-offset-2"
                        >
                          Generate it on the Overview page
                        </Link>
                        .
                      </p>
                      <button
                        type="button"
                        onClick={() => setBackgroundFlagDismissed(true)}
                        aria-label="Dismiss background warning"
                        className="-mr-1 -mt-0.5 rounded p-0.5 text-amber-600 transition-colors hover:bg-amber-100 hover:text-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/40 dark:hover:text-amber-200"
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                {/* Primary contact person (MOR-2342) — who the contact
                    name / email / direct phone fields default to for this
                    run. Follows the firm default until changed here. */}
                <div className={GROUP_CARD_CLASS}>
                  <div className="flex items-center justify-between border-b border-border/50 px-5 py-3">
                    <div>
                      <span className="text-sm font-medium text-primary/80">
                        Primary Contact
                      </span>
                      <span className="ml-2 text-xs text-primary/40">
                        Whose name, email and direct phone appear as the
                        day-to-day contact
                      </span>
                    </div>
                    {contactPersonChoice && (
                      <button
                        type="button"
                        onClick={() => setContactPersonChoice(null)}
                        className="text-xs text-primary/50 underline-offset-2 hover:underline"
                      >
                        Use firm default
                      </button>
                    )}
                  </div>
                  <div className="space-y-3 p-5">
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                      {CONTACT_PERSON_MODES.map((mode) => {
                        const isSelected = effectiveContactPerson.mode === mode;
                        const Icon = CONTACT_PERSON_MODE_ICONS[mode];
                        return (
                          <button
                            key={mode}
                            type="button"
                            onClick={() =>
                              setContactPersonChoice(
                                mode === "staff_member"
                                  ? {
                                      mode,
                                      userId:
                                        effectiveContactPerson.userId ??
                                        contactPersonCandidates?.[0]?.userId,
                                    }
                                  : mode === "job_role"
                                    ? {
                                        mode,
                                        roleId:
                                          effectiveContactPerson.roleId ??
                                          workflowRoles?.[0]?._id,
                                      }
                                    : { mode },
                              )
                            }
                            className={cn(
                              "flex min-w-0 items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm leading-snug transition",
                              isSelected
                                ? "border-primary bg-primary/5 ring-1 ring-primary"
                                : "border-border/50 hover:border-primary/30 hover:bg-secondary/30",
                            )}
                          >
                            <Icon
                              className={cn(
                                "size-4 shrink-0",
                                isSelected ? "text-primary" : "text-primary/50",
                              )}
                            />
                            <span className="min-w-0">
                              {CONTACT_PERSON_MODE_LABELS[mode]}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {effectiveContactPerson.mode === "staff_member" && (
                        <Select
                          value={effectiveContactPerson.userId ?? ""}
                          onValueChange={(userId) =>
                            setContactPersonChoice({
                              mode: "staff_member",
                              userId: userId as Id<"users">,
                            })
                          }
                        >
                          <SelectTrigger className="w-64">
                            <SelectValue placeholder="Choose a staff member" />
                          </SelectTrigger>
                          <SelectContent>
                            {(contactPersonCandidates ?? []).map((c) => (
                              <SelectItem key={c.userId} value={c.userId}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {effectiveContactPerson.mode === "job_role" && (
                        <Select
                          value={effectiveContactPerson.roleId ?? ""}
                          onValueChange={(roleId) =>
                            setContactPersonChoice({
                              mode: "job_role",
                              roleId: roleId as Id<"workflowRoles">,
                            })
                          }
                        >
                          <SelectTrigger className="w-64">
                            <SelectValue placeholder="Choose a job role" />
                          </SelectTrigger>
                          <SelectContent>
                            {(workflowRoles ?? []).map((r) => (
                              <SelectItem key={r._id} value={r._id}>
                                <RoleBadge role={r} size="sm" />
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {contactPersonFields.contact_name ? (
                        <span className="text-xs text-primary/50">
                          {contactPersonFields.contact_name}
                          {contactPersonFields.contact_phone
                            ? ` · ${contactPersonFields.contact_phone}`
                            : " · no direct phone on their profile"}
                        </span>
                      ) : effectiveContactPerson.mode === "job_role" ? (
                        <span className="text-xs text-amber-600">
                          Nobody is allocated to that role on this job — the
                          Contact preset applies
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Signature selection — always visible so users know what will be applied */}
                <div className={GROUP_CARD_CLASS}>
                  <div className="flex items-center justify-between border-b border-border/50 px-5 py-3">
                    <div>
                      <span className="text-sm font-medium text-primary/80">
                        Document Signature
                      </span>
                      <span className="ml-2 text-xs text-primary/40">
                        Choose whose signature appears on generated documents
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 p-5">
                    {signatureOptions.length > 0 ? (
                      <>
                        {signatureOptions.map((member) => {
                          const isSelected = selectedSignatoryId
                            ? selectedSignatoryId === member.userId
                            : currentUser?._id === member.userId;
                          return (
                            <button
                              key={member.userId}
                              type="button"
                              onClick={() =>
                                setSelectedSignatoryId(member.userId)
                              }
                              className={cn(
                                "flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition",
                                isSelected
                                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                                  : "border-border/50 hover:border-primary/30 hover:bg-secondary/30",
                              )}
                            >
                              <div className="flex flex-col">
                                <span className="text-sm font-medium">
                                  {member.name}
                                </span>
                                <span className="text-xs text-primary/40">
                                  {member.signatureImageUrl
                                    ? "Image signature"
                                    : "Text signature"}
                                </span>
                              </div>
                              {member.signatureImageUrl ? (
                                <img
                                  src={member.signatureImageUrl}
                                  alt={`${member.name}'s signature`}
                                  className="h-8 max-w-[120px] object-contain"
                                />
                              ) : member.signatureText ? (
                                <span
                                  className="text-lg"
                                  style={{ fontFamily: "'Caveat', cursive" }}
                                >
                                  {member.signatureText}
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => setSelectedSignatoryId("__none__")}
                          className={cn(
                            "flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition",
                            selectedSignatoryId === "__none__"
                              ? "border-primary bg-primary/5 ring-1 ring-primary"
                              : "border-border/50 hover:border-primary/30 hover:bg-secondary/30",
                          )}
                        >
                          <span className="text-sm text-primary/50">
                            No signature
                          </span>
                        </button>
                      </>
                    ) : (
                      <p className="text-sm text-primary/40">
                        No team members have saved a signature. Go to{" "}
                        <Link
                          to="/dashboard/settings"
                          className="text-primary underline underline-offset-2"
                        >
                          Settings
                        </Link>{" "}
                        to add your signature.
                      </p>
                    )}
                  </div>

                  {/* Multi-signer pick for repeat-signatory blocks. Only shown
                when a selected template repeats over the signatories
                collection — everyone ticked by default, untick down to
                the signers this document needs. */}
                  {signatoriesCollectionUsed && signatureOptions.length > 0 && (
                    <div className="border-t border-border/50 px-5 py-4">
                      <div className="mb-2">
                        <span className="text-sm font-medium text-primary/80">
                          Sign-off blocks (repeating signatures)
                        </span>
                        <span className="ml-2 text-xs text-primary/40">
                          A template on this run repeats a signature block per
                          signer — tick who signs
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {signatureOptions.map((member) => {
                          const ticked = pickedSignatoryIds
                            ? pickedSignatoryIds.has(member.userId)
                            : true;
                          return (
                            <button
                              key={member.userId}
                              type="button"
                              onClick={() =>
                                toggleRepeatSignatory(member.userId)
                              }
                              className={cn(
                                "flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition",
                                ticked
                                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                                  : "border-border/50 opacity-60 hover:border-primary/30 hover:opacity-100",
                              )}
                            >
                              <Checkbox
                                checked={ticked}
                                className="pointer-events-none"
                              />
                              <span className="text-sm">{member.name}</span>
                            </button>
                          );
                        })}
                      </div>
                      {pickedSignatoryIds && pickedSignatoryIds.size === 0 && (
                        <p className="mt-2 text-xs text-red-600">
                          No signers ticked — repeating signature blocks will be
                          empty on the generated documents.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Sticky review controls — progress, field search, filters, and
              section tabs. Pins to the top while scrolling long forms. */}
                {grandTotal > 0 && (
                  <div className="sticky top-16 z-[9] -mx-1 space-y-3 rounded-xl border border-border/50 bg-background/95 px-4 py-3 shadow-sm backdrop-blur-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-[180px] flex-1">
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className="font-medium text-primary/70">
                            {grandNeedsInput === 0
                              ? "All fields ready"
                              : `${grandNeedsInput} of ${grandTotal} field${grandTotal === 1 ? "" : "s"} need input`}
                          </span>
                          <span className="text-primary/40">
                            {progressPct}%
                          </span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                          <div
                            className={cn(
                              "h-full rounded-full transition-all duration-300",
                              grandNeedsInput === 0
                                ? "bg-emerald-500"
                                : "bg-primary",
                            )}
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-primary/40" />
                          <Input
                            type="text"
                            value={reviewSearch}
                            onChange={(e) => setReviewSearch(e.target.value)}
                            placeholder="Search fields"
                            className="h-8 w-40 pl-8 pr-7 text-xs"
                          />
                          {reviewSearch && (
                            <button
                              type="button"
                              onClick={() => setReviewSearch("")}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-primary/40 hover:text-primary/70"
                              aria-label="Clear search"
                            >
                              <XIcon className="size-3.5" />
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowOnlyNeedsInput((v) => !v)}
                          className={cn(
                            "h-8 whitespace-nowrap rounded-md border px-3 text-xs font-medium transition",
                            showOnlyNeedsInput
                              ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/20"
                              : "border-border/50 text-primary/60 hover:border-primary/30 hover:text-primary/80",
                          )}
                          aria-pressed={showOnlyNeedsInput}
                        >
                          Needs input only
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const allKeys = new Set<string>([COMMON_CARD_KEY]);
                            for (const card of perDocumentCards)
                              allKeys.add(card.type.key);
                            // Collapse all only if anything is currently expanded.
                            const anyExpanded =
                              !collapsedCards.has(COMMON_CARD_KEY) ||
                              perDocumentCards.some(
                                (c) => !collapsedCards.has(c.type.key),
                              );
                            setCollapsedCards(
                              anyExpanded ? allKeys : new Set(),
                            );
                          }}
                          className="h-8 whitespace-nowrap rounded-md border border-border/50 px-3 text-xs font-medium text-primary/60 transition hover:border-primary/30 hover:text-primary/80"
                        >
                          {!collapsedCards.has(COMMON_CARD_KEY) ||
                          perDocumentCards.some(
                            (c) => !collapsedCards.has(c.type.key),
                          )
                            ? "Collapse all"
                            : "Expand all"}
                        </button>
                      </div>
                    </div>

                    {/* Section tabs with per-section needs-input counts. */}
                    {reviewTabs.length > 2 && (
                      <div className="flex gap-1 overflow-x-auto rounded-lg bg-secondary/40 p-1">
                        {reviewTabs.map((tab) => (
                          <button
                            key={tab.id}
                            type="button"
                            onClick={() => handleReviewTabClick(tab.id)}
                            className={cn(
                              "flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition",
                              reviewTab === tab.id
                                ? "bg-background text-primary shadow-sm"
                                : "text-primary/50 hover:text-primary/70",
                            )}
                          >
                            {tab.label}
                            {tab.needsInput > 0 && (
                              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-amber-700 dark:bg-amber-950/30">
                                {tab.needsInput}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Common Fields — grouped by category with inline preset selectors */}
                {showCommon &&
                  commonFieldsWithPresets.length > 0 &&
                  (() => {
                    const commonCollapsed =
                      collapsedCards.has(COMMON_CARD_KEY) && !searchActive;
                    const commonStats = reviewFieldStats.common;
                    const commonFilterActive =
                      reviewSearch.trim() !== "" || showOnlyNeedsInput;
                    const anyCommonVisible = commonFieldsWithPresets.some((g) =>
                      g.fields.some(
                        (f) =>
                          matchesReviewSearch(f) &&
                          (!showOnlyNeedsInput ||
                            resolveReviewSource(f.key) === "empty"),
                      ),
                    );
                    return (
                      <div
                        className={GROUP_CARD_CLASS}
                        ref={(el) => {
                          reviewCardRefs.current[COMMON_CARD_KEY] = el;
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => toggleCardCollapsed(COMMON_CARD_KEY)}
                          className="flex w-full items-center justify-between gap-3 border-b border-border/50 px-5 py-3 text-left transition hover:bg-secondary/20"
                        >
                          <div className="flex items-center gap-2">
                            <ChevronDown
                              className={cn(
                                "size-4 shrink-0 text-primary/40 transition-transform",
                                commonCollapsed && "-rotate-90",
                              )}
                            />
                            <span className="text-sm font-medium text-primary/80">
                              Common Fields
                            </span>
                            <span className="text-xs text-primary/40">
                              Used across multiple selected documents
                            </span>
                          </div>
                          <span className="flex items-center gap-2 text-xs text-primary/40">
                            <span>
                              {commonStats.total} field
                              {commonStats.total === 1 ? "" : "s"}
                            </span>
                            {commonStats.needsInput > 0 && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/30">
                                {commonStats.needsInput} need input
                              </span>
                            )}
                          </span>
                        </button>
                        {!commonCollapsed && (
                          <div className="p-5">
                            {commonFilterActive && !anyCommonVisible && (
                              <p className="text-xs text-primary/40">
                                No fields match the current filter.
                              </p>
                            )}
                            {commonFieldsWithPresets.map((group, groupIdx) => {
                              const filterActive =
                                reviewSearch.trim() !== "" ||
                                showOnlyNeedsInput;
                              const isFieldVisible = (f: CanonicalField) =>
                                matchesReviewSearch(f) &&
                                (!showOnlyNeedsInput ||
                                  resolveReviewSource(f.key) === "empty");
                              const visibleGroupFields =
                                group.fields.filter(isFieldVisible);
                              if (
                                filterActive &&
                                visibleGroupFields.length === 0
                              )
                                return null;
                              const catPresets = presetsByCategory.get(
                                group.category.id,
                              );
                              const sorted = catPresets
                                ? [...catPresets].sort((a, b) => {
                                    const aFav = favouriteSet.has(a._id)
                                      ? 0
                                      : 1;
                                    const bFav = favouriteSet.has(b._id)
                                      ? 0
                                      : 1;
                                    if (aFav !== bFav) return aFav - bFav;
                                    return a.name.localeCompare(b.name);
                                  })
                                : [];

                              const hasPreset = sorted.length > 0;
                              const selectedPreset = hasPreset
                                ? sorted.find(
                                    (p) =>
                                      p._id ===
                                      presetSelections[group.presetCategoryId],
                                  )
                                : undefined;

                              return (
                                <div
                                  key={group.presetCategoryId}
                                  className={
                                    groupIdx > 0
                                      ? "mt-6 border-t border-border/30 pt-6"
                                      : ""
                                  }
                                >
                                  <div
                                    className={cn(
                                      "flex items-baseline justify-between gap-2",
                                      !hasPreset && "mb-3",
                                    )}
                                  >
                                    <p className="text-xs font-medium uppercase tracking-wider text-primary/40">
                                      {resolveCategoryLabel(
                                        group.category.id,
                                        group.category.label,
                                        job?.appointmentType,
                                      )}
                                      {group.subLabel
                                        ? ` (${group.subLabel})`
                                        : ""}
                                    </p>
                                    {hasPreset && (
                                      <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-600 dark:bg-purple-950/20">
                                        From Preset
                                      </span>
                                    )}
                                  </div>
                                  {hasPreset && (
                                    <div
                                      className={cn(
                                        "mt-2 rounded-lg border border-border/40 bg-secondary/20 px-4 py-3",
                                        !selectedPreset && "border-dashed",
                                      )}
                                    >
                                      <div className="sm:w-[calc(50%-0.75rem)]">
                                        <PresetSelector
                                          label=""
                                          presets={sorted}
                                          selectedId={
                                            presetSelections[
                                              group.presetCategoryId
                                            ]
                                          }
                                          favouriteSet={favouriteSet}
                                          orgId={orgId as Id<"organizations">}
                                          onSelect={(id) => {
                                            // MOR-2342 QA-08 — switching preset
                                            // changes the defaults only; manual
                                            // edits (blanks included) survive
                                            // until explicitly reset below.
                                            setPresetSelections((prev) => ({
                                              ...prev,
                                              [group.presetCategoryId]: id,
                                            }));
                                          }}
                                        />
                                        {countOverridden(
                                          fieldOverrides,
                                          group.category.fields.map(
                                            (f) => f.key,
                                          ),
                                        ) > 0 && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              resetCategoryOverrides(
                                                group.category.fields.map(
                                                  (f) => f.key,
                                                ),
                                              )
                                            }
                                            className="mt-2 text-xs text-primary/50 underline-offset-2 hover:text-primary hover:underline"
                                          >
                                            Reset edited fields to this preset
                                          </button>
                                        )}
                                      </div>
                                      {visibleGroupFields.length > 0 && (
                                        <div className="mt-3 grid gap-x-6 gap-y-4 sm:grid-cols-2">
                                          {visibleGroupFields.map((field) => {
                                            const value =
                                              mergedFields[field.key] ?? "";
                                            const source = resolveReviewSource(
                                              field.key,
                                            );
                                            return (
                                              <FieldReviewInput
                                                key={field.key}
                                                field={{
                                                  ...field,
                                                  label: resolveFieldLabel(
                                                    field.key,
                                                    field.label,
                                                    job?.appointmentType,
                                                  ),
                                                }}
                                                value={value}
                                                source={source}
                                                onChange={(v) =>
                                                  handleFieldChange(
                                                    field.key,
                                                    v,
                                                  )
                                                }
                                                practitionerOptions={
                                                  practitionerOptions
                                                }
                                                presetPractitionerOptions={
                                                  presetPractitionerOptions
                                                }
                                                primaryPractitionerName={
                                                  mergedFields[
                                                    "practitioner_name"
                                                  ]
                                                }
                                                companyNameOptions={
                                                  companyNameOptions
                                                }
                                                hasOverviewSummary={
                                                  hasOverviewSummary
                                                }
                                                jobId={jobId}
                                              />
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {!hasPreset &&
                                    visibleGroupFields.length > 0 && (
                                      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                                        {visibleGroupFields.map((field) => {
                                          const value =
                                            mergedFields[field.key] ?? "";
                                          const source = resolveReviewSource(
                                            field.key,
                                          );
                                          return (
                                            <FieldReviewInput
                                              key={field.key}
                                              field={{
                                                ...field,
                                                label: resolveFieldLabel(
                                                  field.key,
                                                  field.label,
                                                  job?.appointmentType,
                                                ),
                                              }}
                                              value={value}
                                              source={source}
                                              onChange={(v) =>
                                                handleFieldChange(field.key, v)
                                              }
                                              practitionerOptions={
                                                practitionerOptions
                                              }
                                              presetPractitionerOptions={
                                                presetPractitionerOptions
                                              }
                                              primaryPractitionerName={
                                                mergedFields[
                                                  "practitioner_name"
                                                ]
                                              }
                                              companyNameOptions={
                                                companyNameOptions
                                              }
                                              hasOverviewSummary={
                                                hasOverviewSummary
                                              }
                                              jobId={jobId}
                                            />
                                          );
                                        })}
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                {/* Per-document cards — fields unique to each template */}
                {perDocumentCards.map(
                  ({ type, canonicalFields, extras, customCategoryIds }) => {
                    if (!showDocument(type.key)) return null;
                    const tplDefaults =
                      templateDefaultsByKey.get(type.key) ?? {};
                    const templatePresetCategoryId =
                      type.key === "form_gallagher" ? "gallagher" : null;

                    // Fan-out templates auto-fill contact-derived fields per
                    // recipient at generation time; hide those rows from the
                    // per-doc review (they'd otherwise render as misleading
                    // empty inputs since `mergedFields` doesn't include
                    // contact data — there's no single contact at this
                    // step) and surface the per-recipient list instead.
                    const fanOutContactsForCard =
                      matchingContactsByTemplate.get(type.key);
                    const tickedForCard = contactSelectionsByTemplate.get(
                      type.key,
                    );
                    const fanOutTickedContacts =
                      fanOutContactsForCard?.filter((c) =>
                        tickedForCard?.has(c._id),
                      ) ?? [];
                    const isFanOut = fanOutTickedContacts.length > 0;
                    // Explicit `contact:*` fields are always resolved from a
                    // contact record (per fan-out recipient) — never editable as
                    // a single per-document value, so drop them from the review.
                    // Legacy `recipient_*` fields are only hidden when fanning out.
                    // Conditional fields (MOR-885): dependent fields whose
                    // controlling answer doesn't match are hidden until it does.
                    const suppressedForCard = suppressedKeysByTemplate.get(
                      type.key,
                    );
                    const visibleCanonicalFields = canonicalFields.filter(
                      (f) =>
                        !isContactFieldKey(f.key) &&
                        !suppressedForCard?.has(f.key) &&
                        (!isFanOut || !CONTACT_DERIVABLE_FIELD_KEYS.has(f.key)),
                    );
                    const visibleExtras = extras.filter(
                      (f) =>
                        !isContactFieldKey(f.key) &&
                        !suppressedForCard?.has(f.key),
                    );
                    const templatePresets = templatePresetCategoryId
                      ? (() => {
                          const catPresets = presetsByCategory.get(
                            templatePresetCategoryId,
                          );
                          if (!catPresets?.length) return [];
                          return [...catPresets].sort((a, b) => {
                            const aFav = favouriteSet.has(a._id) ? 0 : 1;
                            const bFav = favouriteSet.has(b._id) ? 0 : 1;
                            if (aFav !== bFav) return aFav - bFav;
                            return a.name.localeCompare(b.name);
                          });
                        })()
                      : [];

                    // Custom category preset selectors live INSIDE this template's
                    // card when fields from those categories only appear in this one
                    // template — keeps preset choice next to the affected fields.
                    const customCategoryGroups = customCategoryIds
                      .map((catId) => {
                        const cat = customCategoriesGrouped.get(catId);
                        if (!cat) return null;
                        const catPresets = presetsByCategory.get(catId);
                        const sortedPresets = catPresets
                          ? [...catPresets].sort((a, b) => {
                              const aFav = favouriteSet.has(a._id) ? 0 : 1;
                              const bFav = favouriteSet.has(b._id) ? 0 : 1;
                              if (aFav !== bFav) return aFav - bFav;
                              return a.name.localeCompare(b.name);
                            })
                          : [];
                        return {
                          id: catId,
                          label: cat.label,
                          presets: sortedPresets,
                        };
                      })
                      .filter(
                        (
                          c,
                        ): c is {
                          id: string;
                          label: string;
                          presets: FieldPresetRow[];
                        } => c !== null,
                      );

                    const cardCollapsed =
                      collapsedCards.has(type.key) && !searchActive;
                    const cardStats = reviewFieldStats.perCard.get(
                      type.key,
                    ) ?? {
                      total: 0,
                      needsInput: 0,
                    };

                    return (
                      <div
                        key={type.key}
                        className={GROUP_CARD_CLASS}
                        ref={(el) => {
                          reviewCardRefs.current[type.key] = el;
                        }}
                      >
                        <div className="flex items-center justify-between gap-3 border-b border-border/50 px-5 py-3">
                          <button
                            type="button"
                            onClick={() => toggleCardCollapsed(type.key)}
                            className="-my-1 flex min-w-0 flex-1 items-center gap-2 rounded-md py-1 pr-2 text-left transition hover:opacity-80"
                          >
                            <ChevronDown
                              className={cn(
                                "size-4 shrink-0 text-primary/40 transition-transform",
                                cardCollapsed && "-rotate-90",
                              )}
                            />
                            <type.icon className="size-4 shrink-0 text-primary/50" />
                            <span className="truncate text-sm font-medium text-primary/80">
                              {type.name}
                            </span>
                            {cardStats.total > 0 && (
                              <span className="ml-1 flex shrink-0 items-center gap-2 text-xs text-primary/40">
                                <span>
                                  {cardStats.total} field
                                  {cardStats.total === 1 ? "" : "s"}
                                </span>
                                {cardStats.needsInput > 0 && (
                                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/30">
                                    {cardStats.needsInput} need input
                                  </span>
                                )}
                              </span>
                            )}
                          </button>
                          {/* Per-document letterhead picker. The label shows
                           *  whatever the document will resolve to right now —
                           *  honouring the wizard override → template override →
                           *  firm default → single-bundle → none chain — so the
                           *  user can see what they're about to generate. */}
                          {!type.thirdPartyForm &&
                            (() => {
                              const wizardOverride =
                                letterheadOverrides[type.key] ?? null;
                              const dbTemplate = templates?.find(
                                (t) => t.templateKey === type.key,
                              );
                              const templateOverride =
                                (dbTemplate?.letterheadOverride ??
                                  null) as WizardLetterheadOverride;
                              const effective =
                                wizardOverride ?? templateOverride;
                              // Derive the trigger label. Wizard pick wins; otherwise
                              // surface the template default if any, with a "(template
                              // default)" hint so the user knows it can be overridden.
                              const triggerLabel = (() => {
                                if (wizardOverride?.kind === "none")
                                  return "No letterhead";
                                if (wizardOverride?.kind === "letterhead")
                                  return wizardOverride.bundleName;
                                if (wizardOverride?.kind === "variant") {
                                  const v = letterheadVariantsRaw?.find(
                                    (x) => x._id === wizardOverride.variantId,
                                  );
                                  return v?.name ?? "Variant";
                                }
                                // No wizard override → show what would apply by default.
                                if (templateOverride?.kind === "none")
                                  return "No letterhead (template default)";
                                if (templateOverride?.kind === "letterhead")
                                  return `${templateOverride.bundleName} (template default)`;
                                if (templateOverride?.kind === "variant") {
                                  const v = letterheadVariantsRaw?.find(
                                    (x) => x._id === templateOverride.variantId,
                                  );
                                  return v?.name
                                    ? `${v.name} (template default)`
                                    : "Template default";
                                }
                                if (
                                  brandingProfile?.firmDefaultLetterheadBundleName
                                ) {
                                  return `${brandingProfile.firmDefaultLetterheadBundleName} (firm default)`;
                                }
                                if (wizardBundleNames.length === 1)
                                  return wizardBundleNames[0];
                                return "No letterhead";
                              })();
                              return (
                                <LetterheadOverridePicker
                                  currentOverride={effective}
                                  firmDefaultBundleName={
                                    brandingProfile?.firmDefaultLetterheadBundleName ??
                                    null
                                  }
                                  favouriteBundleNames={
                                    brandingProfile?.favouriteLetterheadBundleNames ??
                                    []
                                  }
                                  allBundles={wizardBundleNames}
                                  allVariants={letterheadVariantsRaw ?? []}
                                  onChange={(next) =>
                                    setLetterheadOverrides((prev) => ({
                                      ...prev,
                                      [type.key]: next,
                                    }))
                                  }
                                  triggerLabel={triggerLabel}
                                  triggerTitle="Choose a letterhead for this document"
                                  clearLabel={
                                    templateOverride
                                      ? "Use template default"
                                      : brandingProfile?.firmDefaultLetterheadBundleName
                                        ? `Use firm default (${brandingProfile.firmDefaultLetterheadBundleName})`
                                        : "Clear override"
                                  }
                                />
                              );
                            })()}
                        </div>
                        {!cardCollapsed && (
                          <>
                            {/* Variant picker — surfaces only when this template's body
                  references variant blocks. Defaults to the job-wide
                  selection but can be overridden per document so the user
                  can swap (e.g.) which practitioner appears in this letter
                  without affecting the others. */}
                            {(() => {
                              const dbTemplate = templates?.find(
                                (t) => t.templateKey === type.key,
                              );
                              const html = dbTemplate?.templateHtml as
                                string | undefined;
                              if (!html) return null;
                              return (
                                <div className="border-b border-border/50 bg-secondary/20 px-5 py-3">
                                  <VariantPicker
                                    groups={variantGroupsForPicker}
                                    templateHtml={html}
                                    jobSelections={job?.variantSelections}
                                    docSelections={variantOverrides[type.key]}
                                    bindingAware
                                    onChange={(slug, value) =>
                                      setVariantOverride(type.key, slug, value)
                                    }
                                    onClear={(slug) =>
                                      clearVariantOverride(type.key, slug)
                                    }
                                    blocks={buildVariantBlocks(type.key, html)}
                                    blockSnippetSelections={
                                      snippetOverrides[type.key]
                                    }
                                    blockOverrideSnippets={(b) => {
                                      const jobVS = job?.variantSelections as
                                        Record<string, string> | undefined;
                                      const merged = {
                                        ...(jobVS ?? {}),
                                        ...(variantOverrides[type.key] ?? {}),
                                      };
                                      const opt = merged[b.groupSlug];
                                      return variantSnippetsForPicker
                                        .filter(
                                          (s) =>
                                            s.groupSlug === b.groupSlug &&
                                            (!opt || s.optionValue === opt),
                                        )
                                        .map((s) => ({
                                          _id: s._id,
                                          name: s.name,
                                        }));
                                    }}
                                    onBlockSnippetChange={(vbId, snippetId) =>
                                      setSnippetOverride(
                                        type.key,
                                        vbId,
                                        snippetId,
                                      )
                                    }
                                    onBlockSnippetClear={(vbId) =>
                                      clearSnippetOverride(type.key, vbId)
                                    }
                                  />
                                </div>
                              );
                            })()}
                            <div className="p-5">
                              {visibleCanonicalFields.length === 0 &&
                                visibleExtras.length === 0 &&
                                customCategoryGroups.length === 0 &&
                                templatePresets.length === 0 && (
                                  <p className="text-xs text-primary/40">
                                    Uses common fields only — pick a letterhead
                                    above if you want to override the default.
                                  </p>
                                )}
                              {templatePresets.length > 0 &&
                                templatePresetCategoryId && (
                                  <div className="mb-4 rounded-lg border border-border/40 bg-secondary/20 px-4 py-3">
                                    <div className="sm:w-[calc(50%-0.75rem)]">
                                      <PresetSelector
                                        label="Insurance defaults preset"
                                        presets={templatePresets}
                                        selectedId={
                                          presetSelections[
                                            templatePresetCategoryId
                                          ]
                                        }
                                        favouriteSet={favouriteSet}
                                        orgId={orgId as Id<"organizations">}
                                        onSelect={(id) => {
                                          // MOR-2342 QA-08 — keep manual edits
                                          // across a preset switch.
                                          setPresetSelections((prev) => ({
                                            ...prev,
                                            [templatePresetCategoryId]: id,
                                          }));
                                        }}
                                      />
                                      {(() => {
                                        const keys =
                                          FIELD_CATEGORIES.find(
                                            (c) =>
                                              c.id === templatePresetCategoryId,
                                          )?.fields.map((f) => f.key) ?? [];
                                        return countOverridden(
                                          fieldOverrides,
                                          keys,
                                        ) > 0 ? (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              resetCategoryOverrides(keys)
                                            }
                                            className="mt-2 text-xs text-primary/50 underline-offset-2 hover:text-primary hover:underline"
                                          >
                                            Reset edited fields to this preset
                                          </button>
                                        ) : null;
                                      })()}
                                    </div>
                                  </div>
                                )}
                              {isFanOut &&
                                (() => {
                                  const contactFieldParts = [
                                    ...canonicalFields.filter((f) =>
                                      isContactFieldKey(f.key),
                                    ),
                                    ...extras.filter((f) =>
                                      isContactFieldKey(f.key),
                                    ),
                                  ]
                                    .map(
                                      (f) => parseContactFieldKey(f.key)?.part,
                                    )
                                    .filter(
                                      (p): p is ContactFieldPart => p != null,
                                    );

                                  return (
                                    <FanOutContactPreview
                                      contacts={fanOutTickedContacts}
                                      expectedParts={contactFieldParts}
                                      canEditFirmContacts={canEditFirmContacts}
                                      isExternalAdminTemplate={externalAdminTemplateKeys.has(
                                        type.key,
                                      )}
                                      notice={
                                        jobStateFallbackTemplateKeys.has(
                                          type.key,
                                        )
                                          ? "This template matches firm contacts to the job's state, but this job has no registered office or principal place of business state recorded yet — contacts in every state are shown."
                                          : undefined
                                      }
                                    />
                                  );
                                })()}
                              {/* Second-tier source-link preview — surfaces when the
                  template body references `contact_job_<source>_*`
                  fields. Built from the precomputed
                  `sourceFanOutByTemplate` map, filtered to the contacts
                  the user has actually ticked. Each row exposes a
                  Select for last-minute "swap which security interest
                  this letter uses" overrides. */}
                              {isFanOut &&
                                (() => {
                                  const perContactRows =
                                    sourceFanOutByTemplate.get(type.key);
                                  if (
                                    !perContactRows ||
                                    perContactRows.size === 0
                                  )
                                    return null;
                                  const templateParts =
                                    templateSourceFieldPartsByKey.get(
                                      type.key,
                                    ) ?? new Map();

                                  const previewContacts: SourceFanOutPreviewContact[] =
                                    [];
                                  for (const contact of fanOutTickedContacts) {
                                    if (contact.domain !== "job") continue;
                                    const rows = perContactRows.get(
                                      contact._id,
                                    );
                                    if (!rows || rows.length === 0) continue;

                                    // Pre-resolve the natural-row field values up-front so
                                    // the preview component can render editable cards
                                    // without re-walking the raw source payload on every
                                    // re-render.
                                    const resolvedRows: SourceFanOutEntry[] =
                                      rows.map((r) => ({
                                        sourceKind: r.sourceKind,
                                        link: r.link,
                                        resolvedFields: r.row
                                          ? resolveContactSourceFields(
                                              r.sourceKind,
                                              r.row,
                                              "job",
                                              r.link?.sourceId,
                                            )
                                          : {},
                                      }));

                                    // Build the per-kind option list for the override
                                    // Select from this contact's full source map (not just
                                    // the natural rows shown by default) so a row can be
                                    // swapped to any sibling link of the same kind. Each
                                    // option carries its OWN resolved field values so
                                    // swapping immediately surfaces the swapped link's
                                    // defaults in the editable cards.
                                    const allLinksByKind = new Map<
                                      ContactSourceKind,
                                      Array<{
                                        sourceId: string;
                                        roleLabel: string;
                                        detail?: string;
                                        amount?: number;
                                        resolvedFields: Record<string, string>;
                                      }>
                                    >();
                                    const sourcesForContact =
                                      jobContactSourceRows?.[
                                        contact._id as Id<"jobContacts">
                                      ];
                                    if (sourcesForContact) {
                                      for (const kind of Object.keys(
                                        sourcesForContact,
                                      ) as ContactSourceKind[]) {
                                        const entries =
                                          sourcesForContact[kind] ?? [];
                                        if (entries.length === 0) continue;
                                        allLinksByKind.set(
                                          kind,
                                          entries.map(
                                            (entry: {
                                              link: {
                                                sourceId: string;
                                                roleLabel: string;
                                                detail?: string;
                                                amount?: number;
                                              };
                                              row: unknown;
                                            }) => ({
                                              sourceId: entry.link.sourceId,
                                              roleLabel: entry.link.roleLabel,
                                              detail: entry.link.detail,
                                              amount: entry.link.amount,
                                              resolvedFields: entry.row
                                                ? resolveContactSourceFields(
                                                    kind,
                                                    entry.row,
                                                    "job",
                                                    entry.link.sourceId,
                                                  )
                                                : {},
                                            }),
                                          ),
                                        );
                                      }
                                    }

                                    previewContacts.push({
                                      contactId: contact._id,
                                      displayName: contact.displayName,
                                      roleLabel: fanContactRoleLabel(contact),
                                      role: contact.role as ContactRole,
                                      rows: resolvedRows,
                                      allLinksByKind,
                                    });
                                  }

                                  if (previewContacts.length === 0) return null;
                                  return (
                                    <SourceFanOutPreview
                                      templateKey={type.key}
                                      contacts={previewContacts}
                                      templateSourceFieldParts={templateParts}
                                      linkOverrides={sourceLinkOverrides}
                                      fieldOverrides={sourceFieldOverrides}
                                      onLinkOverride={setSourceLinkOverride}
                                      onFieldOverride={setSourceFieldOverride}
                                      buildRowKey={buildSourceFanOutRowKey}
                                    />
                                  );
                                })()}
                              {(() => {
                                // Merge canonical + extra fields into one uniform list so
                                // they can be filtered (search / needs-input) and grouped
                                // into labelled category sections. Small forms with a single
                                // category render without subheadings, unchanged.
                                type ReviewItem = {
                                  key: string;
                                  field: CanonicalField;
                                  value: string;
                                  source: FieldReviewSource;
                                  selectOptions?: string[];
                                };
                                const canonicalItems: ReviewItem[] =
                                  visibleCanonicalFields.map((field) => {
                                    const tplDefault = tplDefaults[field.key];
                                    // Absent falls back to the template's
                                    // example value; an explicit "" is a
                                    // deliberate blank and stays blank
                                    // (MOR-2342 QA-08).
                                    return {
                                      key: field.key,
                                      field,
                                      value:
                                        mergedFields[field.key] ??
                                        tplDefault ??
                                        "",
                                      source: resolveReviewSource(
                                        field.key,
                                        tplDefault,
                                      ),
                                    };
                                  });
                                const cardGroupDefaults =
                                  templateGroupDefaultsByKey.get(type.key) ??
                                  {};
                                const extraItems: ReviewItem[] =
                                  visibleExtras.map((field) => {
                                    const explicit =
                                      fieldOverrides[field.key] ??
                                      mergedFields[field.key] ??
                                      "";
                                    // MOR-1003 — a checkbox-group question with a template
                                    // default renders pre-answered (and generates that way);
                                    // picking another option, or clearing it, still wins.
                                    const value =
                                      explicit ||
                                      (cardGroupDefaults[field.key] ?? "");
                                    const source: FieldReviewSource = explicit
                                      ? "edited"
                                      : value
                                        ? "template_default"
                                        : "empty";
                                    return {
                                      key: field.key,
                                      field: field as CanonicalField,
                                      value,
                                      source,
                                      selectOptions: (
                                        field as { options?: string[] }
                                      ).options,
                                    };
                                  });
                                const filterActive =
                                  reviewSearch.trim() !== "" ||
                                  showOnlyNeedsInput;
                                const filteredItems = [
                                  ...canonicalItems,
                                  ...extraItems,
                                ].filter(
                                  (it) =>
                                    matchesReviewSearch(it.field) &&
                                    (!showOnlyNeedsInput ||
                                      it.source === "empty"),
                                );

                                if (filteredItems.length === 0) {
                                  if (
                                    filterActive &&
                                    (visibleCanonicalFields.length > 0 ||
                                      visibleExtras.length > 0)
                                  ) {
                                    return (
                                      <p className="text-xs text-primary/40">
                                        No fields match the current filter.
                                      </p>
                                    );
                                  }
                                  return null;
                                }

                                const renderItem = (item: ReviewItem) => (
                                  <FieldReviewInput
                                    key={item.key}
                                    field={item.field}
                                    value={item.value}
                                    source={item.source}
                                    onChange={(v) =>
                                      handleFieldChange(item.key, v)
                                    }
                                    practitionerOptions={practitionerOptions}
                                    presetPractitionerOptions={
                                      presetPractitionerOptions
                                    }
                                    primaryPractitionerName={
                                      mergedFields["practitioner_name"]
                                    }
                                    companyNameOptions={companyNameOptions}
                                    selectOptions={item.selectOptions}
                                    hasOverviewSummary={hasOverviewSummary}
                                    jobId={jobId}
                                  />
                                );

                                // Conditional fields (MOR-885) render nested inside the cell
                                // of the field that controls them, so a follow-up question
                                // reads as belonging to its parent instead of landing in an
                                // unrelated column. Dependents whose controlling field isn't
                                // in the same section keep a "Shown because …" line instead.
                                const cardConditions = conditionsByTemplate.get(
                                  type.key,
                                );
                                const fieldLabels = new Map<string, string>();
                                for (const f of [
                                  ...canonicalFields,
                                  ...extras,
                                ]) {
                                  fieldLabels.set(f.key, f.label);
                                }
                                const labelForKey = (key: string) =>
                                  fieldLabels.get(key) ??
                                  lookupField(key)?.label ??
                                  key;

                                // Dependents the current answers keep hidden, surfaced as a
                                // one-line hint on the controlling field so the user knows a
                                // follow-up exists before answering.
                                const hiddenDependents = new Map<
                                  string,
                                  DependentHint[]
                                >();
                                if (cardConditions) {
                                  for (const [
                                    depKey,
                                    condition,
                                  ] of Object.entries(cardConditions)) {
                                    if (!fieldLabels.has(depKey)) continue;
                                    if (!suppressedForCard?.has(depKey))
                                      continue;
                                    // A parent that is itself hidden can't host the hint.
                                    if (
                                      suppressedForCard.has(condition.fieldKey)
                                    )
                                      continue;
                                    const list =
                                      hiddenDependents.get(
                                        condition.fieldKey,
                                      ) ?? [];
                                    list.push({
                                      label: labelForKey(depKey),
                                      values: condition.values,
                                    });
                                    hiddenDependents.set(
                                      condition.fieldKey,
                                      list,
                                    );
                                  }
                                }

                                const buildNodes = (
                                  items: ReviewItem[],
                                ): ConditionalFieldNode[] => {
                                  const nestedKeys = new Set<string>();
                                  const childrenByParent = new Map<
                                    string,
                                    ReviewItem[]
                                  >();
                                  if (cardConditions) {
                                    const present = new Set(
                                      items.map((it) => it.key),
                                    );
                                    for (const item of items) {
                                      const condition =
                                        cardConditions[item.key];
                                      if (
                                        !condition ||
                                        !present.has(condition.fieldKey)
                                      )
                                        continue;
                                      nestedKeys.add(item.key);
                                      const list =
                                        childrenByParent.get(
                                          condition.fieldKey,
                                        ) ?? [];
                                      list.push(item);
                                      childrenByParent.set(
                                        condition.fieldKey,
                                        list,
                                      );
                                    }
                                  }
                                  const toNode = (
                                    item: ReviewItem,
                                  ): ConditionalFieldNode => {
                                    const condition =
                                      cardConditions?.[item.key];
                                    return {
                                      key: item.key,
                                      content: renderItem(item),
                                      revealedBy: condition
                                        ? {
                                            label: labelForKey(
                                              condition.fieldKey,
                                            ),
                                            values: condition.values,
                                          }
                                        : undefined,
                                      children: (
                                        childrenByParent.get(item.key) ?? []
                                      ).map(toNode),
                                      hiddenDependents:
                                        hiddenDependents.get(item.key) ?? [],
                                    };
                                  };
                                  return items
                                    .filter((item) => !nestedKeys.has(item.key))
                                    .map(toNode);
                                };

                                const renderNodes = (items: ReviewItem[]) =>
                                  buildNodes(items).map((node) => (
                                    <ConditionalFieldCell
                                      key={node.key}
                                      node={node}
                                    />
                                  ));

                                const groups =
                                  groupFieldsByCategory(filteredItems);
                                if (groups.length <= 1) {
                                  return (
                                    <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                                      {renderNodes(filteredItems)}
                                    </div>
                                  );
                                }
                                return (
                                  <div className="space-y-5">
                                    {groups.map((group) => (
                                      <div key={group.categoryId}>
                                        <p className="mb-2 text-xs font-medium uppercase tracking-wider text-primary/40">
                                          {group.label}
                                        </p>
                                        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                                          {renderNodes(group.fields)}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                              {customCategoryGroups.length > 0 &&
                                customCategoryGroups.some(
                                  (g) => g.presets.length > 0,
                                ) && (
                                  <div className="mt-4 space-y-3 border-t border-border/40 pt-4">
                                    {customCategoryGroups.map(
                                      (group) =>
                                        group.presets.length > 0 && (
                                          <div
                                            key={group.id}
                                            className="rounded-lg border border-border/40 bg-secondary/20 px-4 py-3"
                                          >
                                            <div className="mb-2 flex items-baseline justify-between gap-2">
                                              <p className="text-xs font-medium uppercase tracking-wider text-primary/40">
                                                {group.label}
                                              </p>
                                              <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-600 dark:bg-purple-950/20">
                                                From Preset
                                              </span>
                                            </div>
                                            <div className="sm:w-[calc(50%-0.75rem)]">
                                              <PresetSelector
                                                label=""
                                                presets={group.presets}
                                                selectedId={
                                                  presetSelections[group.id]
                                                }
                                                favouriteSet={favouriteSet}
                                                orgId={
                                                  orgId as Id<"organizations">
                                                }
                                                onSelect={(id) => {
                                                  // MOR-2342 QA-08 — keep
                                                  // manual edits across a
                                                  // preset switch.
                                                  setPresetSelections(
                                                    (prev) => ({
                                                      ...prev,
                                                      [group.id]: id,
                                                    }),
                                                  );
                                                }}
                                              />
                                              {(() => {
                                                const keys =
                                                  customCategoriesGrouped
                                                    .get(group.id)
                                                    ?.fields.map(
                                                      (f) => f.key,
                                                    ) ?? [];
                                                return countOverridden(
                                                  fieldOverrides,
                                                  keys,
                                                ) > 0 ? (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      resetCategoryOverrides(
                                                        keys,
                                                      )
                                                    }
                                                    className="mt-2 text-xs text-primary/50 underline-offset-2 hover:text-primary hover:underline"
                                                  >
                                                    Reset edited fields to this
                                                    preset
                                                  </button>
                                                ) : null;
                                              })()}
                                            </div>
                                          </div>
                                        ),
                                    )}
                                  </div>
                                )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  },
                )}

                {/* Sticky action bar — pinned to the bottom of the viewport so the
              user never has to scroll to the end of a long form to advance. */}
                <div className="sticky bottom-0 z-[9] -mx-1 flex items-center justify-between gap-3 rounded-xl border border-border/50 bg-background/95 px-4 py-3 shadow-sm backdrop-blur-sm">
                  <Button variant="outline" onClick={() => setStep("select")}>
                    <ChevronLeft className="mr-1.5 size-3.5" />
                    Back
                  </Button>
                  {grandNeedsInput > 0 && (
                    <span className="hidden text-xs text-primary/40 sm:inline">
                      {grandNeedsInput} field{grandNeedsInput === 1 ? "" : "s"}{" "}
                      still need input
                    </span>
                  )}
                  <Button
                    onClick={() =>
                      setStep(
                        hasSelectedAttachments ? "attachments" : "confirm",
                      )
                    }
                  >
                    {hasSelectedAttachments
                      ? "Next: Attachments"
                      : "Next: Confirm"}
                    <ArrowRight className="ml-1.5 size-3.5" />
                  </Button>
                </div>
              </div>
            );
          })()}

        {/* Step 3: Attachment Review */}
        {step === "attachments" && (
          <div className="space-y-6">
            <div className={GROUP_CARD_CLASS}>
              <div className="border-b border-border/50 px-5 py-4">
                <h4 className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <Paperclip className="size-4 text-primary/40" />
                  Review Attachments
                </h4>
                <p className="mt-1 text-xs text-primary/50">
                  These files will be saved separately from merge fields and
                  attached to the generated precedent.
                </p>
              </div>
              <div className="space-y-4 p-5">
                {Array.from(selectedTemplateAttachments.entries()).map(
                  ([templateKey, requirements]) => {
                    const type = allTemplateTypes.find(
                      (item) => item.key === templateKey,
                    );
                    return (
                      <div
                        key={templateKey}
                        className="rounded-lg border border-border/60 bg-background p-4"
                      >
                        <div className="mb-3">
                          <h5 className="text-sm font-medium text-primary">
                            {type?.name ?? templateKey}
                          </h5>
                          <p className="text-xs text-primary/45">
                            {requirements.length} attachment
                            {requirements.length === 1 ? "" : "s"} configured
                          </p>
                        </div>

                        <div className="space-y-3">
                          {requirements.map((requirement, index) => {
                            const slotKey = buildAttachmentSlotKey(
                              requirement.attachmentKey,
                              index,
                            );
                            const selection =
                              attachmentSelections[templateKey]?.[slotKey];
                            const candidates =
                              attachmentCandidatesByKey.get(
                                requirement.attachmentKey,
                              ) ?? [];
                            const selectionKey = `${templateKey}:${slotKey}`;
                            const isUploading =
                              uploadingAttachmentKey === selectionKey;
                            const generatedPrecedentOptions =
                              allTemplateTypes.filter(
                                (item) => item.key !== templateKey,
                              );
                            const selectedGeneratedType = allTemplateTypes.find(
                              (item) =>
                                item.key === selection?.generatedTemplateKey,
                            );
                            // Custom (named) attachments are resolved at the job
                            // level, so their status/file come from the shared
                            // reference map rather than this template's local slot.
                            // EXCEPT generated-precedent slots (MOR-779): those
                            // self-resolve to a sibling document generated in this
                            // run, so the shared job-document reference map must
                            // not override their status or offer its picker.
                            // Template-default slots self-resolve to the file that
                            // ships with the template, and upload slots take a
                            // per-slot file during generation — neither is a
                            // shared job-document reference, so a job-level
                            // "skipped" row must not clobber them either.
                            const customRefKey =
                              requirement.attachmentKey === "custom" &&
                              requirement.sourceKind !==
                                "generated_precedent" &&
                              requirement.sourceKind !== "template_default" &&
                              requirement.sourceKind !== "upload"
                                ? buildAttachmentReferenceKey({
                                    attachmentKey: "custom",
                                    name: requirement.name,
                                    customRef: requirement.customRef,
                                  })
                                : null;
                            const customResolution = customRefKey
                              ? (resolutionByRefKey.get(customRefKey) ?? null)
                              : null;
                            // For `custom` attachments the shared-reference
                            // resolution wins when present, but otherwise fall back
                            // to the local selection — which carries any template
                            // default file. Generation reads the same selection, so
                            // this keeps the badge honest (MOR-568): a custom slot
                            // with a template default reads "Attached", not Missing.
                            const effectiveStatus =
                              requirement.attachmentKey === "custom"
                                ? (customResolution?.status ??
                                  selection?.status ??
                                  "missing")
                                : selection?.status;
                            const effectiveFileName =
                              requirement.attachmentKey === "custom"
                                ? (customResolution?.fileName ??
                                  selection?.fileName)
                                : selection?.fileName;

                            return (
                              <div
                                key={slotKey}
                                className="rounded-md border border-border/50 p-3"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-primary/80">
                                        {requirement.name}
                                      </span>
                                      {requirement.required && (
                                        <Badge
                                          variant="secondary"
                                          className="text-[10px]"
                                        >
                                          Required
                                        </Badge>
                                      )}
                                      {effectiveStatus === "resolved" ? (
                                        <Badge className="text-[10px]">
                                          Attached
                                        </Badge>
                                      ) : effectiveStatus === "skipped" ? (
                                        <Badge
                                          variant="secondary"
                                          className="text-[10px]"
                                        >
                                          Skipped
                                        </Badge>
                                      ) : (
                                        <Badge
                                          variant="destructive"
                                          className="text-[10px]"
                                        >
                                          Missing
                                        </Badge>
                                      )}
                                    </div>
                                    {requirement.description && (
                                      <p className="mt-1 text-xs text-primary/45">
                                        {requirement.description}
                                      </p>
                                    )}
                                  </div>
                                  {effectiveFileName && (
                                    <span
                                      className="max-w-[240px] shrink-0 truncate text-xs text-primary/50"
                                      title={effectiveFileName}
                                    >
                                      {effectiveFileName}
                                    </span>
                                  )}
                                </div>

                                {requirement.sourceKind ===
                                  "template_default" && (
                                  <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
                                    <span className="font-medium">
                                      Template default file:
                                    </span>{" "}
                                    {selection?.fileName ??
                                      requirement.fileName ??
                                      "No default file configured"}
                                  </div>
                                )}

                                {requirement.sourceKind ===
                                  "generated_precedent" && (
                                  <div className="mt-3">
                                    <Label className="text-xs">
                                      Generated precedent
                                    </Label>
                                    <Popover modal>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="outline"
                                          role="combobox"
                                          className="mt-1 w-full justify-between overflow-hidden text-sm font-normal"
                                        >
                                          <span className="truncate text-left">
                                            {selectedGeneratedType?.name ??
                                              "Select generated document"}
                                          </span>
                                          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        className="w-[var(--radix-popover-trigger-width)] p-0"
                                        align="start"
                                        onWheel={(e) => e.stopPropagation()}
                                      >
                                        <Command
                                          onKeyDown={(e) => e.stopPropagation()}
                                        >
                                          <CommandInput placeholder="Search templates..." />
                                          <CommandList className="max-h-64 overscroll-contain">
                                            <CommandEmpty>
                                              No matching template.
                                            </CommandEmpty>
                                            {generatedPrecedentOptions.map(
                                              (option) => (
                                                <CommandItem
                                                  key={option.key}
                                                  value={option.name}
                                                  onSelect={() =>
                                                    handleGeneratedPrecedentChange(
                                                      templateKey,
                                                      slotKey,
                                                      requirement,
                                                      option.key,
                                                    )
                                                  }
                                                >
                                                  <Check
                                                    className={cn(
                                                      "mr-2 h-3 w-3 shrink-0",
                                                      selection?.generatedTemplateKey ===
                                                        option.key
                                                        ? "opacity-100"
                                                        : "opacity-0",
                                                    )}
                                                  />
                                                  <span className="truncate">
                                                    {option.name}
                                                  </span>
                                                </CommandItem>
                                              ),
                                            )}
                                          </CommandList>
                                        </Command>
                                      </PopoverContent>
                                    </Popover>
                                    {generatedPrecedentOptions.length === 0 && (
                                      <p className="mt-1 text-xs text-primary/45">
                                        Select another template in step 1 to
                                        attach it here.
                                      </p>
                                    )}
                                    {selection?.generatedTemplateKey && (
                                      <p className="mt-1 text-xs text-primary/45">
                                        This document will be generated in this
                                        run and attached once export files are
                                        available.
                                      </p>
                                    )}
                                  </div>
                                )}

                                {requirement.attachmentKey === "custom" &&
                                customRefKey ? (
                                  <div className="mt-3 space-y-2">
                                    <SharedReferencePicker
                                      jobId={jobId as Id<"jobs">}
                                      referenceKey={customRefKey}
                                      name={requirement.name}
                                      candidates={referenceCandidates}
                                      resolution={customResolution}
                                      isResolved={
                                        effectiveStatus === "resolved"
                                      }
                                    />
                                    <p className="text-xs text-primary/45">
                                      Note: this is a shared reference - this
                                      document will be applied to every
                                      precedent generated for this job that uses{" "}
                                      <span className="font-medium text-primary/70">
                                        {requirement.name}
                                      </span>
                                      .
                                    </p>
                                  </div>
                                ) : (
                                  <div className="mt-3 grid gap-2 md:grid-cols-[1fr_auto_auto]">
                                    {requirement.sourceKind ===
                                      "job_document" ||
                                    requirement.sourceKind === "upload" ||
                                    requirement.sourceKind === "none" ? (
                                      <Select
                                        value={selection?.documentId ?? ""}
                                        onValueChange={(value) =>
                                          handleAttachmentCandidateChange(
                                            templateKey,
                                            slotKey,
                                            requirement,
                                            value,
                                          )
                                        }
                                      >
                                        <SelectTrigger className="min-w-0 [&>span]:truncate">
                                          <SelectValue
                                            placeholder={
                                              candidates.length > 0
                                                ? "Select existing job file"
                                                : "No matching job files"
                                            }
                                          />
                                        </SelectTrigger>
                                        <SelectContent className="max-w-[min(28rem,90vw)]">
                                          {candidates.map((candidate) => (
                                            <SelectItem
                                              key={candidate.documentId}
                                              value={candidate.documentId}
                                              className="max-w-full"
                                            >
                                              <span
                                                className="block truncate"
                                                title={candidate.fileName}
                                              >
                                                {candidate.fileName}
                                              </span>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <div />
                                    )}

                                    <label className="inline-flex cursor-pointer items-center justify-center rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-primary/70 hover:bg-secondary">
                                      {isUploading ? (
                                        <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                                      ) : (
                                        <Upload className="mr-1.5 size-3.5" />
                                      )}
                                      Upload
                                      <input
                                        type="file"
                                        accept=".pdf,.doc,.docx"
                                        className="sr-only"
                                        onChange={(event) => {
                                          const file =
                                            event.target.files?.[0] ?? null;
                                          handleUploadAttachment(
                                            templateKey,
                                            slotKey,
                                            requirement,
                                            file,
                                          );
                                          event.currentTarget.value = "";
                                        }}
                                      />
                                    </label>

                                    {effectiveStatus === "resolved" &&
                                    (selection?.documentId ||
                                      selection?.generatedTemplateKey) ? (
                                      // An attached document the user supplied — let
                                      // them clear it back to unset. Template defaults
                                      // have no user selection, so they show no action.
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleResetAttachment(
                                            templateKey,
                                            slotKey,
                                            requirement,
                                          )
                                        }
                                      >
                                        Remove
                                      </Button>
                                    ) : effectiveStatus === "skipped" ? (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleResetAttachment(
                                            templateKey,
                                            slotKey,
                                            requirement,
                                          )
                                        }
                                      >
                                        Undo
                                      </Button>
                                    ) : effectiveStatus !== "resolved" ? (
                                      // Skip is only meaningful while the attachment is
                                      // still missing — once something is attached the
                                      // user manages it via the dropdown / Remove.
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleSkipAttachment(
                                            templateKey,
                                            slotKey,
                                            requirement,
                                          )
                                        }
                                      >
                                        Skip
                                      </Button>
                                    ) : (
                                      <div />
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>

            {unresolvedRequiredAttachments.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                Some required attachments are still missing. You can continue,
                but the generated documents will record those attachments as
                missing until you add them.
              </div>
            )}

            <div className="flex items-center justify-between pt-4">
              <Button variant="outline" onClick={() => setStep("review")}>
                <ChevronLeft className="mr-1.5 size-3.5" />
                Back
              </Button>
              <Button onClick={() => setStep("confirm")}>
                Next: Confirm
                <ArrowRight className="ml-1.5 size-3.5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Confirm */}
        {step === "confirm" && (
          <div className="space-y-6">
            <div className={GROUP_CARD_CLASS}>
              <div className="border-b border-border/50 px-5 py-4">
                <h4 className="text-sm font-semibold text-primary">Summary</h4>
                <p className="mt-1 text-xs text-primary/50">
                  {selectedKeys.size} document
                  {selectedKeys.size !== 1 ? "s" : ""} will be generated for{" "}
                  {job.entityName}
                  {confirmAttachmentCount > 0 && (
                    <>
                      {" — "}
                      {confirmSummaryRows.length} main document
                      {confirmSummaryRows.length !== 1 ? "s" : ""} with{" "}
                      {confirmAttachmentCount} attachment
                      {confirmAttachmentCount !== 1 ? "s" : ""}
                    </>
                  )}
                </p>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50 bg-muted/30 text-left text-xs text-primary/50">
                    <th className="px-5 py-2.5 font-medium">Document</th>
                    <th className="px-5 py-2.5 font-medium">Type</th>
                    <th className="px-5 py-2.5 font-medium">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {confirmSummaryRows.map((row) => {
                    const type = allTemplateTypes.find(
                      (t) => t.key === row.key,
                    );
                    return (
                      <Fragment key={row.key}>
                        <tr className="border-b border-border/50 last:border-0">
                          <td className="px-5 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-primary/80">
                                {type?.name ?? row.key}
                              </span>
                              {row.attachmentKeys.length > 0 && (
                                <Badge
                                  variant="outline"
                                  className="gap-1 text-[10px]"
                                >
                                  <Paperclip className="size-2.5" />
                                  {row.attachmentKeys.length}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-2.5">
                            {type?.thirdPartyForm ? (
                              <Badge
                                variant="secondary"
                                className="text-[10px]"
                              >
                                Form
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">
                                Letter
                              </Badge>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-xs text-primary/50">
                            {type?.description ?? ""}
                          </td>
                        </tr>
                        {row.attachmentKeys.map((attachmentKey) => {
                          const attachmentType = allTemplateTypes.find(
                            (t) => t.key === attachmentKey,
                          );
                          return (
                            <tr
                              key={`${row.key}:${attachmentKey}`}
                              className="border-b border-border/50 bg-muted/20 last:border-0"
                            >
                              <td className="px-5 py-2.5">
                                <div className="flex items-center gap-2 border-l-2 border-border pl-4">
                                  <Paperclip className="size-3 shrink-0 text-primary/40" />
                                  <span className="text-sm text-primary/60">
                                    {attachmentType?.name ?? attachmentKey}
                                  </span>
                                  <Badge
                                    variant="secondary"
                                    className="text-[10px]"
                                  >
                                    Attachment
                                  </Badge>
                                </div>
                              </td>
                              <td className="px-5 py-2.5">
                                {attachmentType?.thirdPartyForm ? (
                                  <Badge
                                    variant="secondary"
                                    className="text-[10px]"
                                  >
                                    Form
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px]"
                                  >
                                    Letter
                                  </Badge>
                                )}
                              </td>
                              <td className="px-5 py-2.5 text-xs text-primary/50">
                                {attachmentType?.description ?? ""}
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between pt-4">
              <Button
                variant="outline"
                onClick={() =>
                  setStep(hasSelectedAttachments ? "attachments" : "review")
                }
              >
                <ChevronLeft className="mr-1.5 size-3.5" />
                Back
              </Button>
              <Button onClick={handleGenerate} disabled={generating}>
                {generating ? (
                  <>
                    <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-1.5 size-3.5" />
                    {reselect ? "Regenerate" : "Generate"} {selectedKeys.size}{" "}
                    Document{selectedKeys.size !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            </div>

            <AlertDialog
              open={missingConfirmOpen}
              onOpenChange={setMissingConfirmOpen}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Some contact fields are empty
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {computeAllMissingContactFields().length} contact field
                    {computeAllMissingContactFields().length === 1
                      ? ""
                      : "s"}{" "}
                    referenced by the selected documents are still blank and
                    will appear empty in the generated documents. Go back to
                    fill them in, or generate anyway.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Go back</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      setMissingConfirmOpen(false);
                      void runGenerate();
                    }}
                  >
                    Generate anyway
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preset Selector — dropdown per category
// ---------------------------------------------------------------------------

function PresetSelector({
  label,
  presets,
  selectedId,
  favouriteSet,
  orgId,
  onSelect,
}: {
  label: string;
  presets: FieldPresetRow[];
  selectedId?: Id<"fieldPresets">;
  favouriteSet: Set<Id<"fieldPresets">>;
  orgId: Id<"organizations">;
  onSelect: (id: Id<"fieldPresets">) => void;
}) {
  const [open, setOpen] = useState(false);

  const { mutateAsync: toggleFavourite } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.favourites.toggleFavourite,
    ),
  });

  const selected = presets.find((p) => p._id === selectedId);

  if (presets.length === 1) {
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <span className="text-xs font-medium text-primary/50">{label}</span>
        )}
        <div className="flex h-9 items-center rounded-lg border border-border/50 bg-secondary/30 px-3 text-sm text-primary/70">
          {presets[0].name}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <span className="text-xs font-medium text-primary/50">{label}</span>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-9 items-center justify-between rounded-lg border border-border/50 bg-background px-3 text-sm transition hover:border-border"
          >
            <span className={selected ? "text-primary" : "text-primary/40"}>
              {selected?.name ?? "Select preset..."}
            </span>
            <ChevronDown
              className={cn(
                "size-3.5 text-primary/30 transition",
                open && "rotate-180",
              )}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          {presets.map((preset) => {
            const isFav = favouriteSet.has(preset._id);
            const isSelected = preset._id === selectedId;
            return (
              <div
                key={preset._id}
                className={cn(
                  "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition",
                  isSelected
                    ? "bg-primary/5 text-primary"
                    : "text-primary/70 hover:bg-secondary/50",
                )}
              >
                <button
                  type="button"
                  className="flex flex-1 items-center gap-2 text-left"
                  onClick={() => {
                    onSelect(preset._id);
                    setOpen(false);
                  }}
                >
                  <span className="flex-1">{preset.name}</span>
                  {preset.isDefault && (
                    <span className="text-[10px] text-primary/30">Default</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await toggleFavourite({ orgId, presetId: preset._id });
                    } catch {
                      /* best-effort toggle */
                    }
                  }}
                  className="rounded p-0.5 hover:bg-secondary"
                >
                  <Star
                    className={cn(
                      "size-3",
                      isFav
                        ? "fill-amber-400 text-amber-400"
                        : "text-primary/20",
                    )}
                  />
                </button>
              </div>
            );
          })}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field Review Input
// ---------------------------------------------------------------------------

/**
 * Converts ISO date (YYYY-MM-DD) to AU format (DD/MM/YYYY).
 * Returns the original string if it doesn't match ISO format.
 */
function formatDateAU(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

const PRACTITIONER_SELECT_FIELDS = new Set([
  "second_practitioner_name",
  "signatory_name",
]);

const COMPANY_NAME_SELECT_FIELDS = new Set(["company_name"]);

const COMPANY_NAME_VARIANT_TYPE_LABELS = {
  trading_name: "Trading name",
  trust_name: "Trust name",
  former_trading_name: "Former trading name",
} as const;

function joinNameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

function buildConcatenatedCompanyName(
  registeredName: string,
  variants: Array<{ name: string; type: string; includeInDocuments?: boolean }>,
): string | null {
  const tradingNames = variants
    .filter((variant) => variant.includeInDocuments !== false)
    .filter((variant) => variant.type === "trading_name")
    .map((variant) => variant.name.trim())
    .filter(Boolean);
  const otherNames = variants
    .filter((variant) => variant.includeInDocuments !== false)
    .filter((variant) => variant.type !== "trading_name")
    .map((variant) => variant.name.trim())
    .filter(Boolean);

  const parts = [registeredName.trim()].filter(Boolean);
  if (tradingNames.length > 0) {
    parts.push(`trading as ${joinNameList(tradingNames)}`);
  }
  if (otherNames.length > 0) {
    parts.push(`also known as ${joinNameList(otherNames)}`);
  }

  // Join with newlines, not spaces. In normal HTML flow newlines collapse to a
  // single space, so the default render is unchanged (one line). But this lets
  // the `stack` field modifier ({{company_name|stack}}) break the registered
  // name / "trading as" / "also known as" parts onto separate lines (MOR-625).
  return parts.length > 1 ? parts.join("\n") : null;
}

/**
 * Practitioner picker — matches the PresetSelector visual pattern.
 * Full-width trigger button with chevron, dropdown of known practitioners,
 * plus a custom input row for typing a name not in the list.
 */
function PractitionerComboInput({
  value,
  options,
  onChange,
  isEmpty,
}: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  isEmpty: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const customInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (name: string) => {
    onChange(name);
    setCustomMode(false);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setCustomMode(false);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-lg border px-3 text-sm transition hover:border-border",
            isEmpty
              ? "border-amber-200 dark:border-amber-900/30"
              : "border-border/50",
            "bg-background",
          )}
        >
          <span className={value ? "text-primary" : "text-primary/40"}>
            {value || "Select practitioner..."}
          </span>
          <ChevronDown
            className={cn(
              "size-3.5 text-primary/30 transition",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <button
          type="button"
          className={cn(
            "flex w-full cursor-pointer items-center px-3 py-2 text-sm transition",
            !value
              ? "bg-primary/5 text-primary"
              : "text-primary/40 hover:bg-secondary/50",
          )}
          onClick={() => handleSelect("")}
        >
          None
        </button>
        {options.map((name) => (
          <button
            key={name}
            type="button"
            className={cn(
              "flex w-full cursor-pointer items-center px-3 py-2 text-sm transition",
              value === name
                ? "bg-primary/5 text-primary"
                : "text-primary/70 hover:bg-secondary/50",
            )}
            onClick={() => handleSelect(name)}
          >
            {name}
          </button>
        ))}
        <div className="border-t border-border/50 px-3 py-2">
          {customMode ? (
            <Input
              ref={customInputRef}
              type="text"
              placeholder="Type a custom name..."
              autoFocus
              className="h-8 text-sm"
              defaultValue={!options.includes(value) ? value : ""}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSelect((e.target as HTMLInputElement).value);
                }
              }}
            />
          ) : (
            <button
              type="button"
              className="text-xs text-primary/40 transition hover:text-primary/60"
              onClick={() => setCustomMode(true)}
            >
              Enter custom name...
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CompanyNameComboInput({
  value,
  options,
  onChange,
  isEmpty,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  isEmpty: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const customInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (name: string) => {
    onChange(name);
    setCustomMode(false);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setCustomMode(false);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-lg border px-3 text-sm transition hover:border-border",
            isEmpty
              ? "border-amber-200 dark:border-amber-900/30"
              : "border-border/50",
            "bg-background",
          )}
        >
          <span className={value ? "truncate text-primary" : "text-primary/40"}>
            {value || "Select company name..."}
          </span>
          <ChevronDown
            className={cn(
              "ml-2 size-3.5 shrink-0 text-primary/30 transition",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {options.map((option) => (
          <button
            key={`${option.label}-${option.value}`}
            type="button"
            className={cn(
              "flex w-full cursor-pointer flex-col px-3 py-2 text-left text-sm transition",
              value === option.value
                ? "bg-primary/5 text-primary"
                : "text-primary/70 hover:bg-secondary/50",
            )}
            onClick={() => handleSelect(option.value)}
          >
            <span>{option.value}</span>
            <span className="text-[10px] text-primary/35">{option.label}</span>
          </button>
        ))}
        <div className="border-t border-border/50 px-3 py-2">
          {customMode ? (
            <Input
              ref={customInputRef}
              type="text"
              placeholder="Type a custom company name..."
              autoFocus
              className="h-8 text-sm"
              defaultValue={
                options.some((option) => option.value === value) ? "" : value
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSelect((e.target as HTMLInputElement).value);
                }
              }}
            />
          ) : (
            <button
              type="button"
              className="text-xs text-primary/40 transition hover:text-primary/60"
              onClick={() => setCustomMode(true)}
            >
              Enter custom company name...
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** A follow-up field and the answers that reveal it. */
type DependentHint = { label: string; values: string[] };

/**
 * One field in the review form plus the fields gated behind its answer
 * (MOR-885). Built per category section so a controlling question and its
 * follow-ups render as a single unit in the two-column grid.
 */
type ConditionalFieldNode = {
  key: string;
  /** The field's rendered input. */
  content: ReactNode;
  /** Set when this field only surfaced because a controlling field matched. */
  revealedBy?: DependentHint;
  /** Follow-up fields the current answer has revealed. */
  children: ConditionalFieldNode[];
  /** Follow-up fields the current answer still keeps hidden. */
  hiddenDependents: DependentHint[];
};

/** "Yes" / "Yes or Y" — reads naturally inside the hint lines. */
function formatConditionValues(values: string[]): string {
  if (values.length <= 1) return values[0] ?? "";
  return `${values.slice(0, -1).join(", ")} or ${values[values.length - 1]}`;
}

/** Shared rail styling that marks a field as gated behind another answer. */
const CONDITIONAL_RAIL_CLASS =
  "border-l-2 border-emerald-300 pl-3 dark:border-emerald-800";

/** "Only shown when Yes" line that sits above a gated field. */
function ConditionalHint({
  children,
  muted = false,
}: {
  children: ReactNode;
  muted?: boolean;
}) {
  return (
    <p
      className={cn(
        "flex items-start gap-1 text-[10px] leading-tight",
        muted
          ? "text-primary/40"
          : "font-medium text-emerald-700 dark:text-emerald-300",
      )}
    >
      <GitBranch className="mt-px size-3 shrink-0" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

/**
 * A field gated behind another field's answer. Sits behind a rail and states
 * the answer that revealed it, and hosts its own dependents when conditions
 * are chained.
 */
function ConditionalDependentField({ node }: { node: ConditionalFieldNode }) {
  return (
    <div className={cn("flex flex-col", CONDITIONAL_RAIL_CLASS)}>
      {node.revealedBy && (
        <ConditionalHint>
          Only shown when {formatConditionValues(node.revealedBy.values)}
        </ConditionalHint>
      )}
      <div className="mt-1.5 flex flex-1 flex-col">{node.content}</div>
      {node.children.map((child) => (
        <div key={child.key} className="mt-3">
          <ConditionalDependentField node={child} />
        </div>
      ))}
    </div>
  );
}

/**
 * Renders a field together with the follow-up questions its answer controls.
 * A controlling field claims a full-width row inside a tinted container and
 * its dependents render alongside it behind a rail — otherwise a conditional
 * field lands in whichever column comes next and reads as unrelated to the
 * question that produced it.
 *
 * The container is a nested two-column grid rather than a vertical stack so
 * fields keep their usual width and a tall group can't stretch an unrelated
 * neighbour's cell. Fields with no conditional relationship render bare, so
 * the grid's rhythm is unchanged for ordinary forms.
 */
function ConditionalFieldCell({ node }: { node: ConditionalFieldNode }) {
  const isParent = node.children.length > 0 || node.hiddenDependents.length > 0;

  if (!isParent) {
    // A dependent whose controlling field sits in another section keeps the
    // rail and names the field that revealed it.
    if (node.revealedBy) {
      return (
        <div className={cn("flex flex-col", CONDITIONAL_RAIL_CLASS)}>
          <ConditionalHint>
            Shown because “{node.revealedBy.label}” is{" "}
            {formatConditionValues(node.revealedBy.values)}
          </ConditionalHint>
          <div className="mt-1.5 flex flex-1 flex-col">{node.content}</div>
        </div>
      );
    }
    return <>{node.content}</>;
  }

  return (
    <div className="rounded-lg border border-emerald-200/70 bg-emerald-50/30 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/10 sm:col-span-2">
      {/* `items-start` so a tall dependent doesn't stretch the controlling
          field's cell — the review inputs bottom-align their control with
          `mt-auto`, which would otherwise drop it to the foot of the group. */}
      <div className="grid items-start gap-x-6 gap-y-4 sm:grid-cols-2">
        <div className="flex flex-col">
          {node.revealedBy && (
            <ConditionalHint>
              Shown because “{node.revealedBy.label}” is{" "}
              {formatConditionValues(node.revealedBy.values)}
            </ConditionalHint>
          )}
          <div className="flex flex-1 flex-col">{node.content}</div>
          {node.hiddenDependents.length > 0 && (
            <div className={cn("mt-2 space-y-1", CONDITIONAL_RAIL_CLASS)}>
              {node.hiddenDependents.map((hint) => (
                <ConditionalHint key={hint.label} muted>
                  “{hint.label}” appears when this is{" "}
                  {formatConditionValues(hint.values)}
                </ConditionalHint>
              ))}
            </div>
          )}
        </div>
        {node.children.map((child) => (
          <ConditionalDependentField key={child.key} node={child} />
        ))}
      </div>
    </div>
  );
}

function FieldReviewInput({
  field,
  value,
  source,
  onChange,
  practitionerOptions,
  presetPractitionerOptions,
  primaryPractitionerName,
  companyNameOptions,
  selectOptions,
  hasOverviewSummary,
  jobId,
}: {
  field: CanonicalField;
  value: string;
  source: FieldReviewSource;
  onChange: (value: string) => void;
  practitionerOptions?: string[];
  /**
   * Registered practitioners from the firm's Practitioner Details presets.
   * Only these can be a joint appointee, so `second_practitioner_name` uses
   * this list rather than the job team.
   */
  presetPractitionerOptions?: string[];
  primaryPractitionerName?: string;
  companyNameOptions?: Array<{ value: string; label: string }>;
  /**
   * Options for `select`-typed fields — used by checkbox groups
   * synthesized from PDF placements (e.g. "Finish after" → 1 month /
   * 3 months / 6 months / 12 months).
   */
  selectOptions?: string[];
  /**
   * Whether the job Overview "Background" summary exists. Used to show an
   * inline flag on the `background_summary` field when it will render blank.
   */
  hasOverviewSummary?: boolean;
  /** Job id — links the `background_summary` flag back to the Overview page. */
  jobId?: string;
}) {
  const sourceLabel = {
    job: "From Job",
    default: "Org Preset",
    edited: "Edited",
    empty: "Needs Input",
    template: "Unmapped",
    template_default: "Template Default",
  }[source];

  const sourceColor = {
    job: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20",
    default: "text-blue-600 bg-blue-50 dark:bg-blue-950/20",
    edited: "text-violet-600 bg-violet-50 dark:bg-violet-950/20",
    empty: "text-amber-600 bg-amber-50 dark:bg-amber-950/20",
    template: "text-cyan-600 bg-cyan-50 dark:bg-cyan-950/20",
    template_default: "text-sky-600 bg-sky-50 dark:bg-sky-950/20",
  }[source];

  const isDate = field.fieldType === "date";
  const displayValue = isDate ? formatDateAU(value) : value;

  const filteredPractitionerOptions = useMemo(() => {
    if (field.key === "second_practitioner_name") {
      const candidates = presetPractitionerOptions ?? [];
      return primaryPractitionerName
        ? candidates.filter((n) => n !== primaryPractitionerName)
        : candidates;
    }
    return practitionerOptions ?? [];
  }, [
    practitionerOptions,
    presetPractitionerOptions,
    field.key,
    primaryPractitionerName,
  ]);

  const showPractitionerSelect =
    PRACTITIONER_SELECT_FIELDS.has(field.key) &&
    filteredPractitionerOptions.length > 0;
  const showCompanyNameSelect =
    COMPANY_NAME_SELECT_FIELDS.has(field.key) &&
    (companyNameOptions?.length ?? 0) > 1;

  if (
    (field.fieldType as string) === "select" &&
    selectOptions &&
    selectOptions.length > 0
  ) {
    // Single-select field — used for checkbox groups synthesized from
    // PDF placements ("Finish after", "Application type"). Renders as a
    // segmented control so the user can pick one option; the selected
    // value is matched against each placement's `optionValue` at fill
    // time to decide which box gets ticked.
    const showBadge = source !== "empty";
    // Wordy options (ATO's "Administration ceased, Deed of Company
    // Arrangement entered — …") wrap onto two lines, so they read as a
    // stacked list of choices instead of ragged pills sitting side by side.
    const hasLongOption = selectOptions.some((opt) => opt.length > 24);
    return (
      <div className="flex h-full flex-col">
        <div className="mb-1.5 flex min-h-[2.5rem] flex-col justify-end">
          <div className="flex items-baseline justify-between gap-2">
            <label className="text-sm font-medium text-primary/70">
              {field.label}
            </label>
            {showBadge && (
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 text-[10px] font-medium",
                  sourceColor,
                )}
              >
                {sourceLabel}
              </span>
            )}
          </div>
          {field.description && (
            <p className="text-xs text-primary/40">{field.description}</p>
          )}
        </div>
        <div
          className={cn(
            "mt-auto flex gap-1.5",
            hasLongOption ? "flex-col" : "flex-wrap",
          )}
        >
          {selectOptions.map((opt) => {
            const active = value === opt;
            return (
              <button
                key={opt}
                type="button"
                className={cn(
                  "min-h-9 rounded-md border px-3 py-1.5 text-left text-sm leading-snug transition",
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-primary/15 bg-background text-primary/70 hover:border-primary/30",
                )}
                onClick={() => onChange(active ? "" : opt)}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.fieldType === "checkbox") {
    const checked = value === "true" || value === "yes" || value === "1";
    // A checkbox always has a definite state — never "Needs Input". Only
    // show a source badge when a preset or edit actually set it.
    const showBadge = source !== "empty";
    // Mirror the text-field layout (h-10 header + h-9 control) so checkbox
    // fields line up with their neighbours in the two-column grid.
    return (
      <div className="flex h-full flex-col">
        <div className="mb-1.5 flex min-h-[2.5rem] flex-col justify-end">
          <div className="flex items-baseline justify-between gap-2">
            <label
              htmlFor={`review-${field.key}`}
              className="text-sm font-medium text-primary/70"
            >
              {field.label}
            </label>
            {showBadge && (
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 text-[10px] font-medium",
                  sourceColor,
                )}
              >
                {sourceLabel}
              </span>
            )}
          </div>
          {field.description && (
            <p className="text-xs text-primary/40">{field.description}</p>
          )}
        </div>
        <label
          htmlFor={`review-${field.key}`}
          className="mt-auto flex h-9 cursor-pointer items-center gap-2 rounded-md border border-primary/15 px-3 text-sm"
        >
          <input
            type="checkbox"
            id={`review-${field.key}`}
            checked={checked}
            onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            className="size-4 rounded border-primary/20 text-primary accent-primary"
          />
          <span className="text-primary/70">
            {checked ? "Checked" : "Unchecked"}
          </span>
        </label>
      </div>
    );
  }

  // Background Summary — the Overview summary can run to a couple of
  // paragraphs, so render a multi-line textarea. When no overview has been
  // generated the field is empty and would render blank in the document, so
  // surface an inline flag linking back to the Overview page.
  if (field.key === "background_summary") {
    const showFlag = !hasOverviewSummary && !value.trim();
    return (
      <div className="flex h-full flex-col sm:col-span-2">
        <div className="mb-1.5 flex min-h-[2.5rem] flex-col justify-end">
          <div className="flex items-baseline justify-between gap-2">
            <label
              htmlFor={`review-${field.key}`}
              className="text-sm font-medium text-primary/70"
            >
              {field.label}
            </label>
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px] font-medium",
                sourceColor,
              )}
            >
              {sourceLabel}
            </span>
          </div>
          {field.description && (
            <p className="text-xs text-primary/40">{field.description}</p>
          )}
        </div>
        <div className="mt-auto space-y-1.5">
          <textarea
            id={`review-${field.key}`}
            rows={4}
            placeholder={field.placeholder ?? ""}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={cn(
              "w-full resize-y rounded-md border border-primary/15 bg-background px-3 py-2 text-sm",
              "focus:outline-none focus:ring-1 focus:ring-primary/30",
              source === "empty" && "border-amber-200 dark:border-amber-900/30",
            )}
          />
          {showFlag && (
            <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                No overview generated yet — this will render blank.{" "}
                {jobId ? (
                  <Link
                    to="/dashboard/jobs/$jobId/plan"
                    params={{ jobId }}
                    className="font-medium underline underline-offset-2"
                  >
                    Generate on the Overview page
                  </Link>
                ) : null}
              </span>
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-1.5 flex min-h-[2.5rem] flex-col justify-end">
        <div className="flex items-baseline justify-between gap-2">
          <label className="text-sm font-medium text-primary/70">
            {field.label}
          </label>
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-medium",
              sourceColor,
            )}
          >
            {sourceLabel}
          </span>
        </div>
        {field.description && (
          <p className="text-xs text-primary/40">{field.description}</p>
        )}
      </div>
      <div className="mt-auto">
        {showPractitionerSelect ? (
          <PractitionerComboInput
            value={value}
            options={filteredPractitionerOptions}
            onChange={onChange}
            isEmpty={source === "empty"}
          />
        ) : showCompanyNameSelect ? (
          <CompanyNameComboInput
            value={value}
            options={companyNameOptions ?? []}
            onChange={onChange}
            isEmpty={source === "empty"}
          />
        ) : (
          <Input
            type={field.fieldType === "email" ? "email" : "text"}
            inputMode={field.fieldType === "number" ? "decimal" : undefined}
            placeholder={isDate ? "DD/MM/YYYY" : (field.placeholder ?? "")}
            value={displayValue}
            onChange={(e) =>
              onChange(
                isDate
                  ? (toIsoDate(e.target.value) ?? e.target.value)
                  : e.target.value,
              )
            }
            className={cn(
              "h-9 text-sm",
              source === "empty" && "border-amber-200 dark:border-amber-900/30",
            )}
          />
        )}
      </div>
    </div>
  );
}
